import React, { useState, useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import OptimizationPanel from './components/OptimizationPanel';
import MapboxParcelDrawer from './components/MapboxParcelDrawer';
import ProjectManagementWrapper from './components/ProjectManagementWrapper';
import IntelligentUnitMixAdvisor from './components/IntelligentUnitMixAdvisor';
import ProjectReportGenerator from './components/ProjectReportGenerator';
import boxOptimizer from './utils/boxOptimizer';
import pcaBoxOptimizer from './utils/pcaBoxOptimizer';
import { calculateBuildingMetrics, calculateProjectMetrics } from './utils/buildingMetrics';
import MapboxPCAVisualizer, { generatePCALayout, getInsetPolygon } from './mapboxPCA/MapboxPCAVisualizer';
import settingsManager from './utils/settingsManager';
import UnitMixPage from './UnitMixPage';
import { getMarketAdjustedRates, getAvailableMarkets, MICHIGAN_MARKETS } from './data/michiganMarketData';

// Define action types
const ACTIONS = {
  SET_LAYOUT: 'SET_LAYOUT',
  SET_OPTIMIZATIONS: 'SET_OPTIMIZATIONS',
  SET_SELECTED_BUILDING: 'SET_SELECTED_BUILDING',
  RESET_OPTIMIZATIONS: 'RESET_OPTIMIZATIONS',
  UPDATE_SELECTED_OPTIMIZATIONS: 'UPDATE_SELECTED_OPTIMIZATIONS'
};

// Unit Mix Constants
const BUSINESS_MODELS = [
  'Market Rate Apartment',
  'Short Term Rental',
  'Workforce Housing',
  'Sell',
];

const UNIT_TYPES = [
  { key: 'studio', label: 'Studio', slots: 1, allowEndSlot: true },
  { key: 'studioPlus', label: 'Studio+', slots: 1.25, allowEndSlot: false, pairsWith: 'oneBRPlus' },
  { key: 'oneBR', label: '1 Bedroom', slots: 2, allowEndSlot: true },
  { key: 'oneBRPlus', label: '1 Bedroom+', slots: 1.75, allowEndSlot: true, pairsWith: 'studioPlus' },
  // { key: 'studioExt', label: 'Studio Ext.', slots: 1.5, allowEndSlot: true }, // Temporarily removed
  { key: 'twoBR', label: '2 Bedroom', slots: 3, allowEndSlot: true },
  // { key: 'threeBR', label: '3 Bedroom', slots: 4, allowEndSlot: true }, // Temporarily removed
];

// Unit placement algorithm
function calculateEgressSlots(totalBoxes) {
  const egressSlots = [];
  const maxTravelDistance = 3; // No unit can be more than 3 slots away from egress
  
  console.log(`🚨 Calculating egress for ${totalBoxes} boxes (max travel: ${maxTravelDistance}):`);
  
  if (totalBoxes <= 4) {
    // Very small buildings: one egress in the middle
    const middleSlot = Math.floor(totalBoxes / 2);
    if (middleSlot > 0 && middleSlot < totalBoxes - 1) {
      egressSlots.push(middleSlot);
      console.log(`  📍 Small building egress at box ${middleSlot + 1} (index ${middleSlot})`);
    }
  } else {
    // Larger buildings: place egresses to ensure 3-slot coverage
    // Each egress covers 7 slots total (3 left + egress + 3 right)
    // Place egresses every 6 slots to ensure overlap and complete coverage
    
    let position = 2; // Start at position 2 (3rd box)
    
    while (position < totalBoxes - 1) {
      egressSlots.push(position);
      console.log(`  📍 Egress at box ${position + 1} (index ${position})`);
      
      // Next egress position: move by 6 to ensure coverage overlap
      position += 6;
    }
    
    // Check if the last boxes need additional coverage
    const lastEgressPosition = egressSlots[egressSlots.length - 1];
    const distanceToEnd = totalBoxes - 1 - lastEgressPosition;
    
    if (distanceToEnd > maxTravelDistance) {
      // Add egress near the end to cover remaining boxes
      const additionalEgress = Math.max(lastEgressPosition + 3, totalBoxes - 4);
      if (additionalEgress < totalBoxes - 1 && additionalEgress > lastEgressPosition) {
        egressSlots.push(additionalEgress);
        console.log(`  📍 End coverage egress at box ${additionalEgress + 1} (index ${additionalEgress})`);
      }
    }
  }
  
  // Validate coverage: ensure no box is more than 3 slots from an egress
  for (let boxIndex = 0; boxIndex < totalBoxes; boxIndex++) {
    const minDistanceToEgress = Math.min(...egressSlots.map(egressPos => Math.abs(boxIndex - egressPos)));
    if (minDistanceToEgress > maxTravelDistance) {
      console.warn(`⚠️ Box ${boxIndex + 1} is ${minDistanceToEgress} slots from nearest egress (max allowed: ${maxTravelDistance})`);
    } else {
      console.log(`✅ Box ${boxIndex + 1} is ${minDistanceToEgress} slots from nearest egress`);
    }
  }
  
  console.log(`🚨 Total egress slots: ${egressSlots.length} at positions:`, egressSlots.map(i => i + 1));
  return egressSlots;
}

function placeUnitsInBuilding(buildingBoxes, unitQuantities, floorCount) {
  const totalBoxes = buildingBoxes.length;
  const egressSlots = calculateEgressSlots(totalBoxes);
  const floors = [];
  
  // Calculate total available slots (both sides, minus egress)
  const totalSlots = totalBoxes * 2 * floorCount;
  const egressSlotsTotal = egressSlots.length * floorCount; // Fixed: egress only takes left side (1 slot per egress)
  const availableSlots = totalSlots - egressSlotsTotal;
  
  console.log('🎯 Intelligent Unit Placement:', {
    totalBoxes,
    totalSlots,
    egressSlotsTotal,
    availableSlots,
    unitQuantities
  });
  
  // Create intelligent unit pool based on percentage preferences
  const unitPool = createIntelligentUnitPool(unitQuantities, availableSlots);
  
  console.log('📋 Intelligent unit pool:', unitPool);
  
  // Initialize floors
  for (let floor = 1; floor <= floorCount; floor++) {
    const floorLayout = {
      floorNumber: floor,
      towers: []
    };
    
    // Create towers (left and right sides of each box)
    buildingBoxes.forEach((box, boxIndex) => {
      const towerId = `T${boxIndex + 1}`;
      const isEgress = egressSlots.includes(boxIndex);
      
      floorLayout.towers.push({
        towerId,
        boxIndex,
        isEndSlot: boxIndex === 0 || boxIndex === totalBoxes - 1,
        left: {
          unitId: `B${buildingBoxes[0].buildPathId}-${towerId}-F${floor}-L`,
          unitType: isEgress ? 'EGRESS' : null,
          businessModel: null,
          assigned: isEgress,
          isEgress: isEgress
        },
        right: {
          unitId: `B${buildingBoxes[0].buildPathId}-${towerId}-F${floor}-R`,
          unitType: null, // Egress only on left side
          businessModel: null,
          assigned: false,
          isEgress: false
        }
      });
    });
    
    floors.push(floorLayout);
  }
  
  // Place units using intelligent algorithm
  placeUnitsIntelligently(floors, unitPool);
  
  return floors;
}

// Create intelligent unit pool based on actual unit counts (not percentages)
function createIntelligentUnitPool(unitQuantities, availableSlots) {
  const unitPool = [];
  
  console.log('🎯 Creating unit pool with actual counts:', { unitQuantities, availableSlots });
  
  // Handle paired units first (Studio+ & 1BR+ together)
  const studioPlusCount = unitQuantities.studioPlus || 0;
  const oneBRPlusCount = unitQuantities.oneBRPlus || 0;
  const pairCount = Math.max(studioPlusCount, oneBRPlusCount); // They should be equal
  
  // Add paired units to pool
  for (let i = 0; i < pairCount; i++) {
    unitPool.push({
      key: 'pairedUnit',
      label: 'Studio+ & 1BR+',
      slots: 3,
      allowEndSlot: true, // Allow tetris units in end positions for revenue
      isPaired: true,
      priority: 1, // High priority for revenue
      revenuePerSlot: 350 // Estimated revenue per slot for prioritization
    });
  }
  
  // Add regular units to pool
  const regularUnits = [
    { key: 'twoBR', count: unitQuantities.twoBR || 0, slots: 3, priority: 0, revenuePerSlot: 400 },
    { key: 'oneBR', count: unitQuantities.oneBR || 0, slots: 2, priority: 2, revenuePerSlot: 300 },
    { key: 'studio', count: unitQuantities.studio || 0, slots: 1, priority: 4, revenuePerSlot: 200 }
  ];
  
  regularUnits.forEach(unitData => {
    const unitType = UNIT_TYPES.find(ut => ut.key === unitData.key);
    if (unitType && unitData.count > 0) {
      for (let i = 0; i < unitData.count; i++) {
        unitPool.push({
          key: unitData.key,
          label: unitType.label,
          slots: unitData.slots,
          allowEndSlot: unitType.allowEndSlot,
          isPaired: false,
          priority: unitData.priority,
          revenuePerSlot: unitData.revenuePerSlot
        });
      }
    }
  });
  
  console.log(`🎯 Final unit pool: ${unitPool.length} units`);
  unitPool.forEach((unit, index) => {
    console.log(`  ${index + 1}. ${unit.label} (${unit.slots} slots, revenue/slot: $${unit.revenuePerSlot})`);
  });
  
  return unitPool;
}

// Revenue-optimized unit placement algorithm with proportional floor distribution
function placeUnitsIntelligently(floors, unitPool) {
  let unitsPlaced = 0;
  let unitsSkipped = 0;
  
  console.log(`🏗️ Starting proportional unit placement with ${unitPool.length} units across ${floors.length} floors`);
  
  // Calculate how many units of each type should go on each floor (proportional distribution)
  const floorCount = floors.length;
  const unitsByType = {};
  unitPool.forEach(unit => {
    const key = unit.key;
    if (!unitsByType[key]) {
      unitsByType[key] = { units: [], count: 0 };
    }
    unitsByType[key].units.push(unit);
    unitsByType[key].count++;
  });
  
  // Distribute units across floors proportionally
  const floorUnitPools = floors.map(() => []);
  Object.keys(unitsByType).forEach(unitKey => {
    const { units, count } = unitsByType[unitKey];
    let unitsAssigned = 0;
    
    for (let f = 0; f < floorCount; f++) {
      // Calculate how many of this unit type go on this floor
      const remaining = count - unitsAssigned;
      const floorsRemaining = floorCount - f;
      const unitsForFloor = Math.ceil(remaining / floorsRemaining);
      
      for (let i = 0; i < unitsForFloor && unitsAssigned < count; i++) {
        floorUnitPools[f].push(units[unitsAssigned]);
        unitsAssigned++;
      }
    }
  });
  
  console.log('📊 Units distributed per floor:', floorUnitPools.map((pool, i) => `Floor ${i+1}: ${pool.length} units`));
  
  floors.forEach((floor, floorIndex) => {
    console.log(`📋 Placing units on floor ${floor.floorNumber}`);
    
    // Get all available slots (excluding egress)
    const availableSlots = [];
    floor.towers.forEach((tower, index) => {
      // Add left side if not egress
      if (!tower.left.isEgress) {
        availableSlots.push({ 
          side: 'left', 
          towerIndex: index, 
          tower,
          isEndSlot: tower.isEndSlot,
          position: index
        });
      }
      // Add right side (never egress)
      if (!tower.right.isEgress) {
        availableSlots.push({ 
          side: 'right', 
          towerIndex: index, 
          tower,
          isEndSlot: tower.isEndSlot,
          position: index
        });
      }
    });
    
    console.log(`📊 Available slots on floor ${floor.floorNumber}: ${availableSlots.length}`);
    
    // Sort THIS FLOOR's units by revenue potential (highest first)
    const sortedUnits = [...floorUnitPools[floorIndex]].sort((a, b) => {
      // Prioritize by revenue per slot, then by preference for end units
      const aRevenue = a.revenuePerSlot;
      const bRevenue = b.revenuePerSlot;
      if (aRevenue !== bRevenue) return bRevenue - aRevenue;
      return a.priority - b.priority;
    });
    
    console.log(`💰 Floor ${floor.floorNumber} units:`, sortedUnits.map(u => u.label).slice(0, 10).join(', ') + (sortedUnits.length > 10 ? '...' : ''));
    
    // Place units from this floor's pool
    for (const unit of sortedUnits) {
      // Try to place in best available position
      const placement = findBestPlacement(floor, availableSlots, unit, true);
      
      if (placement) {
        placeUnitInSlotGroup(floor, unit, placement.slotGroup);
        unitsPlaced++;
        
        // Remove used slots from available list
        placement.slotGroup.forEach(slotInfo => {
          const slotIndex = availableSlots.findIndex(s => 
            s.towerIndex === slotInfo.towerIndex && s.side === slotInfo.side
          );
          if (slotIndex !== -1) availableSlots.splice(slotIndex, 1);
        });
      } else {
        console.log(`❌ Could not place ${unit.label} on floor ${floor.floorNumber}`);
        unitsSkipped++;
      }
    }
    
    // DON'T optimize remaining slots - respect user's unit mix preferences
    // optimizeRemainingSlots(floor, availableSlots);
  });
  
  console.log(`✅ Proportional placement: ${unitsPlaced} placed, ${unitsSkipped} skipped`);
  
  // Fill any remaining slots with studios (to ensure all slots are utilized)
  fillRemainingWithStudios(floors);
}

// Find the best placement for a unit, preferring end slots for revenue
function findBestPlacement(floor, availableSlots, unit, preferEndSlots = true) {
  const slotsNeeded = Math.ceil(unit.slots);
  
  // Get available slot groups
  const slotGroups = findConsecutiveSlotGroups(availableSlots, slotsNeeded);
  
  if (slotGroups.length === 0) return null;
  
  // Sort by preference: end slots first (for revenue), then regular slots
  slotGroups.sort((a, b) => {
    const aHasEnd = a.some(slot => slot.isEndSlot);
    const bHasEnd = b.some(slot => slot.isEndSlot);
    
    if (preferEndSlots && unit.revenuePerSlot > 250) { // High-revenue units prefer end slots
      if (aHasEnd && !bHasEnd) return -1;
      if (!aHasEnd && bHasEnd) return 1;
    }
    
    // For studios, prefer non-end slots to save end slots for larger units
    if (unit.key === 'studio') {
      if (aHasEnd && !bHasEnd) return 1;
      if (!aHasEnd && bHasEnd) return -1;
    }
    
    return 0;
  });
  
  const bestGroup = slotGroups[0];
  const isEndPlacement = bestGroup.some(slot => slot.isEndSlot);
  
  return {
    slotGroup: bestGroup,
    isEndPlacement
  };
}

// Find consecutive slot groups that can accommodate a unit
function findConsecutiveSlotGroups(availableSlots, slotsNeeded) {
  const groups = [];
  
  // Group slots by side and sort by position
  const leftSlots = availableSlots
    .filter(slot => slot.side === 'left' && !slot.tower.left.assigned)
    .sort((a, b) => a.position - b.position);
  const rightSlots = availableSlots
    .filter(slot => slot.side === 'right' && !slot.tower.right.assigned)
    .sort((a, b) => a.position - b.position);
  
  [leftSlots, rightSlots].forEach(sideSlots => {
    for (let i = 0; i <= sideSlots.length - slotsNeeded; i++) {
      const group = [];
      let isConsecutive = true;
      
      for (let j = 0; j < slotsNeeded; j++) {
        const currentSlot = sideSlots[i + j];
        if (!currentSlot) {
          isConsecutive = false;
          break;
        }
        
        // Check if slots are consecutive
        if (j > 0) {
          const prevSlot = sideSlots[i + j - 1];
          if (currentSlot.position !== prevSlot.position + 1) {
            isConsecutive = false;
            break;
          }
        }
        
        group.push(currentSlot);
      }
      
      if (isConsecutive && group.length === slotsNeeded) {
        groups.push(group);
      }
    }
  });
  
  return groups;
}

// Place a unit in a specific slot group (handles both regular and paired units)
function placeUnitInSlotGroup(floor, unit, slotGroup) {
  if (unit.isPaired) {
    // Handle paired units (Studio+ + Shared Core + 1BR+) - all consecutive
    const slot1Info = slotGroup[0];
    const slot2Info = slotGroup[1];
    const slot3Info = slotGroup[2];
    
    const slot1 = slot1Info.side === 'right' ? slot1Info.tower.right : slot1Info.tower.left;
    const slot2 = slot2Info.side === 'right' ? slot2Info.tower.right : slot2Info.tower.left;
    const slot3 = slot3Info.side === 'right' ? slot3Info.tower.right : slot3Info.tower.left;
    
    slot1.unitType = 'Studio+';
    slot1.businessModel = 'Market Rate Apartment';
    slot1.assigned = true;
    
    slot2.unitType = 'Shared: 25% Studio+ | 75% 1BR+';
    slot2.businessModel = 'Shared';
    slot2.assigned = true;
    
    slot3.unitType = '1 Bedroom+';
    slot3.businessModel = 'Market Rate Apartment';
    slot3.assigned = true;
  } else {
    // Handle regular units - all slots consecutive
    slotGroup.forEach((slotInfo, index) => {
      const slot = slotInfo.side === 'right' ? slotInfo.tower.right : slotInfo.tower.left;
      
      slot.unitType = unit.label;
      slot.businessModel = 'Market Rate Apartment';
      slot.assigned = true;
    });
  }
}

// Optimize remaining slots by combining adjacent studios into 1BR units
function optimizeRemainingSlots(floor, availableSlots) {
  console.log('🔧 Optimizing remaining slots for revenue...');
  
  // Find pairs of adjacent empty slots that could become 1BR units
  const leftSlots = availableSlots
    .filter(slot => slot.side === 'left' && !slot.tower.left.assigned)
    .sort((a, b) => a.position - b.position);
  const rightSlots = availableSlots
    .filter(slot => slot.side === 'right' && !slot.tower.right.assigned)
    .sort((a, b) => a.position - b.position);
  
  [leftSlots, rightSlots].forEach(sideSlots => {
    for (let i = 0; i < sideSlots.length - 1; i++) {
      const slot1 = sideSlots[i];
      const slot2 = sideSlots[i + 1];
      
      // Check if slots are consecutive
      if (slot2.position === slot1.position + 1) {
        // Convert two adjacent slots to 1BR
        const slot1Tower = slot1.tower[slot1.side];
        const slot2Tower = slot2.tower[slot2.side];
        
        if (!slot1Tower.assigned && !slot2Tower.assigned) {
          slot1Tower.unitType = '1 Bedroom';
          slot1Tower.businessModel = 'Market Rate Apartment';
          slot1Tower.assigned = true;
          
          slot2Tower.unitType = '1 Bedroom';
          slot2Tower.businessModel = 'Market Rate Apartment';
          slot2Tower.assigned = true;
          
          console.log(`✅ Combined T${slot1.towerIndex + 1}-${slot1.side} + T${slot2.towerIndex + 1}-${slot2.side} into 1BR`);
          
          // Remove from available slots
          const index1 = availableSlots.findIndex(s => s === slot1);
          const index2 = availableSlots.findIndex(s => s === slot2);
          if (index1 !== -1) availableSlots.splice(index1, 1);
          if (index2 !== -1) availableSlots.splice(index2 > index1 ? index2 - 1 : index2, 1);
          
          i++; // Skip next slot since we used it
        }
      }
    }
  });
}

// Fill any remaining slots with studios (last resort)
function fillRemainingWithStudios(floors) {
  let studiosFilled = 0;
  
  floors.forEach(floor => {
    floor.towers.forEach(tower => {
      // Fill empty right slots with studios
      if (!tower.right.assigned && !tower.right.isEgress) {
        tower.right.unitType = 'Studio';
        tower.right.businessModel = 'Market Rate Apartment';
        tower.right.assigned = true;
        studiosFilled++;
      }
      
      // Fill empty left slots with studios (avoiding egress)
      if (!tower.left.assigned && !tower.left.isEgress) {
        tower.left.unitType = 'Studio';
        tower.left.businessModel = 'Market Rate Apartment';
        tower.left.assigned = true;
        studiosFilled++;
      }
    });
  });
  
  if (studiosFilled > 0) {
    console.log(`🏠 Filled ${studiosFilled} remaining slots with Studios`);
  }
}

// Visual Unit Map Component
const VisualUnitMap = ({ buildingId, buildingBoxes, unitQuantities, floorCount, onExport }) => {
  console.log('🗺️ VisualUnitMap received unitQuantities:', unitQuantities);
  
  const floorLayouts = useMemo(() => {
    console.log('🗺️ VisualUnitMap useMemo triggered with unitQuantities:', unitQuantities);
    if (!buildingBoxes || buildingBoxes.length === 0) return [];
    return placeUnitsInBuilding(buildingBoxes, unitQuantities, floorCount);
  }, [buildingBoxes, unitQuantities, floorCount]);

  const getUnitTypeColor = (unitType) => {
    const colors = {
      'EGRESS': '#6b7280',
      'Studio': '#3b82f6',
      'Studio+': '#1d4ed8',
      '1 Bedroom': '#059669',
      '1 Bedroom+': '#047857',
      'Studio Ext.': '#7c3aed',
      '2 Bedroom': '#dc2626',
      '3 Bedroom': '#ea580c',
      'Shared Core': '#f59e0b', // Orange for shared core (legacy)
      'Shared: 25% Studio+ | 75% 1BR+': '#f59e0b' // Orange for new shared label
    };
    return colors[unitType] || '#9ca3af';
  };

  const exportToJSON = () => {
    const exportData = {
      buildingId,
      totalBoxes: buildingBoxes.length,
      floorCount,
      egressSlots: calculateEgressSlots(buildingBoxes.length),
      unitQuantities,
      floors: floorLayouts,
      exportTimestamp: new Date().toISOString(),
      summary: {
        totalUnits: buildingBoxes.length * 2 * floorCount,
        egressUnits: calculateEgressSlots(buildingBoxes.length).length * 2 * floorCount,
        livableUnits: (buildingBoxes.length * 2 * floorCount) - (calculateEgressSlots(buildingBoxes.length).length * 2 * floorCount)
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `building-${buildingId}-unit-map.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    // Simple CSV export for VisualUnitMap - this function doesn't have access to livableSpaces
    const csvData = floorLayouts.map(floor => 
      floor.towers.map(tower => [
        `${tower.towerId}-L`,
        buildingId,
        tower.towerId,
        floor.floorNumber,
        'L',
        tower.left.unitType || 'Available'
      ].concat([
        `${tower.towerId}-R`,
        buildingId,
        tower.towerId,
        floor.floorNumber,
        'R',
        tower.right.unitType || 'Available'
      ]))
    ).flat().flat();
    
    const header = "ID,Building,Tower,Floor,Side,Unit Type\n";
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const csv = header + csvContent;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `building-${buildingId}-unit-map.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    // Create a printable version
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Building ${buildingId} - Unit Map</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .floor { margin-bottom: 30px; page-break-inside: avoid; }
          .floor-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; }
          .tower-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(80px, 1fr)); gap: 8px; margin-bottom: 20px; }
          .tower { text-align: center; }
          .tower-header { font-size: 12px; font-weight: bold; margin-bottom: 5px; }
          .unit { padding: 8px 4px; border-radius: 4px; color: white; font-size: 10px; margin-bottom: 2px; min-height: 40px; display: flex; flex-direction: column; justify-content: center; }
          .egress { background-color: #6b7280; }
          .studio { background-color: #3b82f6; }
          .one-bedroom { background-color: #059669; }
          .two-bedroom { background-color: #dc2626; }
          .three-bedroom { background-color: #ea580c; }
          .legend { display: flex; gap: 20px; margin: 20px 0; justify-content: center; }
          .legend-item { display: flex; align-items: center; gap: 5px; }
          .legend-color { width: 16px; height: 16px; border-radius: 2px; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Building ${buildingId} - Visual Unit Map</h1>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="legend">
          <div class="legend-item"><div class="legend-color egress"></div><span>Egress</span></div>
          <div class="legend-item"><div class="legend-color studio"></div><span>Studio</span></div>
          <div class="legend-item"><div class="legend-color one-bedroom"></div><span>1 Bedroom</span></div>
          <div class="legend-item"><div class="legend-color two-bedroom"></div><span>2 Bedroom</span></div>
          <div class="legend-item"><div class="legend-color three-bedroom"></div><span>3 Bedroom</span></div>
        </div>
        
        ${floorLayouts.map(floor => `
          <div class="floor">
            <div class="floor-title">Floor ${floor.floorNumber}</div>
            <div class="tower-grid">
              ${floor.towers.slice(0, 10).map(tower => `
                <div class="tower">
                  <div class="tower-header">${tower.towerId}</div>
                  <div class="unit ${tower.left.unitType?.toLowerCase().replace(/[^a-z]/g, '-') || 'unassigned'}">
                    <div>${tower.left.unitType || 'Unassigned'}</div>
                    <div style="font-size: 8px;">${tower.left.unitId}</div>
                  </div>
                  <div class="unit ${tower.right.unitType?.toLowerCase().replace(/[^a-z]/g, '-') || 'unassigned'}">
                    <div>${tower.right.unitType || 'Unassigned'}</div>
                    <div style="font-size: 8px;">${tower.right.unitId}</div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
        
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 1000);
          }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (!floorLayouts || floorLayouts.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <p style={{ color: '#6b7280', margin: 0 }}>
          No unit layout available. Please configure unit quantities above.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Floor Layouts - Priority Content */}
      {floorLayouts.map(floor => (
        <div key={floor.floorNumber} style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          {/* Condensed Single-Line Header */}
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '8px 16px',
            borderBottom: '1px solid #e2e8f0'
          }}>
            <h5 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              margin: 0
            }}>
              Floor {floor.floorNumber}  Visual Unit Map - Building {buildingId}
            </h5>
          </div>

          {/* Tower Grid */}
          <div style={{ padding: '16px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${floor.towers.length}, 1fr)`,
              gap: '6px',
              marginBottom: '12px'
            }}>
              {/* Tower Headers */}
              {floor.towers.map(tower => (
                <div key={tower.towerId} style={{
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: '#6b7280',
                  padding: '4px'
                }}>
                  {tower.towerId}
                  {tower.isEndSlot && <div style={{ fontSize: '0.6rem', color: '#ef4444' }}>END</div>}
                </div>
              ))}
            </div>

            {/* Left Side Units */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${floor.towers.length}, 1fr)`,
              gap: '6px',
              marginBottom: '8px'
            }}>
              {floor.towers.map(tower => (
                <div key={`${tower.towerId}-L`} style={{
                  backgroundColor: getUnitTypeColor(tower.left.unitType),
                  color: 'white',
                  padding: '6px 3px',
                  borderRadius: '4px',
                  textAlign: 'center',
                  fontSize: '0.6rem',
                  fontWeight: '500',
                  minHeight: '50px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '1px',
                  border: tower.isEndSlot ? '2px solid #ef4444' : 'none'
                }}>
                  <div style={{ fontWeight: '600', fontSize: '0.65rem' }}>
                    {tower.left.unitType || 'Available'}
                  </div>
                  <div style={{ fontSize: '0.55rem', opacity: 0.9 }}>
                    {tower.left.unitId}
                  </div>
                </div>
              ))}
            </div>

            {/* Side Labels */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${floor.towers.length}, 1fr)`,
              gap: '6px',
              marginBottom: '8px'
            }}>
              {floor.towers.map((_, index) => (
                <div key={`left-${index}`} style={{
                  textAlign: 'center',
                  fontSize: '0.65rem',
                  color: '#6b7280',
                  fontWeight: '500'
                }}>
                  Left (Egress Side)
                </div>
              ))}
            </div>

            {/* Center Divider */}
            <div style={{
              height: '2px',
              backgroundColor: '#e2e8f0',
              margin: '8px 0'
            }} />

            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${floor.towers.length}, 1fr)`,
              gap: '6px',
              marginBottom: '8px'
            }}>
              {floor.towers.map((_, index) => (
                <div key={`right-${index}`} style={{
                  textAlign: 'center',
                  fontSize: '0.65rem',
                  color: '#6b7280',
                  fontWeight: '500'
                }}>
                  Right (Unit Side)
                </div>
              ))}
            </div>

            {/* Right Side Units */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${floor.towers.length}, 1fr)`,
              gap: '6px'
            }}>
              {floor.towers.map((tower, index) => {
                // Determine if this slot is part of a multi-slot unit
                let slotPosition = '';
                let isMultiSlot = false;
                
                if (tower.right.unitType && tower.right.unitType !== 'EGRESS' && tower.right.unitType !== 'Available') {
                  // Check if this is part of a multi-slot unit by looking at adjacent slots
                  const prevTower = index > 0 ? floor.towers[index - 1] : null;
                  const nextTower = index < floor.towers.length - 1 ? floor.towers[index + 1] : null;
                  
                  const hasSameUnitBefore = prevTower && prevTower.right.unitType === tower.right.unitType;
                  const hasSameUnitAfter = nextTower && nextTower.right.unitType === tower.right.unitType;
                  
                  if (hasSameUnitBefore || hasSameUnitAfter) {
                    isMultiSlot = true;
                    
                    // Determine position within the unit
                    if (!hasSameUnitBefore && hasSameUnitAfter) {
                      slotPosition = '1'; // First slot
                    } else if (hasSameUnitBefore && !hasSameUnitAfter) {
                      slotPosition = 'End'; // Last slot
                    } else if (hasSameUnitBefore && hasSameUnitAfter) {
                      // This is a middle slot - but we need to be smarter about this
                      // Count how many consecutive slots of the same type we have
                      let consecutiveCount = 1;
                      let checkIndex = index - 1;
                      
                      // Count backwards
                      while (checkIndex >= 0 && floor.towers[checkIndex].right.unitType === tower.right.unitType) {
                        consecutiveCount++;
                        checkIndex--;
                      }
                      
                      // Count forwards
                      checkIndex = index + 1;
                      while (checkIndex < floor.towers.length && floor.towers[checkIndex].right.unitType === tower.right.unitType) {
                        consecutiveCount++;
                        checkIndex++;
                      }
                      
                      // Determine position based on unit type and consecutive count
                      if (tower.right.unitType === '1 Bedroom' && consecutiveCount === 2) {
                        // For 1BR (2 slots), this should be the second slot = "End"
                        slotPosition = 'End';
                      } else if (tower.right.unitType === '2 Bedroom' && consecutiveCount === 3) {
                        // For 2BR (3 slots), this is the middle slot = "Mid"
                        slotPosition = 'Mid';
                      } else {
                        // For other cases, determine by position in sequence
                        let positionInUnit = 1;
                        checkIndex = index - 1;
                        while (checkIndex >= 0 && floor.towers[checkIndex].right.unitType === tower.right.unitType) {
                          positionInUnit++;
                          checkIndex--;
                        }
                        
                        if (positionInUnit === 1) {
                          slotPosition = '1';
                        } else if (positionInUnit === consecutiveCount) {
                          slotPosition = 'End';
                        } else {
                          slotPosition = 'Mid';
                        }
                      }
                    }
                  }
                }
                
                return (
                  <div key={`${tower.towerId}-R`} style={{
                    backgroundColor: getUnitTypeColor(tower.right.unitType),
                    color: 'white',
                    padding: '6px 3px',
                    borderRadius: '4px',
                    textAlign: 'center',
                    fontSize: '0.6rem',
                    fontWeight: '500',
                    minHeight: '50px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: '1px',
                    border: tower.isEndSlot ? '2px solid #ef4444' : 
                           isMultiSlot ? '2px solid rgba(255,255,255,0.5)' : 'none',
                    position: 'relative'
                  }}>
                    {/* Multi-slot indicator */}
                    {isMultiSlot && (
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        color: '#000',
                        fontSize: '0.5rem',
                        padding: '1px 3px',
                        borderRadius: '2px',
                        fontWeight: '600'
                      }}>
                        {slotPosition}
                      </div>
                    )}
                    
                    <div style={{ fontWeight: '600', fontSize: '0.65rem' }}>
                      {tower.right.unitType || 'Available'}
                    </div>
                    <div style={{ fontSize: '0.55rem', opacity: 0.9 }}>
                      {tower.right.unitId}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Bottom Section: Building Summary and Legend + Export Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '1rem'
      }}>
        {/* Left Side: Building Summary */}
        <div style={{
          backgroundColor: '#f0fdf4',
          borderRadius: '8px',
          border: '1px solid #bbf7d0',
          padding: '16px',
          flex: '1'
        }}>
          <h5 style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#374151',
            margin: '0 0 12px 0'
          }}>
            Building Summary
          </h5>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '12px',
            fontSize: '0.75rem'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#059669' }}>
                {buildingBoxes.length}
              </div>
              <div style={{ color: '#6b7280' }}>Total Boxes</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#059669' }}>
                {buildingBoxes.length * 2 * floorCount}
              </div>
              <div style={{ color: '#6b7280' }}>Total Slots</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#dc2626' }}>
                {calculateEgressSlots(buildingBoxes.length).length * floorCount}
              </div>
              <div style={{ color: '#6b7280' }}>Egress Slots</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#3b82f6' }}>
                {(buildingBoxes.length * floorCount) - (calculateEgressSlots(buildingBoxes.length).length * floorCount)}
              </div>
              <div style={{ color: '#6b7280' }}>Available Slots</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#059669' }}>
                {floorCount}
              </div>
              <div style={{ color: '#6b7280' }}>Floors</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#7c3aed' }}>
                {(() => {
                  // Calculate total available units based on actual boxes
                  const totalAvailableUnits = buildingBoxes.length * 2 * floorCount;
                  return totalAvailableUnits;
                })()}
              </div>
              <div style={{ color: '#6b7280' }}>Units to Place</div>
            </div>
          </div>
        </div>

        {/* Right Side: Legend + Export Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          minWidth: '300px'
        }}>
          {/* Legend */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            padding: '12px',
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e2e8f0'
          }}>
            {['EGRESS', 'Studio', 'Studio+ & 1BR+ (paired)', '2 Bedroom', '25% Studio+ | 75% 1BR+ (shared)'].map(unitType => {
              // Handle the paired unit display and shared slot display
              let colorKey = unitType;
              if (unitType === 'Studio+ & 1BR+ (paired)') {
                colorKey = 'Studio+';
              } else if (unitType === '25% Studio+ | 75% 1BR+ (shared)') {
                colorKey = 'Shared: 25% Studio+ | 75% 1BR+';
              }
              
              return (
                <div key={unitType} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: getUnitTypeColor(colorKey),
                    borderRadius: '2px'
                  }} />
                  <span style={{ fontSize: '0.75rem', color: '#374151' }}>{unitType}</span>
                </div>
              );
            })}
          </div>

          {/* Export Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={exportToJSON}
              style={{
                padding: '6px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              📄 JSON
            </button>
            <button
              onClick={exportToCSV}
              style={{
                padding: '6px 12px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              📊 CSV
            </button>
            <button
              onClick={exportToPDF}
              style={{
                padding: '6px 12px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              📋 PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Unit Mix Helper Functions
function normalizeSliders(values) {
  const total = Object.values(values).reduce((a, b) => a + b, 0);
  if (total === 0) return values;
  
  // First pass: calculate proportional values using floor to avoid over-allocation
  const normalized = {};
  let runningTotal = 0;
  const keys = Object.keys(values);
  
  // Calculate all but the last value using floor
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const value = Math.floor((values[key] / total) * 100);
    normalized[key] = value;
    runningTotal += value;
  }
  
  // Set the last value to ensure total is exactly 100
  const lastKey = keys[keys.length - 1];
  normalized[lastKey] = Math.max(0, 100 - runningTotal);
  
  return normalized;
}

function getDefaultBuildingState() {
  // Simplified approach: separate unit type and business model distributions
  const equalUnitDistribution = Math.round(100 / UNIT_TYPES.length);
  const equalBusinessDistribution = Math.round(100 / BUSINESS_MODELS.length);
  
  // Unit type distribution (what to build)
  const unitTypeDistribution = UNIT_TYPES.reduce((acc, type, index) => {
    const value = index === UNIT_TYPES.length - 1 
      ? 100 - (equalUnitDistribution * (UNIT_TYPES.length - 1))
      : equalUnitDistribution;
    return { ...acc, [type.key]: value };
  }, {});
  
  // Business model distribution (how to monetize)
  const businessModelDistribution = BUSINESS_MODELS.reduce((acc, model, index) => {
    const value = index === BUSINESS_MODELS.length - 1 
      ? 100 - (equalBusinessDistribution * (BUSINESS_MODELS.length - 1))
      : equalBusinessDistribution;
    return { ...acc, [model]: value };
  }, {});

  return {
    unitTypeDistribution,
    businessModelDistribution,
    floors: 1,        // Default per-building floor count
    halo: 2.0        // Default halo multiplier (2x building footprint)
  };
}

// Layout reducer
function layoutReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LAYOUT:
      return {
        ...state,
        buildPaths: action.payload.buildPaths,
        boxes: action.payload.boxes,
        selectedOptimizations: state.selectedOptimizations || {},
        livableSpaces: []
      };
    case ACTIONS.SET_OPTIMIZATIONS:
      return {
        ...state,
        buildingOptimizations: action.payload
      };
    case ACTIONS.SET_SELECTED_BUILDING:
      return {
        ...state,
        selectedBuildingId: action.payload
      };
    case ACTIONS.RESET_OPTIMIZATIONS:
      return {
        ...state,
        selectedOptimizations: {}
      };
    case ACTIONS.UPDATE_SELECTED_OPTIMIZATIONS:
      console.log('Reducer: UPDATE_SELECTED_OPTIMIZATIONS', action.payload);
      const newSelectedOptimizations = {
        ...state.selectedOptimizations,
        ...action.payload
      };
      // Compute new optimizedBoxes
      const newOptimizedBoxes = updateDisplayedBoxes(
        state.boxes,
        state.buildPaths,
        state.buildingOptimizations,
        newSelectedOptimizations
      );
      return {
        ...state,
        selectedOptimizations: newSelectedOptimizations,
        optimizedBoxes: newOptimizedBoxes
      };
    default:
      return state;
  }
}

// Move this function above the reducer, outside the component:
function updateDisplayedBoxes(currentBoxes, currentBuildPaths, currentBuildingOptimizations, currentOptimizations) {
  const optimizedBoxes = [];
  
  // Ensure arrays are safe
  const safeCurrentBoxes = Array.isArray(currentBoxes) ? currentBoxes : [];
  const safeCurrentBuildPaths = Array.isArray(currentBuildPaths) ? currentBuildPaths : [];

  safeCurrentBuildPaths.forEach(buildPath => {
    const buildingId = buildPath.id;
    const optimization = currentOptimizations[buildingId] || 'none';

    if (optimization === 'none') {
      // For 'none', use the original boxes
      const originalBuildingBoxes = safeCurrentBoxes.filter(box => box.buildPathId === buildingId);
      optimizedBoxes.push(...originalBuildingBoxes);
    } else {
      // For efficiency or livability, use the optimized boxes
      const buildingOptimization = currentBuildingOptimizations[buildingId] || {};
      const optimizationResult = optimization === 'efficiency' ? 
        buildingOptimization.efficiency : 
        buildingOptimization.livability;

      if (optimizationResult?.optimizedBoxes) {
        // Use the optimized boxes for this building
        const optimizedBuildingBoxes = optimizationResult.optimizedBoxes;
        optimizedBoxes.push(...optimizedBuildingBoxes);
      } else {
        // Fallback to original boxes if optimization not available
        const originalBuildingBoxes = safeCurrentBoxes.filter(box => box.buildPathId === buildingId);
        optimizedBoxes.push(...originalBuildingBoxes);
      }
    }
  });

  return optimizedBoxes;
}

// Helper function to calculate standard metrics
function calculateStandardMetrics(boxes, buildPath) {
  if (!boxes || !buildPath) {
    return { efficiency: 80, livability: 80 }; // Default building-level metrics
  }
  
  // Building-level efficiency: how well boxes fill the building footprint
  // Standard units are designed to achieve 80% building efficiency
  const buildingLevelEfficiency = 80; // This is our baseline for building efficiency
  
  // Note: Site-level efficiency (how much total land is used) is calculated separately
  // and shown at the project level, not per-building level
  
  return {
    efficiency: buildingLevelEfficiency, // 80% building-level efficiency (our standard)
    livability: 80 // Default livability score
  };
}

// Component to display ModIQ configuration metrics
const ModIQConfigMetrics = ({ config, allResults, floorCount }) => {
  const strategyResults = allResults[config.strategy];
  if (!strategyResults) return null;

  const totalBoxes = strategyResults.boxes.length;
  const totalSqFt = strategyResults.boxes.reduce((sum, box) => sum + (box.width * box.height), 0);

  return (
    <div style={{ marginTop: '8px' }}>
      <p style={{ margin: '0 0 4px 0' }}>
        <strong>Total Boxes:</strong> {totalBoxes} ({totalBoxes * floorCount} with {floorCount} floors)
      </p>
      <p style={{ margin: '0' }}>
        <strong>Total Area:</strong> {Math.round(totalSqFt)} sq ft ({Math.round(totalSqFt * floorCount)} with {floorCount} floors)
      </p>
    </div>
  );
};

const SimpleModIQVisualizer = ({ 
  pcaBuildingData, 
  showPCASettings = false, 
  onShowPCASettingsChange = () => {}, 
  onPCABuildingDataChange = () => {},
  onLogout = null, // Add logout prop
  initialProjectId = null, // Project ID from URL to auto-load
  onProjectChange = () => {} // Callback when project changes (for URL updates)
}) => {
  // Add CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes modiq-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Main settings
  const [boxWidth, setBoxWidth] = useState(13.7);
  const [boxLength, setBoxLength] = useState(55);
  const [buildingPadding, setBuildingPadding] = useState(20);
  const [buildingHalo, setBuildingHalo] = useState(1); // New: 1x-10x footprint multiplier
  const [parcelWidth, setParcelWidth] = useState(200);
  const [parcelLength, setParcelLength] = useState(150);
  const [setbackNorth, setSetbackNorth] = useState(10);
  const [setbackEast, setSetbackEast] = useState(10);
  const [setbackSouth, setSetbackSouth] = useState(10);
  const [setbackWest, setSetbackWest] = useState(10);
  const [setbackPoints, setSetbackPoints] = useState(null);
  // Track edge-based setback distances from MapBox PCA system
  const [edgeSetbackDistances, setEdgeSetbackDistances] = useState([]);
  const [currentLayout, setCurrentLayout] = useState('y-parallel');
  
  // Box dimension constraints
  const [minBoxWidth, setMinBoxWidth] = useState(12.29);
  const [maxBoxWidth, setMaxBoxWidth] = useState(15.7);
  const [minBoxLength, setMinBoxLength] = useState(45);
  const [maxBoxLength, setMaxBoxLength] = useState(68);
  
  // Display options
  const [showDimensions, setShowDimensions] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(10);
  const [showLivableSpacesTable, setShowLivableSpacesTable] = useState(true);
  const [showUnitDataTable, setShowUnitDataTable] = useState(false); // Collapsed by default
  
  // Multi-floor options
  const [floorCount, setFloorCount] = useState(1);
  
  // Market selection state (moved from IntelligentUnitMixAdvisor to affect both panels)
  const [selectedMarket, setSelectedMarket] = useState('GRAND_RAPIDS');
  const [customRentOverrides, setCustomRentOverrides] = useState(null); // null = use default rates
  const [showRentEditModal, setShowRentEditModal] = useState(false);
  
  // Project address (captured from MapBox search)
  const [projectAddress, setProjectAddress] = useState(null);
  
  // Collapsible panel states
  const [showCurrentSelection, setShowCurrentSelection] = useState(true);
  const [showMaxRevenue, setShowMaxRevenue] = useState(true);
  
  // Podium state
  const [hasPodium, setHasPodium] = useState(false);
  
  // Layout state managed by reducer
  const [layoutState, dispatch] = useReducer(layoutReducer, {
    buildPaths: [],
    boxes: [],
    buildingOptimizations: {},
    selectedOptimizations: {},
    selectedBuildingId: null
  });
  
  // Color settings
  const [colors, setColors] = useState({
    parcel: "#b1e3fa",
    buildableArea: "#b1e3fa",
    buildPath: "#FFFF00",
    buildingPadding: "#00FF00",
    box: "#FF5733",
    livableSpace: "#8BC34A",
    hallway: "#9E9E9E",
    grid: "#CCCCCC"
  });
  
  // Add Mapbox state
  const [showMapbox, setShowMapbox] = useState(false);
  const [showSetbacks, setShowSetbacks] = useState(false);
  const [mapLightMode, setMapLightMode] = useState(true);
  const [parcelCoordinates, setParcelCoordinates] = useState(null);
  
  // Add state for PCA building data
  const [internalPcaBuildingData, setInternalPcaBuildingData] = useState(null);
  
  // Add internal state for settings panel when no prop handler is provided
  const [internalShowPCASettings, setInternalShowPCASettings] = useState(false);
  
  // Simple and reliable approach - just use internal state to ensure settings work
  const actualShowPCASettings = internalShowPCASettings;
  const actualOnShowPCASettingsChange = setInternalShowPCASettings;
  
  // Add state for setback dialog
  const [showSetbackDialog, setShowSetbackDialog] = useState(false);
  const [setAllSetbacks, setSetAllSetbacks] = useState(false);
  const [setbackValue, setSetbackValue] = useState('');
  
  // Add state for responsive design
  const [isMobile, setIsMobile] = useState(false);
  
  // Add state for PCA layout strategy
  const [pcaLayoutStrategy, setPcaLayoutStrategy] = useState('smart-hybrid');
  
  // Layout Mode: 'density' (maximize boxes) or 'precision' (centered on line)
  const [layoutMode, setLayoutMode] = useState('density');
  
  // Density Mode: starting corner for fill direction
  const [startingCorner, setStartingCorner] = useState('SW');
  
  // Precision Mode: specific box count (null = auto-fill to boundaries)
  const [precisionBoxCount, setPrecisionBoxCount] = useState(null);
  
  // Precision Mode: X-axis offset perpendicular to orientation line in feet
  const [precisionOffset, setPrecisionOffset] = useState(0);
  
  // Precision Mode: Y-axis offset along orientation line in feet
  const [precisionOffsetY, setPrecisionOffsetY] = useState(0);

  // Add responsive effect
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  // Calculate parcel bounds from coordinates
  const calculateParcelBounds = (coordinates) => {
    if (!coordinates || coordinates.length < 3) return { width: 200, height: 150 };

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    coordinates.forEach(coord => {
      minX = Math.min(minX, coord[0]);
      maxX = Math.max(maxX, coord[0]);
      minY = Math.min(minY, coord[1]);
      maxY = Math.max(maxY, coord[1]);
    });

    // Convert to feet (approximate conversion from degrees)
    const feetPerDegree = 364000;
    const width = Math.round((maxX - minX) * feetPerDegree);
    const height = Math.round((maxY - minY) * feetPerDegree);

    // Store the original coordinates relative to minX, minY
    const relativeCoordinates = coordinates.map(coord => [
      (coord[0] - minX) * feetPerDegree,
      (coord[1] - minY) * feetPerDegree
    ]);

    return { 
      width, 
      height,
      minX,
      minY,
      relativeCoordinates
    };
  };
  
  // Memoize buildable area calculations
  const buildableArea = useMemo(() => {
    if (!parcelCoordinates || !setbackPoints) {
      return {
        x1: setbackWest,
        y1: setbackNorth,
        x2: parcelWidth - setbackEast,
        y2: parcelLength - setbackSouth,
        width: parcelWidth - setbackWest - setbackEast,
        height: parcelLength - setbackNorth - setbackSouth
      };
    }

    // Calculate the buildable polygon by offsetting each parcel edge inward by its setback distance
    const bounds = calculateParcelBounds(parcelCoordinates);
    const buildableCoordinates = setbackPoints.map((setback, index) => {
      const [start, end] = setback;
      const startX = (start[0] - bounds.minX) * 364000;
      const startY = parcelLength - ((start[1] - bounds.minY) * 364000); // Invert Y coordinate
      return [startX, startY];
    });

    // Calculate the bounding box of the buildable area
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    buildableCoordinates.forEach(coord => {
      minX = Math.min(minX, coord[0]);
      maxX = Math.max(maxX, coord[0]);
      minY = Math.min(minY, coord[1]);
      maxY = Math.max(maxY, coord[1]);
    });

    return {
      x1: minX,
      y1: minY,
      x2: maxX,
      y2: maxY,
      width: maxX - minX,
      height: maxY - minY,
      coordinates: buildableCoordinates
    };
  }, [
    parcelWidth, parcelLength,
    setbackNorth, setbackEast, setbackSouth, setbackWest,
    parcelCoordinates, setbackPoints
  ]);
  
  // Update setback values based on drawn setbacks
  const updateSetbackValues = (setbacks) => {
    if (!setbacks || setbacks.length < 4) return;

    // Calculate average setback distance for each side
    const setbackDistances = setbacks.map(setback => {
      const [start, end] = setback;
      return Math.sqrt(
        Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)
      ) * 364000; // Convert to feet
    });

    setSetbackNorth(Math.round(setbackDistances[0]));
    setSetbackEast(Math.round(setbackDistances[1]));
    setSetbackSouth(Math.round(setbackDistances[2]));
    setSetbackWest(Math.round(setbackDistances[3]));
  };

  // Generate layout based on selected strategy
  const generateLayout = useCallback((layoutStrategy) => {
    const layoutToUse = layoutStrategy || currentLayout;
    const newBuildPaths = [];
    const newBoxes = [];
    
    if (layoutToUse === 'y-parallel') {
      // Y-Axis Parallel Layout
      let currentY = buildableArea.y1;
      
      while (currentY + boxLength <= buildableArea.y2) {
        // Create build path
        const buildPath = {
          id: newBuildPaths.length + 1,
          x: buildableArea.x1,
          y: currentY,
          width: buildableArea.width,
          height: boxLength,
          padding: buildingPadding,
          direction: 'west-east'
        };
        
        newBuildPaths.push(buildPath);
        
        // Place boxes in this path
        let currentX = buildPath.x;
        while (currentX + boxWidth <= buildPath.x + buildPath.width) {
          newBoxes.push({
            id: newBoxes.length + 1,
            buildPathId: buildPath.id,
            x: currentX,
            y: currentY,
            width: boxWidth,
            height: boxLength,
            orientation: 'horizontal'
          });
          
          currentX += boxWidth;
        }
        
        currentY += boxLength + buildingPadding;
      }
    } 
    else if (layoutToUse === 'y-perpendicular') {
      // Y-Axis Perpendicular Layout
      let currentY = buildableArea.y1;
      
      // First build path running West to East
      if (currentY + boxLength <= buildableArea.y2) {
        const firstBuildPath = {
          id: newBuildPaths.length + 1,
          x: buildableArea.x1,
          y: currentY,
          width: buildableArea.width,
          height: boxLength,
          padding: buildingPadding,
          direction: 'west-east'
        };
        
        newBuildPaths.push(firstBuildPath);
        
        // Place boxes in first path
        let boxX = firstBuildPath.x;
        while (boxX + boxWidth <= firstBuildPath.x + firstBuildPath.width) {
          newBoxes.push({
            id: newBoxes.length + 1,
            buildPathId: firstBuildPath.id,
            x: boxX,
            y: currentY,
            width: boxWidth,
            height: boxLength,
            orientation: 'horizontal'
          });
          
          boxX += boxWidth;
        }
        
        currentY += boxLength;
        
        // Now add perpendicular build paths
        let perpPathX = buildableArea.x1;
        while (perpPathX + boxLength <= buildableArea.x2) {
          if (currentY + buildingPadding + boxWidth <= buildableArea.y2) {
            const buildPath = {
              id: newBuildPaths.length + 1,
              x: perpPathX,
              y: currentY + buildingPadding,
              width: boxLength,
              height: buildableArea.y2 - (currentY + buildingPadding),
              padding: buildingPadding,
              direction: 'north-south'
            };
            
            newBuildPaths.push(buildPath);
            
            // Place boxes in this path
            let boxY = buildPath.y;
            while (boxY + boxWidth <= buildPath.y + buildPath.height) {
              newBoxes.push({
                id: newBoxes.length + 1,
                buildPathId: buildPath.id,
                x: perpPathX,
                y: boxY,
                width: boxLength,
                height: boxWidth,
                orientation: 'vertical'
              });
              
              boxY += boxWidth;
            }
          }
          
          perpPathX += boxLength + buildingPadding;
        }
      }
    }
    else if (layoutToUse === 'x-parallel') {
      // X-Axis Parallel Layout
      let currentX = buildableArea.x1;
      
      while (currentX + boxLength <= buildableArea.x2) {
        const buildPath = {
          id: newBuildPaths.length + 1,
          x: currentX,
          y: buildableArea.y1,
          width: boxLength,
          height: buildableArea.height,
          padding: buildingPadding,
          direction: 'north-south'
        };
        
        newBuildPaths.push(buildPath);
        
        let currentY = buildPath.y;
        while (currentY + boxWidth <= buildPath.y + buildPath.height) {
          newBoxes.push({
            id: newBoxes.length + 1,
            buildPathId: buildPath.id,
            x: currentX,
            y: currentY,
            width: boxLength,
            height: boxWidth,
            orientation: 'vertical'
          });
          
          currentY += boxWidth;
        }
        
        currentX += boxLength + buildingPadding;
      }
    }
    else if (layoutToUse === 'x-perpendicular') {
      // X-Axis Perpendicular Layout
      let currentX = buildableArea.x1;
      
      // First build path running North to South
      if (currentX + boxLength <= buildableArea.x2) {
        const firstBuildPath = {
          id: newBuildPaths.length + 1,
          x: currentX,
          y: buildableArea.y1,
          width: boxLength,
          height: buildableArea.height,
          padding: buildingPadding,
          direction: 'north-south'
        };
        
        newBuildPaths.push(firstBuildPath);
        
        let boxY = firstBuildPath.y;
        while (boxY + boxWidth <= firstBuildPath.y + firstBuildPath.height) {
          newBoxes.push({
            id: newBoxes.length + 1,
            buildPathId: firstBuildPath.id,
            x: currentX,
            y: boxY,
            width: boxLength,
            height: boxWidth,
            orientation: 'vertical'
          });
          
          boxY += boxWidth;
        }
        
        currentX += boxLength;
        
        // Add perpendicular build paths
        let perpPathY = buildableArea.y1;
        while (perpPathY + boxLength <= buildableArea.y2) {
          if (currentX + buildingPadding + boxWidth <= buildableArea.x2) {
            const buildPath = {
              id: newBuildPaths.length + 1,
              x: currentX + buildingPadding,
              y: perpPathY,
              width: buildableArea.x2 - (currentX + buildingPadding),
              height: boxLength,
              padding: buildingPadding,
              direction: 'west-east'
            };
            
            newBuildPaths.push(buildPath);
            
            let boxX = buildPath.x;
            while (boxX + boxWidth <= buildPath.x + buildPath.width) {
              newBoxes.push({
                id: newBoxes.length + 1,
                buildPathId: buildPath.id,
                x: boxX,
                y: perpPathY,
                width: boxWidth,
                height: boxLength,
                orientation: 'horizontal'
              });
              
              boxX += boxWidth;
            }
          }
          
          perpPathY += boxLength + buildingPadding;
        }
      }
    }
    
    // Filter out buildings with fewer than 4 boxes (minimum building size rule)
    const boxCountsByBuilding = {};
    newBoxes.forEach(box => {
      if (!boxCountsByBuilding[box.buildPathId]) {
        boxCountsByBuilding[box.buildPathId] = 0;
      }
      boxCountsByBuilding[box.buildPathId]++;
    });
    
    // Filter build paths to only include those with >= 4 boxes
    const validBuildPaths = newBuildPaths.filter(buildPath => 
      (boxCountsByBuilding[buildPath.id] || 0) >= 4
    );
    
    // Filter boxes to only include those from valid buildings
    const validBuildPathIds = new Set(validBuildPaths.map(bp => bp.id));
    const validBoxes = newBoxes.filter(box => 
      validBuildPathIds.has(box.buildPathId)
    );
    
    // Reassign sequential IDs to maintain consistency
    const finalBuildPaths = validBuildPaths.map((buildPath, index) => ({
      ...buildPath,
      id: index + 1
    }));
    
    // Create mapping from old IDs to new IDs
    const idMapping = {};
    validBuildPaths.forEach((buildPath, index) => {
      idMapping[buildPath.id] = index + 1;
    });
    
    // Update box buildPathIds and reassign sequential box IDs
    const finalBoxes = validBoxes.map((box, index) => ({
      ...box,
      id: index + 1,
      buildPathId: idMapping[box.buildPathId]
    }));
    
    return { newBuildPaths: finalBuildPaths, newBoxes: finalBoxes };
  }, [
    boxWidth, boxLength, buildingPadding, currentLayout, buildableArea
  ]);
  
  // Memoize optimization constraints
  const constraints = useMemo(() => ({
    minBoxWidth,
    maxBoxWidth,
    minBoxLength,
    maxBoxLength
  }), [minBoxWidth, maxBoxWidth, minBoxLength, maxBoxLength]);
  
  // Effect for layout generation and optimization
  useEffect(() => {
    // Early exit if no buildings exist yet - prevents unnecessary runs on component mount
    if ((!layoutState.buildPaths || layoutState.buildPaths.length === 0) && 
        (!internalPcaBuildingData || !internalPcaBuildingData.buildPaths || internalPcaBuildingData.buildPaths.length === 0)) {
      console.log('⏭️ Skipping optimization - no buildings available yet');
      return;
    }

    console.log('🚨 OPTIMIZATION USEEFFECT TRIGGERED! 🚨', {
      selectedOptimizations: layoutState.selectedOptimizations,
      timestamp: new Date().toISOString()
    });
    
    console.log('Optimization effect triggered:', {
      internalPcaBuildingData: !!internalPcaBuildingData,
      buildPaths: internalPcaBuildingData?.buildPaths?.length || layoutState.buildPaths?.length || 0,
      orientationAngle: internalPcaBuildingData?.orientationAngle,
      buildingPadding: buildingPadding,
      boxWidth: boxWidth,
      boxLength: boxLength,
      selectedOptimizations: layoutState.selectedOptimizations,
      layoutStateBoxes: layoutState.boxes?.length || 0,
      layoutStateBuildPaths: layoutState.buildPaths?.length || 0
    });

    // Determine which data source to use - PRIORITIZE LAYOUT STATE (which gets updated by PCA)
    let buildPaths, boxes;
    if (layoutState.buildPaths && layoutState.buildPaths.length > 0 && layoutState.boxes && layoutState.boxes.length > 0) {
      // Use layout state data (PREFERRED - this is where PCA data gets stored)
      buildPaths = layoutState.buildPaths;
      boxes = layoutState.boxes;
      console.log('🎯 Using layout state data for optimizations (PREFERRED - includes PCA data)');
    } else if (internalPcaBuildingData && internalPcaBuildingData.buildPaths && internalPcaBuildingData.buildPaths.length > 0) {
      // Use PCA data as fallback (shouldn't happen if handleBuildingDataUpdate works correctly)
      buildPaths = internalPcaBuildingData.buildPaths;
      boxes = internalPcaBuildingData.boxes;
      console.log('🎯 Using PCA building data for optimizations (FALLBACK)');
    } else {
      console.log('❌ No building data available for optimizations - skipping');
      return;
    }

    // Determine buildable area coordinates - PRIORITIZE PCA DATA
    let buildableAreaCoords = null;
    if (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) {
      buildableAreaCoords = internalPcaBuildingData.buildableAreaCoords;
      console.log('🏗️ Using PCA buildableAreaCoords (PREFERRED)');
    } else if (buildableArea && buildableArea.coordinates) {
      buildableAreaCoords = buildableArea.coordinates;
      console.log('🏗️ Using legacy buildableArea.coordinates');
    } else if (parcelCoordinates && parcelCoordinates.length > 0) {
      buildableAreaCoords = parcelCoordinates;
      console.log('🏗️ Using parcelCoordinates as fallback');
    } else {
      console.log('❌ No buildable area coordinates available - skipping optimization');
      return;
    }

    console.log('🎯 Final buildableAreaCoords being passed to optimizer:', {
      isNull: buildableAreaCoords === null,
      isArray: Array.isArray(buildableAreaCoords),
      length: buildableAreaCoords?.length,
      type: typeof buildableAreaCoords,
      source: internalPcaBuildingData?.buildableAreaCoords ? 'PCA' : buildableArea?.coordinates ? 'legacy' : parcelCoordinates?.length ? 'parcel' : 'none'
    });

    // Generate optimizations for each building
    const newBuildingOptimizations = {};
    // Ensure buildPaths and boxes are arrays before iterating
    const safeBuildPaths = Array.isArray(buildPaths) ? buildPaths : [];
    const safeBoxes = Array.isArray(boxes) ? boxes : [];
    safeBuildPaths.forEach(buildPath => {
      const buildingId = buildPath.id;
      const buildingBoxes = safeBoxes.filter(box => box.buildPathId === buildingId);
      
      console.log(`🔧 Processing building ${buildingId} with ${buildingBoxes.length} boxes for optimization`);
      
      if (buildingBoxes.length === 0) {
        console.log(`⚠️ No boxes found for building ${buildingId}, skipping optimization`);
        return;
      }
      
      // 🚨 DEBUGGING: Add logs right before calling efficiency optimization
      console.log('🚨🚨🚨 ABOUT TO CALL EFFICIENCY OPTIMIZATION! 🚨🚨🚨');
      console.log('📋 Parameters being passed:', {
        buildingBoxes: buildingBoxes.length,
        buildPath: buildPath?.id,
        buildableAreaCoords: buildableAreaCoords?.length,
        constraints: constraints,
        dataSource: internalPcaBuildingData?.buildableAreaCoords ? 'PCA' : 'fallback'
      });
      
      // Run optimizations with appropriate optimizer
      const efficiencyResult = pcaBoxOptimizer.shrinkBoxesForEfficiency(
        buildingBoxes, 
        [buildPath], // Pass buildPath as array to match function signature
        buildableAreaCoords, // Pass proper buildable area coordinates (not parcel)
        constraints
      );
      
      console.log('📊 EFFICIENCY RESULT RECEIVED:', {
        hasOptimizedBoxes: !!efficiencyResult.optimizedBoxes,
        optimizedBoxesCount: efficiencyResult.optimizedBoxes?.length,
        hasMetrics: !!efficiencyResult.metrics,
        metricsBoxCount: efficiencyResult.metrics?.boxCount,
        metricsEfficiency: efficiencyResult.metrics?.efficiency
      });
      
      const livabilityResult = pcaBoxOptimizer.expandBoxesForLivability(
        buildingBoxes, 
        [buildPath], // Pass buildPath as array to match function signature
        buildableAreaCoords, // Pass proper buildable area coordinates (not parcel)
        constraints
      );

      console.log('📊 LIVABILITY RESULT RECEIVED:', {
        hasOptimizedBoxes: !!livabilityResult.optimizedBoxes,
        optimizedBoxesCount: livabilityResult.optimizedBoxes?.length,
        hasMetrics: !!livabilityResult.metrics,
        metricsLivability: livabilityResult.metrics?.livability
      });

      newBuildingOptimizations[buildingId] = {
        efficiency: {
          optimizedBoxes: efficiencyResult.optimizedBoxes,
          optimizedDimensions: efficiencyResult.optimizedDimensions,
          layoutAnalysis: efficiencyResult.layoutAnalysis,
          metrics: efficiencyResult.metrics
        },
        livability: {
          optimizedBoxes: livabilityResult.optimizedBoxes,
          optimizedDimensions: livabilityResult.optimizedDimensions,
          layoutAnalysis: livabilityResult.layoutAnalysis,
          metrics: livabilityResult.metrics
        }
      };
    });

    console.log('🎯 FINAL OPTIMIZATION RESULTS:', {
      buildingCount: Object.keys(newBuildingOptimizations).length,
      buildingIds: Object.keys(newBuildingOptimizations),
      hasEfficiencyResults: Object.values(newBuildingOptimizations).some(opt => opt.efficiency?.optimizedBoxes?.length > 0),
      hasLivabilityResults: Object.values(newBuildingOptimizations).some(opt => opt.livability?.optimizedBoxes?.length > 0)
    });

    dispatch({ 
      type: ACTIONS.SET_OPTIMIZATIONS, 
      payload: newBuildingOptimizations 
    });
  }, [
    internalPcaBuildingData,
    pcaLayoutStrategy, // Add layout strategy dependency so optimizations recalculate when strategy changes
    internalPcaBuildingData?.orientationAngle, // Add orientation angle dependency so optimizations recalculate when angle changes
    constraints,
    buildableArea, // Add buildable area as dependency
    buildingPadding,
    boxWidth,
    boxLength,
    setbackNorth, // Add setback dependencies so optimizations recalculate when setbacks change
    setbackEast,
    setbackSouth,
    setbackWest,
    layoutState.selectedOptimizations, // Add this so optimizations recalculate when user selects different optimization types
    layoutState.boxes, // Add missing dependency
    layoutState.buildPaths, // Add missing dependency
    parcelCoordinates // Add missing dependency
  ]);

  // Group livable spaces by building
  const getGroupedSpaces = () => {
    const grouped = {};
    
    layoutState.livableSpaces.forEach(space => {
      const buildingId = space.buildingId;
      
      if (!grouped[buildingId]) {
        grouped[buildingId] = [];
      }
      
      grouped[buildingId].push(space);
    });
    
    return grouped;
  };
  
  // Calculate results
  const calculateResults = () => {
    const results = {
      componentArea: 0,      // Area of all boxes/components (all floors)
      podiumArea: 0,         // Area of podium (building footprint)
      totalBuildingArea: 0,  // Total of both
      totalLivableSpaces: 0,
      totalBoxes: 0
    };

    // Gather all currently displayed boxes (optimized or standard per building)
    let activeBoxes = [];
    let totalComponentArea = 0;
    let totalBoxes = 0;
    
    // Ensure buildPaths and boxes are arrays before iterating
    const safeLayoutBuildPaths = Array.isArray(layoutState.buildPaths) ? layoutState.buildPaths : [];
    const safeLayoutBoxes = Array.isArray(layoutState.boxes) ? layoutState.boxes : [];
    safeLayoutBuildPaths.forEach(buildPath => {
      const buildingId = buildPath.id;
      const optimization = layoutState.selectedOptimizations[buildingId] || 'none';
      
      // Get per-building floor count (fallback to global if not set)
      // const buildingFloors = buildingSettings[buildingId]?.floors || floorCount;
      
      let buildingBoxes = [];
      if (optimization === 'none') {
        buildingBoxes = safeLayoutBoxes.filter(box => box.buildPathId === buildingId);
      } else {
        const buildingOptimization = layoutState.buildingOptimizations[buildingId] || {};
        const optimizationResult = optimization === 'efficiency'
          ? buildingOptimization.efficiency
          : buildingOptimization.livability;
        if (optimizationResult?.optimizedBoxes) {
          buildingBoxes = optimizationResult.optimizedBoxes;
        } else {
          buildingBoxes = safeLayoutBoxes.filter(box => box.buildPathId === buildingId);
        }
      }
      
      // Calculate area for this building using its specific floor count
      const buildingSingleFloorArea = buildingBoxes.reduce((total, box) => total + (box.width * box.height), 0);
      const buildingTotalArea = buildingSingleFloorArea * floorCount;
      totalComponentArea += buildingTotalArea;
      
      // Calculate total boxes for this building
      totalBoxes += buildingBoxes.length * floorCount;
      
      activeBoxes = activeBoxes.concat(buildingBoxes);
    });

    // Use per-building calculated totals
    results.componentArea = totalComponentArea;

    // Podium area (sum of all build path footprints, only once)
    if (hasPodium) {
      const safeBuildPathsForPodium = Array.isArray(layoutState.buildPaths) ? layoutState.buildPaths : [];
      results.podiumArea = safeBuildPathsForPodium.reduce((total, buildPath) => total + (buildPath.width * buildPath.height), 0);
    }

    results.totalBuildingArea = results.componentArea + results.podiumArea;

    // Livable spaces and boxes
    results.totalBoxes = totalBoxes;
    results.totalLivableSpaces = totalBoxes * 2;

    return results;
  };
  
  const results = calculateResults();
  
  // Export data to CSV
  const exportToCSV = () => {
    const header = "ID,Building,Tower,Floor,Side,Box,X Position,Y Position,Position Width,Position Height,Width (ft),Length (ft),Square Feet\n";
    
    const csvContent = livableSpaces.map(space => {
      return `${space.id},${space.buildingId},${space.towerId},${space.floorId},${space.side},${space.boxId},${space.position.x},${space.position.y},${space.position.width},${space.position.height},${space.width},${space.length},${space.squareFeet}`;
    }).join("\n");
    const csv = header + csvContent;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `modiq-layout-${currentLayout}-${new Date().toISOString().slice(0,10)}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };
  
  // Handle parcel drawing
  const handleParcelDrawn = (coordinates) => {
    setParcelCoordinates(coordinates);
    if (coordinates) {
      // Calculate parcel dimensions from coordinates
      const bounds = calculateParcelBounds(coordinates);
      setParcelWidth(bounds.width);
      setParcelLength(bounds.height);
      // Generate new layout after updating dimensions
      generateLayout();
    }
  };

  // Handle building data from PCA system
  const handleBuildingDataUpdate = useCallback((pcaData) => {
    console.log('🏗️ STEP 6A: handleBuildingDataUpdate called');
    console.log('   Received pcaData:', {
      hasBuildPaths: !!pcaData?.buildPaths,
      buildPathsCount: pcaData?.buildPaths?.length || 0,
      hasBoxes: !!pcaData?.boxes,
      boxesCount: pcaData?.boxes?.length || 0,
      angle: pcaData?.orientationAngle,
      strategy: pcaData?.strategy,
      hasCoords: !!pcaData?.buildableAreaCoords,
      minBoxesPerBuilding: pcaData?.minBoxesPerBuilding
    });
    
    // Filter out buildings with fewer boxes than the minimum setting
    // Ensure buildPaths and boxes are arrays
    const safePcaBuildPaths = Array.isArray(pcaData?.buildPaths) ? pcaData.buildPaths : [];
    const safePcaBoxes = Array.isArray(pcaData?.boxes) ? pcaData.boxes : [];
    if (pcaData && safePcaBuildPaths.length > 0 && safePcaBoxes.length > 0) {
      const minBoxesRequired = pcaData.minBoxesPerBuilding || 3; // Use setting or fallback to 3
      
      console.log(`🔍 Filtering buildings with minimum ${minBoxesRequired} boxes requirement`);
      
      // Filter buildings that meet the minimum box requirement
      const validBuildings = safePcaBuildPaths.filter(buildPath => {
        const buildingBoxes = safePcaBoxes.filter(box => box.buildPathId === buildPath.id);
        const isValid = buildingBoxes.length >= minBoxesRequired;
        
        if (!isValid) {
          console.log(`❌ Filtering out Building ${buildPath.id}: only ${buildingBoxes.length} boxes (need ${minBoxesRequired})`);
        } else {
          console.log(`✅ Keeping Building ${buildPath.id}: ${buildingBoxes.length} boxes (meets ${minBoxesRequired} minimum)`);
        }
        
        return isValid;
      });
      
      // Filter boxes to only include those from valid buildings
      const validBuildingIds = new Set(validBuildings.map(building => building.id));
      const validBoxes = safePcaBoxes.filter(box => validBuildingIds.has(box.buildPathId));
      
      console.log(`📊 Filtering results:`, {
        originalBuildings: safePcaBuildPaths.length,
        validBuildings: validBuildings.length,
        filteredOut: safePcaBuildPaths.length - validBuildings.length,
        originalBoxes: safePcaBoxes.length,
        validBoxes: validBoxes.length,
        filteredBoxes: safePcaBoxes.length - validBoxes.length
      });
      
      // Update pcaData with filtered buildings and boxes
      const filteredPcaData = {
        ...pcaData,
        buildPaths: validBuildings,
        boxes: validBoxes
      };
      
      console.log('🏗️ STEP 6B: Setting filtered PCA building data');
      setInternalPcaBuildingData(filteredPcaData);
      
      // Update layout state with filtered PCA building data
      if (validBuildings.length > 0) {
        console.log('🏗️ STEP 6C: Dispatching SET_LAYOUT with filtered data');
        dispatch({ 
          type: ACTIONS.SET_LAYOUT, 
          payload: { 
            buildPaths: validBuildings, 
            boxes: validBoxes 
          }
        });
        
        // Update box dimensions to match PCA settings
        console.log('🏗️ STEP 6D: Updating box dimensions');
        setBoxWidth(pcaData.boxWidth);
        setBoxLength(pcaData.boxLength);
        setBuildingPadding(pcaData.buildingPadding);
      } else {
        console.log('🏗️ STEP 6C: No valid buildings after filtering - clearing layout state');
        dispatch({ 
          type: ACTIONS.SET_LAYOUT, 
          payload: { 
            buildPaths: [], 
            boxes: [] 
          }
        });
      }
    } else {
      console.log('🏗️ STEP 6B: No PCA data to filter - setting as-is');
      setInternalPcaBuildingData(pcaData);
      
      if (pcaData && pcaData.buildPaths && pcaData.buildPaths.length > 0) {
        console.log('🏗️ STEP 6C: Dispatching SET_LAYOUT without filtering');
        dispatch({ 
          type: ACTIONS.SET_LAYOUT, 
          payload: { 
            buildPaths: pcaData.buildPaths, 
            boxes: pcaData.boxes 
          }
        });
      }
    }
    
    console.log('🏗️ STEP 6E: handleBuildingDataUpdate completed');
  }, [dispatch]);

  // Handle setback updates
  const handleSetbacksUpdated = (setbacks) => {
    setSetbackPoints(setbacks);
    if (setbacks) {
      // Update setback values based on the drawn setbacks
      updateSetbackValues(setbacks);
      // Generate new layout after updating setbacks
      generateLayout();
    }
  };

  // Add function to update all setbacks
  const updateAllSetbacks = (value) => {
    setSetbackNorth(Number(value));
    setSetbackEast(Number(value));
    setSetbackSouth(Number(value));
    setSetbackWest(Number(value));
  };

  // Helper function to calculate percentage change
  const calculateChange = (newValue, originalValue) => {
    console.log('🔍 calculateChange DEBUG:', {
      newValue: newValue,
      originalValue: originalValue,
      difference: newValue - originalValue,
      percentChange: originalValue ? ((newValue - originalValue) / originalValue) * 100 : 0
    });
    
    if (!originalValue) return { change: 0, symbol: '–' };
    const percentChange = ((newValue - originalValue) / originalValue) * 100;
    
    console.log('📊 Percent change calculation:', {
      percentChange: percentChange,
      absoluteChange: Math.abs(percentChange),
      isUnderThreshold: Math.abs(percentChange) < 0.1,
      threshold: 0.1
    });
    
    // Return no change if the difference is very small (floating point comparison)
    if (Math.abs(percentChange) < 0.1) return { change: 0, symbol: '–' };
    return {
      change: Math.abs(percentChange).toFixed(1),
      symbol: percentChange > 0 ? '↑' : percentChange < 0 ? '↓' : '–'
    };
  };

  // Helper function to render change indicator
  const renderChangeIndicator = (newValue, originalValue) => {
    const { change, symbol } = calculateChange(newValue, originalValue);
    if (symbol === '–') {
      return (
        <span style={{
          marginLeft: '8px',
          color: '#1a202c', // Black color for no change
          fontSize: '0.875rem',
          fontWeight: '500',
          whiteSpace: 'nowrap'
        }}>
          − 0.0%
        </span>
      );
    }

    return (
      <span style={{
        marginLeft: '8px',
        color: symbol === '↑' ? '#059669' : '#DC2626',
        fontSize: '0.875rem',
        fontWeight: '500',
        whiteSpace: 'nowrap'
      }}>
        {symbol} {change}%
      </span>
    );
  };

  // Standard Unit Card - Store standard values for comparison
  const standardBoxWidth = boxWidth;
  const standardBoxLength = boxLength;
  const standardBoxSqFt = boxWidth * boxLength;
  const standardBoxCount = (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === layoutState.selectedBuildingId).length;
  const standardBuildingSqFt = standardBoxCount * standardBoxSqFt;
  const standardUnitCount = standardBoxCount * 2;

  // Metric Row Component
  const MetricRow = ({ label, value, comparison, multiFloor, multiFloorValue }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      padding: '3px 0',
      gap: '6px',
      borderBottom: '1px solid rgba(0,0,0,0.03)'
    }}>
      <div style={{ 
        flex: '1',
        fontSize: '0.8rem',
        color: '#1a202c',
        fontWeight: '500'
      }}>
        {label}:
      </div>
      <div style={{ 
        fontSize: '0.8rem',
        color: '#1a202c',
        fontWeight: '500',
        textAlign: 'right',
        minWidth: '60px'
      }}>
        {value}
        {multiFloor && floorCount > 1 && (
          <span style={{ color: '#6B7280', fontSize: '0.75rem', display: 'block' }}>
            ({multiFloorValue})
          </span>
        )}
        {comparison}
      </div>
    </div>
  );

  // Compact Metric Row for side-by-side display
  const CompactMetricRow = ({ label, value, comparison }) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '2px 0'
    }}>
      <span style={{ 
        fontSize: '0.75rem',
        color: '#4a5568',
        fontWeight: '500'
      }}>
        {label}:
      </span>
      <span style={{ 
        fontSize: '0.75rem',
        color: '#1a202c',
        fontWeight: '600'
      }}>
        {value}{comparison}
      </span>
    </div>
  );

  // Add ModIQ analysis state
  const [showModIQDialog, setShowModIQDialog] = useState(false);
  const [modIQResults, setModIQResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Add comprehensive optimization state
  const [isComprehensiveOptimizing, setIsComprehensiveOptimizing] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(null);
  const [comprehensiveResults, setComprehensiveResults] = useState(null);
  
  // Add Project Overview dialog state
  const [showProjectOverview, setShowProjectOverview] = useState(false);
  
  // Add optimal configuration state for passing to PCA component
  const [optimalConfiguration, setOptimalConfiguration] = useState(null);

  // Add function to apply optimal configuration from ModIQ Pro
  const applyOptimalConfiguration = useCallback((winner) => {
    console.log('🎯 STEP 1: Starting optimal configuration application');
    console.log('   Winner data:', {
      angle: winner.angle,
      strategy: winner.strategy,
      strategyName: winner.strategyName,
      boxCount: winner.metrics.boxCount,
      buildingCount: winner.metrics.buildingCount,
      hasBuildPaths: !!winner.buildPaths,
      buildPathsCount: winner.buildPaths?.length || 0,
      hasBoxes: !!winner.boxes,
      boxesCount: winner.boxes?.length || 0
    });

    console.log('🎯 STEP 2: Current state before applying configuration');
    console.log('   layoutState:', {
      buildPaths: layoutState.buildPaths?.length || 0,
      boxes: layoutState.boxes?.length || 0
    });
    console.log('   internalPcaBuildingData:', {
      buildPaths: internalPcaBuildingData?.buildPaths?.length || 0,
      boxes: internalPcaBuildingData?.boxes?.length || 0,
      hasCoords: !!internalPcaBuildingData?.buildableAreaCoords
    });

    // DIRECTLY USE THE ACTUAL BUILDING DATA that ModIQ found optimal
    // ModIQ already applied proper filtering during optimization, so use results as-is
    if (winner.buildPaths && winner.boxes) {
      console.log('🎯 STEP 3: Using ModIQ optimal building data directly (no additional filtering)');
      console.log(`📊 ModIQ optimal result: ${winner.boxes.length} boxes in ${winner.buildPaths.length} buildings`);
      
      // Use the optimal data exactly as ModIQ found it - no additional filtering needed
      const optimalBuildings = winner.buildPaths;
      const optimalBoxes = winner.boxes;
      
      // Update layoutState with the exact optimal building data
      console.log('🎯 STEP 4: Dispatching SET_LAYOUT action with optimal data');
      dispatch({ 
        type: ACTIONS.SET_LAYOUT, 
        payload: { 
          buildPaths: optimalBuildings, 
          boxes: optimalBoxes 
        }
      });

      // Update UI controls to match optimal configuration
      console.log('🎯 STEP 4.5: Setting UI controls to match optimal configuration');
      setOrientationOverride(winner.angle);
      setPcaLayoutStrategy(winner.strategy);

      // Update PCA building data so the map visualization matches
      const optimalPCAData = {
        buildPaths: optimalBuildings,
        boxes: optimalBoxes,
        orientationAngle: winner.angle,
        strategy: winner.strategy,
        buildableAreaCoords: internalPcaBuildingData?.buildableAreaCoords || null,
        boxWidth: boxWidth, // Use current box width from settings panel
        boxLength: boxLength, // Use current box length from settings panel  
        buildingPadding: buildingPadding, // Use current building padding from settings panel
        minBoxesPerBuilding: internalPcaBuildingData?.minBoxesPerBuilding || 3 // Include the minimum boxes setting
      };
      
      console.log('🎯 STEP 5: Setting optimal PCA building data');
      console.log('   optimalPCAData:', {
        buildPathsCount: optimalPCAData.buildPaths.length,
        boxesCount: optimalPCAData.boxes.length,
        angle: optimalPCAData.orientationAngle,
        strategy: optimalPCAData.strategy,
        hasCoords: !!optimalPCAData.buildableAreaCoords,
        minBoxesPerBuilding: optimalPCAData.minBoxesPerBuilding
      });
      setInternalPcaBuildingData(optimalPCAData);
      
      // Also call the handler to ensure PCA component is synchronized
      console.log('🎯 STEP 6: Calling handleBuildingDataUpdate with optimal data');
      handleBuildingDataUpdate(optimalPCAData);
      
      console.log('🎯 STEP 7: Configuration application completed');
      
    } else {
      console.warn('⚠️ No building data in ModIQ result, falling back to parameter-based approach');
      
      // Fallback: use the old approach of setting parameters
      const optimalConfig = {
        layoutStrategy: winner.strategy,
        orientationAngle: winner.angle,
        isOptimalConfiguration: true,
        timestamp: Date.now()
      };
      
      setOptimalConfiguration(optimalConfig);
    }
    
    // Close dialog
    console.log('🎯 STEP 8: Closing ModIQ dialog');
    setShowModIQDialog(false);
  }, [dispatch, handleBuildingDataUpdate, internalPcaBuildingData, standardBoxWidth, standardBoxLength, layoutState, boxWidth, boxLength, buildingPadding]);
  
  // Callback when PCA component applies the configuration
  const handleConfigurationApplied = useCallback((appliedConfig) => {
    console.log('✅ Configuration applied in PCA component:', appliedConfig);
    
    // Clear the optimal configuration to prevent re-application
    setOptimalConfiguration(null);
    
    // Configuration applied successfully - no need for alert dialog
    // The user can see the changes in the visualization immediately
  }, []);

  // Calculate comprehensive project metrics
  const calculateProjectMetrics = () => {
    console.log('📊 PROJECT METRICS CALCULATION START:', {
      hasPcaBuildingData: !!(internalPcaBuildingData && internalPcaBuildingData.buildPaths),
      pcaBuildingDataBuildings: internalPcaBuildingData?.buildPaths?.length || 0,
      pcaBuildingDataBoxes: internalPcaBuildingData?.boxes?.length || 0,
      hasLayoutState: !!(layoutState.buildPaths && layoutState.buildPaths.length > 0),
      layoutStateBuildings: layoutState.buildPaths?.length || 0,
      layoutStateBoxes: layoutState.boxes?.length || 0,
      timestamp: Date.now()
    });

    // CRITICAL FIX: Always use the same data source as the map (live PCA data)
    // Priority 1: Use live layout state (what the map actually shows)
    // Priority 2: Fall back to PCA building data only if no live data
    let allActiveBoxes = [];
    let allActiveBuildPaths;
    let sourceBoxes;
    
    if (layoutState.buildPaths && layoutState.buildPaths.length > 0 && layoutState.boxes && layoutState.boxes.length > 0) {
      // Use live layout state (same as map display)
      allActiveBuildPaths = layoutState.buildPaths;
      sourceBoxes = layoutState.boxes;
      console.log('📊 Using LIVE layout state for Project Overview (same as map):', {
        buildings: layoutState.buildPaths.length,
        boxes: layoutState.boxes.length
      });
    } else if (internalPcaBuildingData && internalPcaBuildingData.buildPaths && internalPcaBuildingData.boxes) {
      // Fallback to PCA building data
      allActiveBuildPaths = internalPcaBuildingData.buildPaths;
      sourceBoxes = internalPcaBuildingData.boxes;
      console.log('📊 Using PCA building data for Project Overview (fallback):', {
        buildings: internalPcaBuildingData.buildPaths.length,
        boxes: internalPcaBuildingData.boxes.length
      });
    } else {
      // No data available
      console.log('📊 No data available for Project Overview');
      return {
        totalBuildings: 0,
        totalUnits: 0,
        totalBoxes: 0,
        totalBoxesAllFloors: 0,
        componentAreaPerFloor: 0,
        componentAreaAllFloors: 0,
        podiumArea: 0,
        totalBuildingArea: 0,
        buildableAreaSqFt: 0,
        landEfficiency: 0,
        floorCount: floorCount,
        hasPodium: hasPodium,
        projectTotal: 0
      };
    }
    
    // Get minimum boxes required from PCA building data or settings
    const minBoxesRequired = internalPcaBuildingData?.minBoxesPerBuilding || settingsManager.get('buildingConstraints.minBoxes', 3);
    
    console.log('📊 PROJECT METRICS: Minimum boxes requirement:', minBoxesRequired);
    
    // Filter out buildings with fewer than minimum boxes BEFORE calculating metrics
    console.log('📊 PROJECT METRICS: Filtering buildings...');
    const validBuildPaths = allActiveBuildPaths.filter(buildPath => {
      const buildingBoxes = sourceBoxes.filter(box => box.buildPathId === buildPath.id);
      const isValid = buildingBoxes.length >= minBoxesRequired;
      
      console.log(`🏗️ PROJECT METRICS - Building ${buildPath.id}: ${buildingBoxes.length} boxes, min required: ${minBoxesRequired}, valid: ${isValid}`);
      
      if (!isValid) {
        console.log(`❌ PROJECT OVERVIEW: Filtering out Building ${buildPath.id}: only ${buildingBoxes.length} boxes (need ${minBoxesRequired})`);
      } else {
        console.log(`✅ PROJECT OVERVIEW: Keeping Building ${buildPath.id}: ${buildingBoxes.length} boxes (meets ${minBoxesRequired} minimum)`);
      }
      
      return isValid;
    });
    
    console.log('📊 PROJECT METRICS FILTERING RESULT:', {
      originalBuildings: allActiveBuildPaths.length,
      validBuildings: validBuildPaths.length,
      filteredOutBuildings: allActiveBuildPaths.length - validBuildPaths.length,
      originalBoxes: sourceBoxes.length
    });
    
    // Now process only valid buildings for optimization
    validBuildPaths.forEach(buildPath => {
      const buildingId = buildPath.id;
      const optimization = layoutState.selectedOptimizations[buildingId] || 'none';
      let buildingBoxes = [];
      
      if (optimization === 'none') {
        buildingBoxes = sourceBoxes.filter(box => box.buildPathId === buildingId);
      } else {
        const buildingOptimization = layoutState.buildingOptimizations[buildingId] || {};
        const optimizationResult = optimization === 'efficiency'
          ? buildingOptimization.efficiency
          : buildingOptimization.livability;
        if (optimizationResult?.optimizedBoxes) {
          buildingBoxes = optimizationResult.optimizedBoxes;
        } else {
          buildingBoxes = sourceBoxes.filter(box => box.buildPathId === buildingId);
        }
      }
      allActiveBoxes = allActiveBoxes.concat(buildingBoxes);
    });

    // Calculate areas
    const totalBoxesPerFloor = allActiveBoxes.length;
    const totalBoxesAllFloors = totalBoxesPerFloor * floorCount; // Total boxes across all floors
    const totalUnits = totalBoxesAllFloors * 2; // 2 units per box
    
    // Component area = total area of all boxes across all floors
    // Convert from meters to feet to match Building Analysis calculations
    const componentAreaPerFloor = allActiveBoxes.reduce((total, box) => {
      // Convert box dimensions from meters to feet (box.width and box.height are in meters)
      const boxWidthFt = box.width * 3.28084;
      const boxHeightFt = box.height * 3.28084;
      return total + (boxWidthFt * boxHeightFt);
    }, 0);
    const componentAreaAllFloors = componentAreaPerFloor * floorCount;
    
    // Calculate building footprint area (needed for both project total and land efficiency)
    // Convert from meters to feet to match other calculations
    const buildingFootprintArea = validBuildPaths.reduce((total, buildPath) => {
      const buildPathWidthFt = buildPath.width * 3.28084;
      const buildPathHeightFt = buildPath.height * 3.28084;
      return total + (buildPathWidthFt * buildPathHeightFt);
    }, 0);
    
    // Project Total calculation: Footprint + Component Area
    // Footprint = one floor of boxes (componentAreaPerFloor)
    // Component Area = all floors of boxes (componentAreaAllFloors)
    // Project Total = Footprint + Component Area
    const projectTotal = componentAreaPerFloor + componentAreaAllFloors;
    
    // Building area calculation (legacy - keeping for compatibility):
    // - If 1 floor and no podium: Building Area = Component Area (efficient design)
    // - If multiple floors or has podium: Building Area = footprint + podium area
    let totalBuildingArea;
    let podiumArea = 0;
    
    if (floorCount === 1 && !hasPodium) {
      // Simple case: Building Area = Component Area
      totalBuildingArea = componentAreaPerFloor;
    } else {
      // Complex case: Building footprint + podium + multi-floor component area
      
      if (hasPodium) {
        // Podium area = building footprint area (podium covers building base)
        podiumArea = buildingFootprintArea;
        // Total building area = podium + (component area across all floors)
        totalBuildingArea = podiumArea + componentAreaAllFloors;
      } else {
        // Multi-floor, no podium: Building area = component area across all floors
        totalBuildingArea = componentAreaAllFloors;
      }
    }
    
    // Calculate land efficiency (site-level)
    let landEfficiency = 0;
    let buildableAreaSqFt = 0;
    
    if (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) {
      // Calculate buildable area from coordinates
      const coords = internalPcaBuildingData.buildableAreaCoords;
      
      // Calculate bounding box in degrees
      let minLng = Infinity, maxLng = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;
      
      coords.forEach(coord => {
        minLng = Math.min(minLng, coord[0]);
        maxLng = Math.max(maxLng, coord[0]);
        minLat = Math.min(minLat, coord[1]);
        maxLat = Math.max(maxLat, coord[1]);
      });
      
      // Convert degrees to approximate meters (for small areas near equator)
      const avgLat = (minLat + maxLat) / 2;
      const metersPerDegreeLng = 111320 * Math.cos(avgLat * Math.PI / 180);
      const metersPerDegreeLat = 110540;
      
      const widthDegrees = maxLng - minLng;
      const heightDegrees = maxLat - minLat;
      const widthMeters = widthDegrees * metersPerDegreeLng;
      const heightMeters = heightDegrees * metersPerDegreeLat;
      
      // Approximate area as rectangle (good enough for rectangular parcels)
      const buildableAreaSqMeters = widthMeters * heightMeters;
      buildableAreaSqFt = buildableAreaSqMeters * 10.764; // Convert to sq ft
      
      // Sanity check: buildable area should be reasonable
      if (buildableAreaSqFt < 1000 || buildableAreaSqFt > 10000000) {
        console.warn('⚠️ Buildable area seems unreasonable:', buildableAreaSqFt, 'sq ft. Using fallback.');
        buildableAreaSqFt = Math.max(componentAreaPerFloor * 1.5, 10000);
      }
      
      // Land efficiency = building footprint / buildable area (footprint only, not total building area)
      const buildingFootprint = floorCount === 1 && !hasPodium ? componentAreaPerFloor : 
        validBuildPaths.reduce((total, buildPath) => total + (buildPath.width * buildPath.height), 0);
      landEfficiency = buildableAreaSqFt > 0 ? (buildingFootprint / buildableAreaSqFt) * 100 : 0;
      landEfficiency = Math.min(100, landEfficiency);
      
    } else if (buildableArea && buildableArea.width && buildableArea.height) {
      // Fallback to legacy buildable area calculation
      buildableAreaSqFt = buildableArea.width * buildableArea.height;
      const buildingFootprint = floorCount === 1 && !hasPodium ? componentAreaPerFloor : 
        validBuildPaths.reduce((total, buildPath) => total + (buildPath.width * buildPath.height), 0);
      landEfficiency = buildableAreaSqFt > 0 ? (buildingFootprint / buildableAreaSqFt) * 100 : 0;
      landEfficiency = Math.min(100, landEfficiency);
    }

    return {
      totalBuildings: validBuildPaths.length,
      totalUnits: totalUnits,
      totalBoxes: totalBoxesPerFloor,
      totalBoxesAllFloors: totalBoxesAllFloors, // This is what should be displayed as "Total Boxes"
      componentAreaPerFloor: Math.round(componentAreaPerFloor),
      componentAreaAllFloors: Math.round(componentAreaAllFloors), // Total box area across all floors
      podiumArea: Math.round(podiumArea), // Podium area = building footprint if hasPodium, else 0
      totalBuildingArea: Math.round(totalBuildingArea), // Updated calculation logic
      buildableAreaSqFt: Math.round(buildableAreaSqFt),
      landEfficiency: landEfficiency,
      floorCount: floorCount,
      hasPodium: hasPodium,
      projectTotal: Math.round(projectTotal)
    };
  };

  // Add comprehensive ModIQ optimization function
  const runComprehensiveOptimization = async () => {
    // Check for available data sources in order of preference
    let buildableAreaCoords = null;
    let orientationAngle = 0;
    
    // CRITICAL FIX: Always use the SAME buildable area calculation as the live PCA system
    // The live system calculates buildable area from parcelCoordinates + current setbacks
    // ModIQ should use the exact same calculation, not cached data
    
    if (parcelCoordinates && parcelCoordinates.length > 0) {
      // Use parcel coordinates with current setbacks (same as live system)
      // CRITICAL: Apply setbacks the same way as live PCA system
      
      // Get the actual setback distances used by the live PCA system
      const actualSetbackDistances = internalPcaBuildingData?.setbackDistances;
      
      if (actualSetbackDistances && actualSetbackDistances.length === parcelCoordinates.length - 1) {
        // Use the EXACT same setback array as the live PCA system
        console.log('✅ Using PCA setback distances for ModIQ optimization:', actualSetbackDistances);
        try {
          const buildableAreaWithSetbacks = getInsetPolygon(parcelCoordinates, actualSetbackDistances);
          if (buildableAreaWithSetbacks && buildableAreaWithSetbacks.length > 0) {
            buildableAreaCoords = buildableAreaWithSetbacks;
            console.log('✅ Using buildable area WITH PCA setbacks for ModIQ optimization (exact same calculation as live system)');
          } else {
            // Fallback to raw parcel if setback calculation fails
            buildableAreaCoords = parcelCoordinates;
            console.log('⚠️ PCA setback calculation failed, using raw parcel coordinates for ModIQ optimization');
          }
        } catch (error) {
          console.warn('⚠️ PCA setback calculation error:', error);
          buildableAreaCoords = parcelCoordinates;
          console.log('⚠️ Using raw parcel coordinates due to PCA setback error');
        }
      } else {
        // Fallback to manual setback values if PCA setbacks not available
        console.log('⚠️ PCA setbacks not available, falling back to manual setback values');
        const currentSetbacks = [setbackNorth, setbackEast, setbackSouth, setbackWest];
        try {
          const buildableAreaWithSetbacks = getInsetPolygon(parcelCoordinates, currentSetbacks);
          if (buildableAreaWithSetbacks && buildableAreaWithSetbacks.length > 0) {
            buildableAreaCoords = buildableAreaWithSetbacks;
            console.log('✅ Using buildable area WITH manual setbacks for ModIQ optimization (fallback method)');
          } else {
            // Fallback to raw parcel if setback calculation fails
            buildableAreaCoords = parcelCoordinates;
            console.log('⚠️ Manual setback calculation failed, using raw parcel coordinates for ModIQ optimization');
          }
        } catch (error) {
          console.warn('⚠️ Manual setback calculation error:', error);
          buildableAreaCoords = parcelCoordinates;
          console.log('⚠️ Using raw parcel coordinates due to manual setback error');
        }
      }
      orientationAngle = principalAngle || 0;
    }
    // Priority 2: PCA building data as fallback
    else if (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) {
      buildableAreaCoords = internalPcaBuildingData.buildableAreaCoords;
      orientationAngle = internalPcaBuildingData.orientationAngle || 0;
      console.log('✅ Using PCA buildable area coordinates for ModIQ optimization (fallback)');
    }
    // Priority 3: Legacy buildable area
    else if (buildableArea && (buildableArea.coordinates || (buildableArea.x1 !== undefined))) {
      // Use buildable area bounds that respect current setback settings
      if (buildableArea && buildableArea.coordinates) {
        buildableAreaCoords = buildableArea.coordinates;
      } else {
        // Create simple rectangular coordinates from buildable area with current setbacks
        buildableAreaCoords = [
          [buildableArea.x1, buildableArea.y1],
          [buildableArea.x2, buildableArea.y1],
          [buildableArea.x2, buildableArea.y2],
          [buildableArea.x1, buildableArea.y2],
          [buildableArea.x1, buildableArea.y1] // Close the polygon
        ];
      }
      console.log('✅ Using legacy buildable area coordinates for ModIQ optimization');
    }
    else {
      alert('Please draw a parcel or generate buildings first to enable ModIQ Pro optimization.\n\nModIQ Pro needs building layout data to analyze different configurations.');
      return;
    }

    // Validate that we have valid coordinates
    if (!buildableAreaCoords || !Array.isArray(buildableAreaCoords) || buildableAreaCoords.length < 3) {
      alert('Invalid parcel data detected. Please redraw the parcel or generate buildings first.');
      console.error('❌ Invalid buildable area coordinates:', buildableAreaCoords);
      return;
    }

    setIsComprehensiveOptimizing(true);
    setOptimizationProgress(null);
    setComprehensiveResults(null);

    // Add a small delay to ensure the UI updates before starting the heavy computation
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      console.log('🚀 Starting ModIQ Pro Optimization using PCA system...', {
        buildableAreaCoords: buildableAreaCoords.length,
        orientationAngle,
        boxWidth,
        boxLength,
        buildingPadding
      });
      
      // Use the SAME PCA system that manual controls use
      const results = await runModIQWithPCASystem(
        buildableAreaCoords,
        (progress) => {
          setOptimizationProgress(progress);
        }
      );

      setComprehensiveResults(results);
      console.log('✅ ModIQ Pro optimization complete!', results.summary);

      // **Automatically apply the optimal configuration**
      if (results.summary.winner) {
        console.log('🎯 Auto-applying optimal configuration to map...');
        applyOptimalConfiguration(results.summary.winner);
      }

      // Show results dialog after a brief delay (so user sees the map update first)
      // setTimeout(() => {
      //   setShowModIQDialog(true);
      // }, 1500);

    } catch (error) {
      console.error('❌ Optimization failed:', error);
      alert(`ModIQ Pro optimization failed: ${error.message}\n\nPlease try again or check the console for more details.`);
    } finally {
      setIsComprehensiveOptimizing(false);
      setOptimizationProgress(null);
    }
  };

  // NEW: ModIQ optimization using headless PCA system (no UI updates during analysis)
  const runModIQWithPCASystem = async (buildableAreaCoords, onProgress) => {
    const strategies = [
      { key: 'smart-hybrid', name: 'Auto-Select Best Fit' },
      { key: 'parallel-to-orientation', name: 'Parallel to Orientation' },
      { key: 'perpendicular-to-orientation', name: 'Perpendicular to Orientation' }
    ];

    // Use 1-degree precision for comprehensive analysis
    const angleStep = 1; // Test every 1 degree for precise results
    const totalAngles = 360 / angleStep;
    const totalEvaluations = totalAngles * strategies.length;
    
    let evaluationCount = 0;
    let validConfigurations = 0;
    let invalidConfigurations = 0;
    const allResults = [];
    let currentBest = null;
    const startTime = Date.now();

    // Get current settings for the analysis
    const currentBoxWidth = boxWidth;
    const currentBoxLength = boxLength;
    const currentBuildingPadding = buildingPadding;
    const minBoxesRequired = internalPcaBuildingData?.minBoxesPerBuilding || 3; // Default to 3
    const maxBoxesRequired = internalPcaBuildingData?.maxBoxesPerBuilding || 50; // Default to 50
    
    console.log(`🎯 Starting headless ModIQ optimization: ${totalEvaluations} configurations`);
    console.log(`📐 Using: ${currentBoxWidth}' × ${currentBoxLength}' boxes with ${currentBuildingPadding}' padding`);
    console.log(`🏗️ Building constraints: ${minBoxesRequired}-${maxBoxesRequired} boxes per building`);

    // Test each angle and strategy combination WITHOUT UI updates
    for (let angle = 0; angle < 360; angle += angleStep) {
      for (const strategy of strategies) {
        evaluationCount++;
        
        try {
          // Call generatePCALayout directly - using EXACT SAME PARAMETERS as live system
          const layoutResult = generatePCALayout(
            strategy.key,
            buildableAreaCoords,
            currentBoxWidth,
            currentBoxLength,
            currentBuildingPadding,
            angle,
            minBoxesRequired,
            maxBoxesRequired  // Use actual setting instead of hardcoded 30
          );

          if (layoutResult.buildPaths && layoutResult.boxes && layoutResult.buildPaths.length > 0 && layoutResult.boxes.length > 0) {
            // Validate building sizes
            const validBuildings = [];
            const validBoxes = [];
            let totalBoxCount = 0;
            let totalBuildingCount = 0;

            // Ensure arrays are safe
            const safeLayoutResultBuildPaths = Array.isArray(layoutResult.buildPaths) ? layoutResult.buildPaths : [];
            const safeLayoutResultBoxes = Array.isArray(layoutResult.boxes) ? layoutResult.boxes : [];

            for (const buildPath of safeLayoutResultBuildPaths) {
              const buildingBoxes = safeLayoutResultBoxes.filter(box => box.buildPathId === buildPath.id);
              
              if (buildingBoxes.length >= minBoxesRequired && buildingBoxes.length <= maxBoxesRequired) {
                validBuildings.push(buildPath);
                validBoxes.push(...buildingBoxes);
                totalBoxCount += buildingBoxes.length;
                totalBuildingCount++;
              }
            }

            if (validBuildings.length > 0 && totalBoxCount > 0) {
              // Calculate units based on current floor count
              const unitsTotal = totalBoxCount * 2 * floorCount;

              // Calculate areas (boxes already in correct units from generatePCALayout)
              const componentAreaSqFt = validBoxes.reduce((total, box) => {
                // Convert from meters to square feet
                const widthFt = box.width * 3.28084;
                const heightFt = box.height * 3.28084;
                return total + (widthFt * heightFt);
              }, 0);

              const result = {
                angle: angle,
                strategy: strategy.key,
                strategyName: strategy.name,
                buildPaths: validBuildings,
                boxes: validBoxes,
                metrics: {
                  boxCount: totalBoxCount,
                  buildingCount: totalBuildingCount,
                  unitsTotal: unitsTotal,
                  componentAreaSqFt: Math.round(componentAreaSqFt),
                  averageBoxesPerBuilding: Math.round(totalBoxCount / totalBuildingCount * 10) / 10
                },
                score: totalBoxCount // Use box count as primary optimization metric
              };

              allResults.push(result);
              validConfigurations++;

              // Update current best
              if (!currentBest || result.score > currentBest.score) {
                currentBest = result;
                console.log(`🏆 New best: ${result.score} boxes at ${angle}° with ${strategy.name}`);
              }
            } else {
              invalidConfigurations++;
              console.log(`❌ No valid buildings (all < ${minBoxesRequired} boxes) at ${angle}° with ${strategy.name}`);
            }
          } else {
            invalidConfigurations++;
            console.log(`❌ Failed to generate layout at ${angle}° with ${strategy.name}`);
          }

        } catch (error) {
          invalidConfigurations++;
          console.error(`❌ Error evaluating ${angle}° with ${strategy.name}:`, error);
        }

        // Report progress
        if (onProgress && evaluationCount % 10 === 0) {
          const progress = Math.round((evaluationCount / totalEvaluations) * 100);
          const elapsedMs = Date.now() - startTime;
          const estimatedTotalMs = (elapsedMs / evaluationCount) * totalEvaluations;
          const remainingMs = Math.max(0, estimatedTotalMs - elapsedMs);
          const estimatedTimeRemaining = Math.round(remainingMs / 1000);

          onProgress({
            progress,
            currentAngle: angle,
            currentStrategy: strategy.name,
            evaluationCount,
            totalEvaluations,
            validConfigurations,
            invalidConfigurations,
            estimatedTimeRemaining,
            currentBest: currentBest ? {
              boxCount: currentBest.metrics.boxCount,
              angle: currentBest.angle,
              strategy: currentBest.strategyName
            } : null
          });
        }

        // Small delay to prevent UI blocking
        if (evaluationCount % 50 === 0) {
          await new Promise(resolve => setTimeout(resolve, 1));
        }
      }
    }

    // Sort results by score (descending)
    allResults.sort((a, b) => b.score - a.score);
    const top10 = allResults.slice(0, 10);
    const winner = allResults[0];

    // Analyze strategy performance
    const strategyPerformance = strategies.map(strategy => {
      const strategyResults = allResults.filter(r => r.strategy === strategy.key);
      if (strategyResults.length === 0) {
        return {
          strategy: strategy.key,
          name: strategy.name,
          maxBoxCount: 0,
          averageBoxCount: 0,
          bestAngle: 0
        };
      }

      const maxResult = strategyResults[0]; // Already sorted by score
      const averageBoxCount = Math.round(
        strategyResults.reduce((sum, r) => sum + r.metrics.boxCount, 0) / strategyResults.length
      );

      return {
        strategy: strategy.key,
        name: strategy.name,
        maxBoxCount: maxResult.metrics.boxCount,
        averageBoxCount: averageBoxCount,
        bestAngle: maxResult.angle
      };
    });

    const totalTimeMs = Date.now() - startTime;

    console.log(`✅ Headless ModIQ optimization complete!`);
    console.log(`   Total time: ${(totalTimeMs / 1000).toFixed(1)}s`);
    console.log(`   Configurations tested: ${evaluationCount}`);
    console.log(`   Valid configurations: ${validConfigurations} (${((validConfigurations / evaluationCount) * 100).toFixed(1)}%)`);
    console.log(`   Invalid configurations: ${invalidConfigurations}`);
    console.log(`   Best result: ${winner?.metrics.boxCount} boxes at ${winner?.angle}° with ${winner?.strategyName}`);
    console.log(`   Performance: ${Math.round(evaluationCount / (totalTimeMs / 1000))} configurations/second`);

    return {
      results: allResults,
      summary: {
        winner: winner,
        top10: top10,
        totalConfigurations: evaluationCount,
        validConfigurations: validConfigurations,
        invalidConfigurations: invalidConfigurations,
        totalTimeMs: totalTimeMs,
        configurationsPerSecond: Math.round(evaluationCount / (totalTimeMs / 1000))
      },
      strategyPerformance: strategyPerformance
    };
  };

  // Helper function to test a specific PCA configuration (LEGACY - now unused)
  const testPCAConfiguration = async (angle, strategy) => {
    console.warn('⚠️ testPCAConfiguration is deprecated - use generatePCALayout directly for headless operation');
    
    // Apply the configuration by setting the state variables
    setPcaLayoutStrategy(strategy);
    setOrientationOverride(angle);
    
    // Wait for the PCA system to process the change
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Return the current state after the change
    if (internalPcaBuildingData && internalPcaBuildingData.buildPaths && internalPcaBuildingData.boxes) {
      console.log(`�� Using PCA data: ${internalPcaBuildingData.boxes.length} boxes, ${internalPcaBuildingData.buildPaths.length} buildings`);
      return {
        buildPaths: JSON.parse(JSON.stringify(internalPcaBuildingData.buildPaths)),
        boxes: JSON.parse(JSON.stringify(internalPcaBuildingData.boxes)),
        buildableAreaCoords: internalPcaBuildingData.buildableAreaCoords
      };
    } else if (layoutState.buildPaths && layoutState.boxes) {
      console.log(`📊 Using layout state: ${layoutState.boxes.length} boxes, ${layoutState.buildPaths.length} buildings`);
      return {
        buildPaths: JSON.parse(JSON.stringify(layoutState.buildPaths)),
        boxes: JSON.parse(JSON.stringify(layoutState.boxes))
      };
    } else {
      console.log(`📊 No valid data available for ${angle}° with ${strategy}`);
      return { buildPaths: [], boxes: [] };
    }
  };

  // Add ModIQ analysis function (legacy - for backwards compatibility)
  const analyzeAllConfigurations = () => {
    setIsAnalyzing(true);
    
    const strategies = ['y-parallel', 'y-perpendicular', 'x-parallel', 'x-perpendicular'];
    const results = {
      bestEfficiency: {
        score: 0,
        strategy: null,
        buildingConfigs: {}
      },
      bestLivability: {
        score: 0,
        strategy: null,
        buildingConfigs: {}
      },
      bestBalanced: {
        score: 0,
        strategy: null,
        buildingConfigs: {}
      },
      allResults: {}
    };

    // Analyze each strategy
    strategies.forEach(strategy => {
      // Generate layout for this strategy
      const { newBuildPaths, newBoxes } = generateLayout(strategy);
      
      const strategyResults = {
        buildPaths: newBuildPaths,
        boxes: newBoxes,
        buildingOptimizations: {},
        metrics: {
          efficiency: 0,
          livability: 0,
          balanced: 0
        }
      };

      // Process each building
      newBuildPaths.forEach(buildPath => {
        const buildingId = buildPath.id;
        const buildingBoxes = newBoxes.filter(box => box.buildPathId === buildingId);
        
        // Run optimizations
        // Use PCA optimizer if we have PCA building data, otherwise use legacy optimizer
        const usesPCAData = internalPcaBuildingData && internalPcaBuildingData.buildPaths.length > 0;
        
        const efficiencyResult = usesPCAData
          ? pcaBoxOptimizer.shrinkBoxesForEfficiency(
              buildingBoxes, 
              [buildPath], 
              parcelCoordinates, // Use parcel coordinates for ModIQ analysis
              constraints
            )
          : boxOptimizer.shrinkBoxesForEfficiency(
              buildingBoxes, 
              [buildPath], 
              buildableArea, 
              constraints
            );
        const efficiencyMetrics = usesPCAData
          ? pcaBoxOptimizer.calculateOptimizationMetrics(
              efficiencyResult.optimizedBoxes, 
              buildingBoxes, 
              parcelWidth, 
              parcelLength, 
              buildableArea,
              buildPath
            )
          : boxOptimizer.calculateOptimizationMetrics(
              efficiencyResult.optimizedBoxes, 
              buildingBoxes, 
              parcelWidth, 
              parcelLength, 
              buildableArea,
              buildPath
            );
        
        const livabilityResult = usesPCAData
          ? pcaBoxOptimizer.expandBoxesForLivability(
              buildingBoxes, 
              [buildPath], 
              parcelCoordinates, // Use parcel coordinates for ModIQ analysis
              constraints
            )
          : boxOptimizer.expandBoxesForLivability(
              buildingBoxes, 
              [buildPath], 
              buildableArea, 
              constraints
            );
        const livabilityMetrics = usesPCAData
          ? pcaBoxOptimizer.calculateOptimizationMetrics(
              livabilityResult.optimizedBoxes, 
              buildingBoxes, 
              parcelWidth, 
              parcelLength, 
              buildableArea,
              buildPath
            )
          : boxOptimizer.calculateOptimizationMetrics(
              livabilityResult.optimizedBoxes, 
              buildingBoxes, 
              parcelWidth, 
              parcelLength, 
              buildableArea,
              buildPath
            );
        
        strategyResults.buildingOptimizations[buildingId] = {
          efficiency: {
            optimizedBoxes: efficiencyResult.optimizedBoxes,
            optimizedDimensions: efficiencyResult.optimizedDimensions,
            layoutAnalysis: efficiencyResult.layoutAnalysis,
            metrics: efficiencyMetrics
          },
          livability: {
            optimizedBoxes: livabilityResult.optimizedBoxes,
            optimizedDimensions: livabilityResult.optimizedDimensions,
            layoutAnalysis: livabilityResult.layoutAnalysis,
            metrics: livabilityMetrics
          }
        };
      });

      // Calculate overall strategy metrics
      const buildingCount = newBuildPaths.length;
      let totalEfficiency = 0;
      let totalLivability = 0;

      Object.values(strategyResults.buildingOptimizations).forEach(building => {
        totalEfficiency += building.efficiency.metrics.efficiency || 0;
        totalLivability += building.livability.metrics.livabilityIndex || 0;
      });

      strategyResults.metrics = {
        efficiency: totalEfficiency / buildingCount,
        livability: totalLivability / buildingCount,
        balanced: (totalEfficiency + totalLivability) / (2 * buildingCount)
      };

      // Update best results
      if (strategyResults.metrics.efficiency > results.bestEfficiency.score) {
        results.bestEfficiency = {
          score: strategyResults.metrics.efficiency,
          strategy: strategy,
          buildingConfigs: Object.fromEntries(
            Object.entries(strategyResults.buildingOptimizations).map(([id, config]) => [id, 'efficiency'])
          )
        };
      }

      if (strategyResults.metrics.livability > results.bestLivability.score) {
        results.bestLivability = {
          score: strategyResults.metrics.livability,
          strategy: strategy,
          buildingConfigs: Object.fromEntries(
            Object.entries(strategyResults.buildingOptimizations).map(([id, config]) => [id, 'livability'])
          )
        };
      }

      if (strategyResults.metrics.balanced > results.bestBalanced.score) {
        results.bestBalanced = {
          score: strategyResults.metrics.balanced,
          strategy: strategy,
          buildingConfigs: Object.fromEntries(
            Object.entries(strategyResults.buildingOptimizations).map(([id, config]) => [id, 'efficiency'])
          )
        };
      }

      results.allResults[strategy] = strategyResults;
    });

    setModIQResults(results);
    setIsAnalyzing(false);
    setShowModIQDialog(true);
  };

  // Add function to apply ModIQ configuration
  const applyModIQConfiguration = useCallback((config) => {
    // Set the layout strategy
    setCurrentLayout(config.strategy);
    
    // Set the optimizations for each building
    dispatch({
      type: ACTIONS.UPDATE_SELECTED_OPTIMIZATIONS,
      payload: config.buildingConfigs
    });
    
    setShowModIQDialog(false);
  }, []);

  const BuildingMetricsCard = ({ building, buildPath }) => {
    const metrics = calculateBuildingMetrics(building.boxes, buildPath);
    
    return (
        <div className="building-metrics-card">
            <h3>Building {building.id}</h3>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Efficiency</label>
                    <span>{metrics.efficiency.toFixed(1)}%</span>
                </div>
                <div className="metric">
                    <label>Livability</label>
                    <span>{metrics.livability.toFixed(1)}%</span>
                </div>
                <div className="metric">
                    <label>Cost Factor</label>
                    <span>{metrics.costMultiplier.toFixed(2)}x</span>
                </div>
                <div className="metric">
                    <label>Units</label>
                    <span>{metrics.boxCount}</span>
                </div>
            </div>
        </div>
    );
};

const ProjectMetricsCard = ({ buildings, buildPaths }) => {
    // Calculate metrics for each building
    const buildingMetrics = {};
    buildings.forEach(building => {
        const buildPath = buildPaths[building.id];
        buildingMetrics[building.id] = calculateBuildingMetrics(building.boxes, buildPath);
    });

    // Calculate project-level metrics
    const projectMetrics = calculateProjectMetrics(buildingMetrics);

    return (
        <div className="project-metrics-card">
            <h3>Project Metrics</h3>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Overall Efficiency</label>
                    <span>{projectMetrics.efficiency.toFixed(1)}%</span>
                </div>
                <div className="metric">
                    <label>Average Livability</label>
                    <span>{projectMetrics.livability.toFixed(1)}%</span>
                </div>
                <div className="metric">
                    <label>Average Cost</label>
                    <span>{projectMetrics.costMultiplier.toFixed(2)}x</span>
                </div>
                <div className="metric">
                    <label>Total Units</label>
                    <span>{projectMetrics.totalBoxCount}</span>
                </div>
            </div>
        </div>
    );
};

  // Add setback dialog component
  const SetbackDialog = () => {
    const handleSetbackChange = (e, setter) => {
      setter(e.target.value);
    };

    const dialog = (
      <div style={{
        position: 'fixed',
        top: '80px',
        right: '320px',
        width: "800px",
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 160px)', // Reduced height to ensure it fits
        border: '1px solid #E5E7EB'
      }}>
        {/* Header - Fixed */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #E5E7EB',
          flexShrink: 0
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            margin: 0 
          }}>Setback Settings</h3>
        </div>

        {/* Scrollable Content */}
        <div style={{
          padding: '16px',
          overflowY: 'auto',
          flexGrow: 1
        }}>
          {/* Set All Setbacks Section */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '12px'
            }}>
              <input
                type="checkbox"
                id="setAllSetbacks"
                checked={setAllSetbacks}
                onChange={(e) => {
                  setSetAllSetbacks(e.target.checked);
                  if (e.target.checked) {
                    const value = setbackNorth.toString();
                    setSetbackValue(value);
                    updateAllSetbacks(Number(value) || 0);
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
              <label htmlFor="setAllSetbacks" style={{ fontSize: '14px', cursor: 'pointer' }}>
                Set all setbacks to same value
              </label>
            </div>
            
            {setAllSetbacks && (
              <div style={{ 
                padding: '12px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px'
              }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  Value for all setbacks (ft)
                </label>
                <input 
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={setbackValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSetbackValue(value);
                    updateAllSetbacks(Number(value) || 0);
                  }}
                  style={{ 
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
            )}
          </div>
            
          {/* Individual Setback Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>North (ft)</label>
              <input 
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={setbackNorth}
                onChange={(e) => handleSetbackChange(e, setSetbackNorth)}
                disabled={setAllSetbacks}
                style={{ 
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: setAllSetbacks ? '#f3f4f6' : 'white'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>East (ft)</label>
              <input 
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={setbackEast}
                onChange={(e) => handleSetbackChange(e, setSetbackEast)}
                disabled={setAllSetbacks}
                style={{ 
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: setAllSetbacks ? '#f3f4f6' : 'white'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>South (ft)</label>
              <input 
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={setbackSouth}
                onChange={(e) => handleSetbackChange(e, setSetbackSouth)}
                disabled={setAllSetbacks}
                style={{ 
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: setAllSetbacks ? '#f3f4f6' : 'white'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>West (ft)</label>
              <input 
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={setbackWest}
                onChange={(e) => handleSetbackChange(e, setSetbackWest)}
                disabled={setAllSetbacks}
                style={{ 
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: setAllSetbacks ? '#f3f4f6' : 'white'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );

    return createPortal(dialog, document.body);
  };

  // Add button to toggle setback dialog
  const SetbackButton = () => (
    <button
      style={{
        padding: '8px 16px',
        backgroundColor: showSetbackDialog ? '#22C55E' : '#6B7280',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
      onClick={() => setShowSetbackDialog(!showSetbackDialog)}
    >
      {showSetbackDialog ? 'Hide Setbacks' : 'Show Setbacks'}
    </button>
  );

  // After layoutState is defined, add:
  const optimizedBoxes = useMemo(() => updateDisplayedBoxes(
    layoutState.boxes,
    layoutState.buildPaths,
    layoutState.buildingOptimizations,
    layoutState.selectedOptimizations
  ), [
    layoutState.boxes,
    layoutState.buildPaths,
    layoutState.buildingOptimizations,
    layoutState.selectedOptimizations
  ]);

  const livableSpaces = useMemo(() => {
    const activeBoxes = Object.values(layoutState.selectedOptimizations).every(o => o === 'none') 
      ? layoutState.boxes 
      : optimizedBoxes;
    if (activeBoxes.length === 0) return [];
    const spaces = [];
    activeBoxes.forEach(box => {
      const buildingId = box.buildPathId;
      const towerId = box.id;
      const floorId = 1;
      const leftSpaceId = `B${buildingId}-T${towerId}-F${floorId}-L`;
      const rightSpaceId = `B${buildingId}-T${towerId}-F${floorId}-R`;
      
      // Convert box dimensions from meters to feet (box.width and box.height are in meters)
      const boxWidthFt = box.width * 3.28084;
      const boxLengthFt = box.height * 3.28084;
      const boxSqFt = boxWidthFt * boxLengthFt;
      
      // New calculations per user requirements:
      // Sq Ft = (box square footage - 5) / 2
      // Width = box width
      // Length = (box length - 5) / 2
      const unitSqFt = Math.round(((boxSqFt - 5) / 2) * 10) / 10;
      const unitWidth = Math.round(boxWidthFt * 10) / 10;
      const unitLength = Math.round(((boxLengthFt - 5) / 2) * 10) / 10;
      
      let leftPosition, rightPosition;
      if (box.orientation === 'horizontal') {
        leftPosition = {
          x: box.x,
          y: box.y,
          width: box.width * 0.4,
          height: box.height
        };
        rightPosition = {
          x: box.x + box.width * 0.6,
          y: box.y,
          width: box.width * 0.4,
          height: box.height
        };
      } else {
        leftPosition = {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height * 0.4
        };
        rightPosition = {
          x: box.x,
          y: box.y + box.height * 0.6,
          width: box.width,
          height: box.height * 0.4
        };
      }
      spaces.push({
        id: leftSpaceId,
        buildingId,
        towerId,
        floorId,
        side: 'L',
        boxId: box.id,
        position: leftPosition,
        squareFeet: unitSqFt,
        width: unitWidth,
        length: unitLength
      });
      spaces.push({
        id: rightSpaceId,
        buildingId,
        towerId,
        floorId,
        side: 'R',
        boxId: box.id,
        position: rightPosition,
        squareFeet: unitSqFt,
        width: unitWidth,
        length: unitLength
      });
    });
    return spaces;
  }, [layoutState.boxes, optimizedBoxes, layoutState.selectedOptimizations]);

  useEffect(() => {
    if (!layoutState.selectedBuildingId && layoutState.buildPaths.length > 0) {
      dispatch({
        type: ACTIONS.SET_SELECTED_BUILDING,
        payload: layoutState.buildPaths[0].id
      });
    }
  }, [layoutState.selectedBuildingId, layoutState.buildPaths]);
  const [showMapboxPCA, setShowMapboxPCA] = useState(false);

  // Navigation state
  const [showUnitsPage, setShowUnitsPage] = useState(false);
  
  // Card expansion state
  const [expandedCards, setExpandedCards] = useState({
    standard: false,
    efficiency: false,
    livability: false
  });
  
  // Orientation angle state
  const [orientationOverride, setOrientationOverride] = useState(null);
  const [principalAngle, setPrincipalAngle] = useState(0);
  
  // Debug orientation state changes
  useEffect(() => {
    console.log('🧭 ORIENTATION STATE DEBUG:', {
      orientationOverride: orientationOverride,
      principalAngle: principalAngle,
      currentDisplayValue: orientationOverride ?? principalAngle ?? 0,
      orientationOverrideType: typeof orientationOverride,
      principalAngleType: typeof principalAngle,
      isOrientationOverrideNull: orientationOverride === null,
      isOrientationOverrideUndefined: orientationOverride === undefined
    });
  }, [orientationOverride, principalAngle]);
  
  // Settings panel state
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  
  // Function to toggle card expansion
  const toggleCard = (cardType) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardType]: !prev[cardType]
    }));
  };

  // Unit Mix state management
  const [buildingSettings, setBuildingSettings] = useState({});
  
  // Initialize unit mix settings for buildings
  useEffect(() => {
    // Ensure buildPaths is an array
    const safeBuildPaths = Array.isArray(layoutState.buildPaths) ? layoutState.buildPaths : [];
    if (safeBuildPaths.length > 0) {
      setBuildingSettings(prev => {
        const next = { ...prev };
        safeBuildPaths.forEach(buildPath => {
          if (!next[buildPath.id]) {
            next[buildPath.id] = getDefaultBuildingState();
          }
        });
        // Remove settings for buildings that no longer exist
        Object.keys(next).forEach(id => {
          if (!safeBuildPaths.find(bp => bp.id === Number(id))) {
            delete next[id];
          }
        });
        return next;
      });
    }
  }, [layoutState.buildPaths]);

  // Unit Mix handlers - Updated with slot-aware snapping
  const handleUnitTypeSlider = useCallback((buildingId, unitType, value) => {
    const currentBoxes = (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === buildingId);
    const totalBoxes = currentBoxes.length;
    const egressSlots = calculateEgressSlots(totalBoxes);
    const totalSlots = totalBoxes * 2 * floorCount;
    const egressSlotsTotal = egressSlots.length * floorCount;
    const availableSlots = totalSlots - egressSlotsTotal;
    
    console.log(`🎯 Slot-aware slider adjustment for ${unitType}:`, {
      totalSlots,
      egressSlotsTotal,
      availableSlots,
      requestedPercentage: value
    });

    // Define slot requirements for each unit type
    const SLOT_REQUIREMENTS = {
      studio: 1,
      oneBR: 2,
      twoBR: 3,
      studioPlus: 1, // Part of 3-slot pair with 1BR+
      oneBRPlus: 2   // Part of 3-slot pair with Studio+
    };

    // Convert percentage to actual units
    const totalUnits = availableSlots; // Available slots = max possible units
    const requestedUnits = Math.round(totalUnits * (value / 100));
    const slotsPerUnit = SLOT_REQUIREMENTS[unitType];
    
    // Calculate max units possible for this type given slot constraints
    const maxUnitsForType = Math.floor(availableSlots / slotsPerUnit);
    
    // Snap to valid unit count
    const snappedUnits = Math.min(requestedUnits, maxUnitsForType);
    const snappedSlotsUsed = snappedUnits * slotsPerUnit;
    
    // Calculate what percentage this actually represents
    const snappedPercentage = totalUnits > 0 ? Math.round((snappedUnits / totalUnits) * 100) : 0;
    
    console.log(`📊 Slot calculation for ${unitType}:`, {
      requestedUnits,
      slotsPerUnit,
      maxUnitsForType,
      snappedUnits,
      snappedSlotsUsed,
      snappedPercentage: `${snappedPercentage}%`
    });

    // Update building settings with snapped value
    setBuildingSettings(prev => {
      const settings = { ...prev[buildingId] };
      let updated = { ...settings.unitTypeDistribution, [unitType]: snappedPercentage };
      
      // Handle paired units: Studio+ and 1BR+ must always have the same value
      if (unitType === 'studioPlus') {
        updated.oneBRPlus = snappedPercentage;
      } else if (unitType === 'oneBRPlus') {
        updated.studioPlus = snappedPercentage;
      }
      
      // Validate total slot usage and adjust if necessary
      let totalSlotsUsed = 0;
      const unitCounts = {};
      
      UNIT_TYPES.forEach(type => {
        const percentage = updated[type.key] || 0;
        const units = Math.round(totalUnits * (percentage / 100));
        const slots = units * SLOT_REQUIREMENTS[type.key];
        unitCounts[type.key] = units;
        totalSlotsUsed += slots;
      });
      
      console.log(`🔍 Total slot validation:`, {
        totalSlotsUsed,
        availableSlots,
        isValid: totalSlotsUsed <= availableSlots,
        unitCounts
      });
      
      // If we're over the slot limit, scale everything down proportionally
      if (totalSlotsUsed > availableSlots) {
        console.log(`⚠️ Over slot limit! Scaling down from ${totalSlotsUsed} to ${availableSlots} slots`);
        
        const scaleFactor = availableSlots / totalSlotsUsed;
        const scaledDistribution = {};
        
        UNIT_TYPES.forEach(type => {
          const scaledUnits = Math.floor(unitCounts[type.key] * scaleFactor);
          const scaledPercentage = totalUnits > 0 ? Math.round((scaledUnits / totalUnits) * 100) : 0;
          scaledDistribution[type.key] = scaledPercentage;
        });
        
        updated = scaledDistribution;
        console.log(`✅ Scaled distribution:`, scaledDistribution);
      }
      
      settings.unitTypeDistribution = normalizeSliders(updated);
      return { ...prev, [buildingId]: settings };
    });
    
    // Update unitQuantities state to match the snapped sliders
    const newUnitQuantities = {};
    UNIT_TYPES.forEach(type => {
      const percentage = unitType === type.key ? snappedPercentage : 
                        (buildingSettings[buildingId]?.unitTypeDistribution[type.key] || 0);
      const unitsOfThisType = Math.round(totalUnits * (percentage / 100));
      newUnitQuantities[type.key] = unitsOfThisType;
    });
    
    setUnitQuantities(newUnitQuantities);
    console.log(`✅ Updated unit quantities:`, newUnitQuantities);
    
  }, [buildingSettings, layoutState.boxes, floorCount]);

  const handleBusinessModelSlider = useCallback((buildingId, model, value) => {
    setBuildingSettings(prev => {
      const settings = { ...prev[buildingId] };
      const updated = { ...settings.businessModelDistribution, [model]: value };
      settings.businessModelDistribution = normalizeSliders(updated);
      return { ...prev, [buildingId]: settings };
    });
  }, []);

  // Helper function to distribute units proportionally ensuring sum equals total
  const distributeUnitsProportionally = useCallback((totalUnits, distribution) => {
    const keys = Object.keys(distribution);
    const percentages = keys.map(key => distribution[key] || 0);
    const totalPercentage = percentages.reduce((a, b) => a + b, 0);
    
    if (totalPercentage === 0) {
      const result = {};
      keys.forEach(key => result[key] = 0);
      return result;
    }
    
    // Calculate using floor for all except the largest, then give remainder to largest
    const result = {};
    let remaining = totalUnits;
    
    // Sort keys by percentage descending to give remainder to largest category
    const sortedKeys = [...keys].sort((a, b) => (distribution[b] || 0) - (distribution[a] || 0));
    
    // Assign floor values to all but the first (largest)
    for (let i = 1; i < sortedKeys.length; i++) {
      const key = sortedKeys[i];
      const percentage = distribution[key] || 0;
      const units = Math.floor(totalUnits * (percentage / totalPercentage));
      result[key] = units;
      remaining -= units;
    }
    
    // Give remainder to the largest category
    result[sortedKeys[0]] = remaining;
    
    return result;
  }, []);

  // Synchronize unitQuantities with slider changes for selected building
  // This calculates actual dwelling unit counts based on available slots and slot requirements
  useEffect(() => {
    if (layoutState.selectedBuildingId && buildingSettings[layoutState.selectedBuildingId]) {
      const currentSettings = buildingSettings[layoutState.selectedBuildingId];
      const currentBoxes = (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === layoutState.selectedBuildingId);
      
      // Calculate available slots (total slots minus egress)
      const totalBoxes = currentBoxes.length;
      const egressPositions = calculateEgressSlots(totalBoxes);
      const totalSlots = totalBoxes * 2 * floorCount;
      const egressSlotsTotal = egressPositions.length * floorCount;
      const availableSlots = totalSlots - egressSlotsTotal;
      
      // Slot requirements for each unit type
      const SLOT_REQUIREMENTS = {
        studio: 1,
        oneBR: 2,
        twoBR: 3,
        studioPlus: 1, // Part of 3-slot pair with 1BR+
        oneBRPlus: 2   // Part of 3-slot pair with Studio+
      };
      
      // Calculate actual dwelling unit counts based on slot allocation
      const calculatedQuantities = {};
      UNIT_TYPES.forEach(type => {
        const percentage = currentSettings.unitTypeDistribution[type.key] || 0;
        const slotsForType = Math.floor(availableSlots * (percentage / 100));
        const slotsPerUnit = SLOT_REQUIREMENTS[type.key] || 1;
        const unitCount = Math.floor(slotsForType / slotsPerUnit);
        calculatedQuantities[type.key] = unitCount;
      });
      
      setUnitQuantities(calculatedQuantities);
    }
  }, [layoutState.selectedBuildingId, buildingSettings, layoutState.boxes, floorCount]);

  // Add missing unitQuantities state
  const [unitQuantities, setUnitQuantities] = useState({
    studio: 25,
    oneBR: 25,
    twoBR: 25,
    studioPlus: 25
  });

  // DEBUG: Track state changes after ModIQ application
  useEffect(() => {
    console.log('🔄 STATE CHANGE DETECTED:');
    console.log('   layoutState changed:', {
      buildPaths: layoutState.buildPaths?.length || 0,
      boxes: layoutState.boxes?.length || 0,
      timestamp: Date.now()
    });
  }, [layoutState.buildPaths, layoutState.boxes]);

  useEffect(() => {
    console.log('🔄 PCA DATA CHANGE DETECTED:');
    console.log('   internalPcaBuildingData changed:', {
      buildPaths: internalPcaBuildingData?.buildPaths?.length || 0,
      boxes: internalPcaBuildingData?.boxes?.length || 0,
      angle: internalPcaBuildingData?.orientationAngle,
      strategy: internalPcaBuildingData?.strategy,
      timestamp: Date.now()
    });
  }, [internalPcaBuildingData]);

  // Add missing PCA-related state variables
  const [layoutStrategy, setLayoutStrategy] = useState('smart-hybrid');
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [selectedOptimizations, setSelectedOptimizations] = useState({});
  const [buildingOptimizations, setBuildingOptimizations] = useState({});

  // Add missing PCA-related handler functions
  const handlePCAParcelSelected = useCallback((coords) => {
    setParcelCoordinates(coords);
    if (coords) {
      const bounds = calculateParcelBounds(coords);
      setParcelWidth(bounds.width);
      setParcelLength(bounds.height);
    }
  }, []);

  const handlePCABuildingDataUpdate = useCallback((pcaData) => {
    console.log('🏗️ PCA Building Data Update received:', pcaData);
    // Use the existing handleBuildingDataUpdate function
    handleBuildingDataUpdate(pcaData);
  }, [handleBuildingDataUpdate]);

  const handlePrincipalAngleUpdate = useCallback((angle) => {
    setPrincipalAngle(angle);
  }, []);

  // Handle setback distance updates from MapBox PCA component
  const handleSetbackDistancesChange = useCallback((distances) => {
    console.log('📏 Received edge setback distances from MapBox:', distances);
    console.log('📏 Previous edge setback distances:', edgeSetbackDistances);
    setEdgeSetbackDistances(distances);
    console.log('📏 Updated edge setback distances state');
  }, [edgeSetbackDistances]);

  // Debug edge setback distances changes
  useEffect(() => {
    console.log('🔍 edgeSetbackDistances state changed:', edgeSetbackDistances);
    console.log('🔍 edgeSetbackDistances length:', edgeSetbackDistances?.length);
    console.log('🔍 edgeSetbackDistances values:', edgeSetbackDistances);
  }, [edgeSetbackDistances]);

  // Debug edge setback distances changes
  useEffect(() => {
    console.log('🔍 SimpleModIQVisualizer edgeSetbackDistances state changed:', {
      edgeSetbackDistances: edgeSetbackDistances,
      length: edgeSetbackDistances?.length || 0,
      values: edgeSetbackDistances
    });
  }, [edgeSetbackDistances]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
      {/* Sticky Header - Full Width - Always Visible */}
      <div id="sticky-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        gap: '20px',
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 1000,
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Left side: Logo and Navigation buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }}>
          <img src="/images/modiq-logo-sm.png" alt="ModIQ Logo" style={{ height: '77px' }} />
          
          {/* Navigation Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{ 
                padding: '8px 16px',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              onClick={() => {
                const element = document.getElementById('parcel-analysis');
                const headerHeight = document.getElementById('sticky-header')?.offsetHeight || 0;
                if (element) {
                  const elementPosition = element.offsetTop - headerHeight - 20; // 20px extra padding
                  window.scrollTo({ top: elementPosition, behavior: 'smooth' });
                }
              }}
            >
              Parcel
            </button>
            <button
              style={{ 
                padding: '8px 16px',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              onClick={() => {
                const element = document.getElementById('unit-analysis');
                const headerHeight = document.getElementById('sticky-header')?.offsetHeight || 0;
                if (element) {
                  const elementPosition = element.offsetTop - headerHeight - 20; // 20px extra padding
                  window.scrollTo({ top: elementPosition, behavior: 'smooth' });
                }
              }}
            >
              Unit
            </button>
          </div>
        </div>

        {/* Right side: Project Overview and Project Cost Panels - Always show containers */}
        <div style={{ display: 'flex', gap: '16px' }}>
          {/* Project Overview Panel */}
          <div style={{
            width: "680px", // Reduced from 900px to make room for Cost container
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            padding: '12px 16px', // Reduced vertical padding from 16px to 12px
            flexShrink: 0
          }}>
            {layoutState.buildPaths && layoutState.buildPaths.length > 0 ? (
              (() => {
                const metrics = calculateProjectMetrics();
                return (
                  <>
                    <h3 style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 8px 0', // Reduced from 12px to 8px
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3h18v18h-18zM9 9h6v6h-6z"/>
                      </svg>
                      Project Overview
                    </h3>

                    {/* All Metrics in One Row */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(8, 1fr)`, // 8 columns for project metrics including Footprint
                      gap: '10px',
                      fontSize: '0.75rem'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.totalBuildings}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>
                          Buildings
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.floorCount}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>
                          Floors
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.hasPodium ? 'Yes' : 'No'}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>
                          Podium
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.totalUnits}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>
                          Slots
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.totalBoxesAllFloors}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>
                          Total Boxes
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.componentAreaPerFloor.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2', whiteSpace: 'nowrap' }}>
                          Footprint (sq ft)
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.projectTotal.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2', whiteSpace: 'nowrap' }}>
                          Project Total (sq ft)
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                          {metrics.componentAreaAllFloors.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2', whiteSpace: 'nowrap' }}>
                          Component Area (sq ft)
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()
            ) : (
              // Empty state - show placeholder content
              <>
                <h3 style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#6b7280',
                  margin: '0 0 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3h18v18h-18zM9 9h6v6h-6z"/>
                  </svg>
                  Project Overview
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: "repeat(8, 1fr)",
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  {['Buildings', 'Floors', 'Podium', 'Slots', 'Total Boxes', 'Footprint (sq ft)', 'Project Total (sq ft)', 'Component Area (sq ft)'].map((label, index) => (
                    <div key={index} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: '700', color: '#d1d5db' }}>
                        —
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#9ca3af', lineHeight: '1.2', whiteSpace: 'nowrap' }}>
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Project Cost Panel */}
          <div style={{
            width: "340px", // Increased from 320px to accommodate the smaller Overview panel
            backgroundColor: '#f0fdf4',
            borderRadius: '6px',
            border: '1px solid #bbf7d0',
            padding: '12px 16px', // Reduced vertical padding from 16px to 12px
            flexShrink: 0
          }}>
            {layoutState.buildPaths && layoutState.buildPaths.length > 0 ? (
              (() => {
                const metrics = calculateProjectMetrics();
                const componentArea = metrics.componentAreaAllFloors;
                
                // Get cost settings from settings manager
                const componentsMin = settingsManager.getSetting('advanced.components.min') || 135;
                const componentsMax = settingsManager.getSetting('advanced.components.max') || 185;
                const onSiteMin = settingsManager.getSetting('advanced.onSiteConstruction.min') || 100;
                const onSiteMax = settingsManager.getSetting('advanced.onSiteConstruction.max') || 150;
                
                // Calculate cost ranges
                const componentsCostMin = componentArea * componentsMin;
                const componentsCostMax = componentArea * componentsMax;
                const onSiteCostMin = componentArea * onSiteMin;
                const onSiteCostMax = componentArea * onSiteMax;
                
                // Helper function to format large numbers
                const formatCurrency = (amount) => {
                  if (amount >= 1000000) {
                    return `$${(amount / 1000000).toFixed(1)}M`;
                  } else if (amount >= 1000) {
                    return `$${(amount / 1000).toFixed(0)}K`;
                  } else {
                    return `$${amount.toLocaleString()}`;
                  }
                };
                
                return (
                  <>
                    <h3 style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 8px 0', // Reduced from 12px to 8px
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                      Project Cost
                    </h3>
                    
                    {/* Cost Ranges - Side by Side */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '16px' // Reduced from 24px to 16px
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', whiteSpace: 'nowrap' }}> {/* Reduced font size and added nowrap */}
                          Components
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937', lineHeight: '1.2' }}> {/* Reduced font size and line height */}
                          {formatCurrency(componentsCostMin)} - {formatCurrency(componentsCostMax)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                          On-Site Construction
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937', lineHeight: '1.2' }}>
                          {formatCurrency(onSiteCostMin)} - {formatCurrency(onSiteCostMax)}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()
            ) : (
              // Empty state - show placeholder content
              <>
                <h3 style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#6b7280',
                  margin: '0 0 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                  Project Cost
                </h3>
                
                {/* Cost Ranges - Side by Side - Placeholder */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                      Components
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#d1d5db', lineHeight: '1.2' }}>
                      — — —
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                      On-Site Construction
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#d1d5db', lineHeight: '1.2' }}>
                      — — —
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        position: 'relative',
        marginTop: '0' // Always 0 since header is always visible
      }}>
        {/* Persistent Project Metrics Panel - Top Right */}
        {layoutState.buildPaths && layoutState.buildPaths.length > 0 && (
          <div style={{
            display: "none",
            right: '16px',
            width: "800px",
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e2e8f0',
            zIndex: 10,
            padding: '16px'
          }}>
            {(() => {
              const metrics = calculateProjectMetrics();
              return (
                <>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 12px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3h18v18h-18zM9 9h6v6h-6z"/>
                    </svg>
                    Project Overview
                  </h3>
                  
                  {/* All Metrics in One Row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(8, 1fr)`, // 8 columns for project metrics including Footprint
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#059669' }}>
                        {metrics.totalBuildings}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Buildings
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#6366f1' }}>
                        {metrics.floorCount}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Floors
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#dc2626' }}>
                        {metrics.hasPodium ? 'Yes' : 'No'}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Podium
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#3b82f6' }}>
                        {metrics.totalUnits}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Slots
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#8b5cf6' }}>
                        {metrics.totalBoxesAllFloors}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Total Boxes
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#f59e0b' }}>
                        {metrics.componentAreaPerFloor.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Footprint (sq ft)
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#16a34a' }}>
                        {metrics.projectTotal.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Project Total (sq ft)
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#7c3aed' }}>
                        {metrics.componentAreaAllFloors.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        Component Area (sq ft)
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Main Controls and Settings */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1.5rem',
          boxSizing: 'border-box'
        }}>
          {showSetbackDialog && <SetbackDialog />}

          {/* Building Tabs and Optimization Controls */}
          <div id="parcel-analysis" style={{ 
            backgroundColor: 'white', 
            padding: '0.75rem', // Reduced from 1rem
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {/* Building Tabs - Only show when buildPaths exist */}
            {layoutState.buildPaths && layoutState.buildPaths.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '6px', // Reduced from 8px
              marginBottom: '10px', // Reduced from 12px
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '4px', // Reduced from 6px
              flexWrap: 'wrap'
            }}>
              {[...(Array.isArray(layoutState.buildPaths) ? layoutState.buildPaths : [])].sort((a, b) => a.id - b.id).map((buildPath, index) => {
                // Calculate box count for this building (base floor count × total floors)
                // Use layoutState.boxes as source of truth (consistent with Unit Analysis)
                const baseBoxCount = (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === buildPath.id).length;
                const totalBoxCount = baseBoxCount * floorCount;
                
                return (
                <button
                  key={buildPath.id}
                  style={{
                    padding: '6px 12px', // Reduced from 8px 16px
                    backgroundColor: layoutState.selectedBuildingId === buildPath.id ? '#3B82F6' : 'transparent',
                    color: layoutState.selectedBuildingId === buildPath.id ? 'white' : '#6B7280',
                    border: '1px solid',
                    borderColor: layoutState.selectedBuildingId === buildPath.id ? '#2563EB' : '#E5E7EB',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem', // Reduced from 0.875rem
                    fontWeight: layoutState.selectedBuildingId === buildPath.id ? '600' : '500',
                    boxShadow: layoutState.selectedBuildingId === buildPath.id ? '0 2px 4px rgba(37, 99, 235, 0.1)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                  onClick={() => dispatch({
                    type: ACTIONS.SET_SELECTED_BUILDING,
                    payload: buildPath.id
                  })}
                >
                  <span>Building {index + 1}</span>
                  <span style={{
                    fontSize: '0.7rem',
                    opacity: 0.9,
                    fontWeight: 'normal'
                  }}>
                    {floorCount > 1 ? `${totalBoxCount} boxes (${floorCount} floors)` : `${baseBoxCount} boxes`}
                  </span>
                </button>
                );
              })}
            </div>
            )}

            {/* Side-by-Side Layout: Visualization (60%) + State Cards (40%) */}
            <div style={{
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '10px' : '14px', // Reduced from 12px/16px
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Visualization Section - 60% width */}
              <div style={{
                flex: isMobile ? '1 1 auto' : '1 1 60%',
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <div style={{ 
                  overflow: 'hidden', 
                  height: isMobile ? '400px' : '650px', // Reduced from 700px
                  position: 'relative'
                }}>
                  <MapboxPCAVisualizer
                    onParcelSelected={handlePCAParcelSelected}
                    onBuildingDataUpdate={handlePCABuildingDataUpdate}
                    layoutStrategy={pcaLayoutStrategy}
                    selectedBuildingId={selectedBuildingId}
                    selectedOptimizations={selectedOptimizations}
                    buildingOptimizations={buildingOptimizations}
                    floorCount={floorCount}
                    setFloorCount={setFloorCount}
                    hasPodium={hasPodium}
                    setHasPodium={setHasPodium}
                    optimalConfiguration={optimalConfiguration}
                    onConfigurationApplied={handleConfigurationApplied}
                    orientationOverride={orientationOverride}
                    setOrientationOverride={setOrientationOverride}
                    onPrincipalAngleUpdate={handlePrincipalAngleUpdate}
                    showSettingsPanel={actualShowPCASettings}
                    setShowSettingsPanel={actualOnShowPCASettingsChange}
                    buildPaths={internalPcaBuildingData?.buildPaths}
                    boxes={internalPcaBuildingData?.boxes}
                    onLogout={onLogout} // Pass onLogout prop
                    externalParcelCoordinates={parcelCoordinates}
                    externalSetbackPoints={setbackPoints}
                    externalSetbackDistances={edgeSetbackDistances}
                    onSetbackDistancesChange={handleSetbackDistancesChange}
                    onAddressSelected={(addressData) => {
                      console.log('📍 Address selected:', addressData);
                      setProjectAddress(addressData?.fullAddress || null);
                    }}
                    projectAddress={projectAddress}
                    onProjectAddressChange={setProjectAddress}
                    layoutMode={layoutMode}
                    startingCorner={startingCorner}
                    precisionBoxCount={precisionBoxCount}
                    precisionOffset={precisionOffset}
                    precisionOffsetY={precisionOffsetY}
                  />
                </div>
              </div>

              {/* State Cards Section - 40% width */}
              <div style={{
                flex: isMobile ? '1 1 auto' : '1 1 40%',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px', // Reduced from 12px
                minWidth: 0,
                overflow: 'visible'
              }}>
                {/* Project Management Panel */}
                <ProjectManagementWrapper 
                  // Current state
                  parcelCoordinates={parcelCoordinates}
                  parcelWidth={parcelWidth}
                  parcelLength={parcelLength}
                  setbackNorth={setbackNorth}
                  setbackEast={setbackEast}
                  setbackSouth={setbackSouth}
                  setbackWest={setbackWest}
                  setbackPoints={setbackPoints}
                  pcaLayoutStrategy={pcaLayoutStrategy}
                  orientationOverride={orientationOverride}
                  principalAngle={principalAngle}
                  boxWidth={boxWidth}
                  boxLength={boxLength}
                  minBoxWidth={minBoxWidth}
                  maxBoxWidth={maxBoxWidth}
                  minBoxLength={minBoxLength}
                  maxBoxLength={maxBoxLength}
                  buildingPadding={buildingPadding}
                  floorCount={floorCount}
                  hasPodium={hasPodium}
                  layoutState={layoutState}
                  internalPcaBuildingData={internalPcaBuildingData}
                  buildingSettings={buildingSettings}
                  colors={colors}
                  showDimensions={showDimensions}
                  showGrid={showGrid}
                  gridSize={gridSize}
                  showLivableSpacesTable={showLivableSpacesTable}
                  unitQuantities={unitQuantities}
                  calculateProjectMetrics={calculateProjectMetrics}
                  
                  // State setters
                  setParcelCoordinates={setParcelCoordinates}
                  setParcelWidth={setParcelWidth}
                  setParcelLength={setParcelLength}
                  setSetbackNorth={setSetbackNorth}
                  setSetbackEast={setSetbackEast}
                  setSetbackSouth={setSetbackSouth}
                  setSetbackWest={setSetbackWest}
                  setSetbackPoints={setSetbackPoints}
                  setPcaLayoutStrategy={setPcaLayoutStrategy}
                  setOrientationOverride={setOrientationOverride}
                  setPrincipalAngle={setPrincipalAngle}
                  setBoxWidth={setBoxWidth}
                  setBoxLength={setBoxLength}
                  setMinBoxWidth={setMinBoxWidth}
                  setMaxBoxWidth={setMaxBoxWidth}
                  setMinBoxLength={setMinBoxLength}
                  setMaxBoxLength={setMaxBoxLength}
                  setBuildingPadding={setBuildingPadding}
                  setFloorCount={setFloorCount}
                  setHasPodium={setHasPodium}
                  setInternalPcaBuildingData={setInternalPcaBuildingData}
                  setBuildingSettings={setBuildingSettings}
                  setColors={setColors}
                  setShowDimensions={setShowDimensions}
                  setShowGrid={setShowGrid}
                  setGridSize={setGridSize}
                  setShowLivableSpacesTable={setShowLivableSpacesTable}
                  setUnitQuantities={setUnitQuantities}
                  dispatch={dispatch}
                  ACTIONS={ACTIONS}
                  edgeSetbackDistances={edgeSetbackDistances}
                  setEdgeSetbackDistances={setEdgeSetbackDistances}
                  
                  // Market and pricing data
                  selectedMarket={selectedMarket}
                  setSelectedMarket={setSelectedMarket}
                  customRentOverrides={customRentOverrides}
                  setCustomRentOverrides={setCustomRentOverrides}
                  
                  // Project address
                  projectAddress={projectAddress}
                  setProjectAddress={setProjectAddress}
                  
                  // Layout mode (Density/Precision)
                  layoutMode={layoutMode}
                  setLayoutMode={setLayoutMode}
                  startingCorner={startingCorner}
                  setStartingCorner={setStartingCorner}
                  precisionBoxCount={precisionBoxCount}
                  setPrecisionBoxCount={setPrecisionBoxCount}
                  precisionOffset={precisionOffset}
                  setPrecisionOffset={setPrecisionOffset}
                  precisionOffsetY={precisionOffsetY}
                  setPrecisionOffsetY={setPrecisionOffsetY}

                  // URL-based project loading
                  initialProjectId={initialProjectId}
                  onProjectChange={onProjectChange}
                />

                {/* Layout Strategy Selector with ModIQ Optimize Button */}
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '10px',
                  borderRadius: '0.5rem',
                  border: '2px solid #e2e8f0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px'
                  }}>
                    <h4 style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0'
                    }}>
                      Layout Strategy
                    </h4>
                    <button
                      style={{
                        padding: '6px',
                        backgroundColor: 'transparent',
                        color: actualShowPCASettings ? '#374151' : '#6b7280',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        transition: 'color 0.2s ease'
                      }}
                      onClick={() => actualOnShowPCASettingsChange(!actualShowPCASettings)}
                      title="Settings"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </svg>
                    </button>
                  </div>
                  
                  {/* Layout Mode Toggle */}
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                    <button
                      onClick={() => setLayoutMode('density')}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: layoutMode === 'density' ? '#3b82f6' : '#e5e7eb',
                        color: layoutMode === 'density' ? 'white' : '#6b7280',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Density
                    </button>
                    <button
                      onClick={() => setLayoutMode('precision')}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: layoutMode === 'precision' ? '#3b82f6' : '#e5e7eb',
                        color: layoutMode === 'precision' ? 'white' : '#6b7280',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Precision
                    </button>
                  </div>
                  
                  {/* Density Mode Controls */}
                  {layoutMode === 'density' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select 
                          style={{ 
                            flex: '1',
                            border: '1px solid #e2e8f0', 
                            borderRadius: '0.375rem', 
                            padding: '0.5rem',
                            backgroundColor: 'white',
                            color: 'inherit',
                            fontSize: '0.8rem'
                          }}
                          value={pcaLayoutStrategy}
                          onChange={(e) => {
                            console.log('🔄 STRATEGY CHANGE:', e.target.value);
                            setPcaLayoutStrategy(e.target.value);
                          }}
                        >
                          <option value="smart-hybrid">Auto-Select Best Fit</option>
                          <option value="parallel-to-orientation">Parallel to Orientation</option>
                          <option value="perpendicular-to-orientation">Perpendicular to Orientation</option>
                        </select>
                        <select
                          style={{
                            width: '70px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            padding: '0.5rem',
                            backgroundColor: 'white',
                            color: 'inherit',
                            fontSize: '0.8rem'
                          }}
                          value={startingCorner}
                          onChange={(e) => setStartingCorner(e.target.value)}
                          title="Starting Corner"
                        >
                          <option value="SW">SW</option>
                          <option value="SE">SE</option>
                          <option value="NW">NW</option>
                          <option value="NE">NE</option>
                        </select>
                      </div>
                    </div>
                  )}
                  
                  {/* Precision Mode Controls */}
                  {layoutMode === 'precision' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '3px' }}>
                            Boxes
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={precisionBoxCount || ''}
                            onChange={(e) => setPrecisionBoxCount(e.target.value ? Number(e.target.value) : null)}
                            placeholder="Auto"
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              backgroundColor: 'white',
                              fontSize: '0.75rem'
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '3px' }}>
                            X Offset (ft)
                          </label>
                          <input
                            type="number"
                            value={precisionOffset}
                            onChange={(e) => setPrecisionOffset(Number(e.target.value) || 0)}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              backgroundColor: 'white',
                              fontSize: '0.75rem'
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#6b7280', display: 'block', marginBottom: '3px' }}>
                            Y Offset (ft)
                          </label>
                          <input
                            type="number"
                            value={precisionOffsetY}
                            onChange={(e) => setPrecisionOffsetY(Number(e.target.value) || 0)}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              backgroundColor: 'white',
                              fontSize: '0.75rem'
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ fontSize: '0.6rem', color: '#9ca3af' }}>
                        X shifts perpendicular to line, Y shifts along line.
                      </div>
                    </div>
                  )}
                  
                  {/* ModIQ Button Row - only show in Density mode */}
                  {layoutMode === 'density' && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <button
                      style={{
                        padding: '8px 12px',
                        width: '100px', // Fixed width to prevent layout shift
                        height: '32px', // Fixed height to prevent vertical layout shift
                        backgroundColor: (() => {
                          // Check if we have any building data available
                          const hasData = (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) || 
                                         (parcelCoordinates && parcelCoordinates.length > 0) || 
                                         (layoutState.buildPaths && layoutState.buildPaths.length > 0);
                          
                          if (isComprehensiveOptimizing) {
                            return '#EE2A7B'; // Pink when optimizing
                          } else if (hasData) {
                            return '#EE2A7B'; // Pink when enabled
                          } else {
                            return '#D1D5DB'; // Light gray when disabled
                          }
                        })(),
                        color: (() => {
                          const hasData = (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) || 
                                         (parcelCoordinates && parcelCoordinates.length > 0) || 
                                         (layoutState.buildPaths && layoutState.buildPaths.length > 0);
                          
                          return (hasData || isComprehensiveOptimizing) ? 'white' : '#9CA3AF';
                        })(),
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (() => {
                          const hasData = (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) || 
                                         (parcelCoordinates && parcelCoordinates.length > 0) || 
                                         (layoutState.buildPaths && layoutState.buildPaths.length > 0);
                          
                          return (hasData && !isComprehensiveOptimizing) ? 'pointer' : 'not-allowed';
                        })(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center', // Center content in fixed width
                        gap: '6px',
                        fontWeight: '500',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box', // Include padding in height calculation
                        opacity: (() => {
                          const hasData = (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) || 
                                         (parcelCoordinates && parcelCoordinates.length > 0) || 
                                         (layoutState.buildPaths && layoutState.buildPaths.length > 0);
                          
                          return (hasData || isComprehensiveOptimizing) ? 1 : 0.6;
                        })()
                      }}
                      onClick={runComprehensiveOptimization}
                      disabled={isComprehensiveOptimizing || !(
                        (internalPcaBuildingData && internalPcaBuildingData.buildableAreaCoords) || 
                        (parcelCoordinates && parcelCoordinates.length > 0) || 
                        (layoutState.buildPaths && layoutState.buildPaths.length > 0)
                      )}
                    >
                      {isComprehensiveOptimizing ? (
                        <>
                          <span style={{ 
                            fontSize: '18px',
                            animation: 'modiq-spin 0.8s linear infinite',
                            display: 'inline-block',
                            fontWeight: 'bold'
                          }}>
                            ◐
                          </span>
                          Optimizing...
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2L2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                          </svg>
                          ModIQ Pro
                        </>
                      )}
                    </button>
                    </div>
                  )}
                  
                  {/* Orientation Angle Controls */}
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#6b7280', 
                      marginBottom: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>Orientation Angle</span>
                      <span>Principal: {principalAngle?.toFixed(1)}°</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="range"
                        min={-180}
                        max={180}
                        step={0.1}
                        value={orientationOverride ?? principalAngle ?? 0}
                        onChange={e => setOrientationOverride(Number(e.target.value))}
                        style={{ 
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          background: '#e2e8f0',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />
                      <input
                        type="number"
                        min={-180}
                        max={180}
                        step={0.1}
                        value={orientationOverride ?? principalAngle ?? 0}
                        onChange={e => setOrientationOverride(Number(e.target.value))}
                        style={{ 
                          width: '60px',
                          padding: '4px 6px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          textAlign: 'center'
                        }}
                      />
                      <button
                        style={{ 
                          padding: '4px 8px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          background: '#f8fafc',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          fontWeight: '500'
                        }}
                        onClick={() => setOrientationOverride(null)}
                        disabled={orientationOverride === null || orientationOverride === undefined}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* Only show state cards when buildings exist */}
                {layoutState.buildPaths && layoutState.buildPaths.length > 0 && layoutState.selectedBuildingId && (
                  <>
                    {/* Building Indicator */}
                    <div style={{
                      backgroundColor: '#f0f9ff',
                      padding: '6px 10px', // Reduced from 8px 12px
                      borderRadius: '0.375rem',
                      border: '1px solid #bfdbfe',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        fontSize: '0.8rem', // Reduced from 0.875rem
                        fontWeight: '600',
                        color: '#1e40af'
                      }}>
                        Building {layoutState.selectedBuildingId} Analysis
                      </span>
                    </div>

                    {/* Standard Unit Card */}
                    <div style={{ 
                      backgroundColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'none' ? '#f8f9fa' : '#f8fafc',
                      borderRadius: '0.5rem',
                      border: '2px solid',
                      borderColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'none' ? '#e9ecef' : '#e2e8f0',
                      overflow: 'hidden'
                    }}>
                      {/* Card Header */}
                      <div 
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: expandedCards.standard ? '1px solid #e2e8f0' : 'none'
                        }}
                        onClick={() => {
                          toggleCard('standard');
                          dispatch({
                            type: ACTIONS.UPDATE_SELECTED_OPTIMIZATIONS,
                            payload: {
                              [layoutState.selectedBuildingId]: 'none'
                            }
                          });
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h4 style={{
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#495057',
                            margin: 0
                          }}>
                            Standard Unit
                          </h4>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            transform: `rotate(${expandedCards.standard ? '90deg' : '0deg'})`,
                            transition: 'transform 0.2s ease'
                          }}>
                            ▶
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#495057' }}>
                            {standardBoxWidth.toFixed(1)} × {standardBoxLength.toFixed(1)}ft
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                            Baseline
                          </div>
                        </div>
                      </div>

                      {/* Collapsible Content */}
                      {expandedCards.standard && (
                        <div style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <MetricRow 
                              label="Size"
                              value={`${standardBoxWidth.toFixed(1)} × ${standardBoxLength.toFixed(1)}ft`}
                            />
                            <MetricRow 
                              label="Box SQFT"
                              value={standardBoxSqFt.toFixed(1)}
                            />
                            <MetricRow 
                              label="Component SQFT"
                              value={(() => {
                                const totalSqFt = standardBoxSqFt * standardBoxCount * floorCount;
                                return totalSqFt.toLocaleString();
                              })()}
                            />
                            <MetricRow 
                              label="Building SQFT"
                              value={`${(standardBoxSqFt * standardBoxCount * (floorCount + (hasPodium ? 1 : 0))).toLocaleString()}`}
                            />
                            <div style={{ display: 'grid', gridTemplateColumns: "repeat(7, 1fr)", gap: '8px', margin: '4px 0' }}>
                              <CompactMetricRow 
                                label="Boxes"
                                value={floorCount > 1 ? `${standardBoxCount * floorCount}` : standardBoxCount}
                              />
                              <CompactMetricRow 
                                label="Units" 
                                value={floorCount > 1 ? `${standardUnitCount * floorCount}` : standardUnitCount}
                              />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: "repeat(7, 1fr)", gap: '8px' }}>
                              <CompactMetricRow 
                                label="Efficiency"
                                value={`${calculateStandardMetrics(
                                  (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === layoutState.selectedBuildingId), 
                                  (Array.isArray(layoutState.buildPaths) ? layoutState.buildPaths : []).find(bp => bp.id === layoutState.selectedBuildingId)
                                ).efficiency.toFixed(1)}%`}
                              />
                              <CompactMetricRow 
                                label="Livability"
                                value="80%"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Efficiency Card */}
                    <div style={{ 
                      backgroundColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'efficiency' ? '#ebf5ff' : '#f8fafc',
                      borderRadius: '0.5rem',
                      border: '2px solid',
                      borderColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'efficiency' ? '#bfdbfe' : '#e2e8f0',
                      overflow: 'hidden'
                    }}>
                      {/* Card Header */}
                      <div 
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: expandedCards.efficiency ? '1px solid #e2e8f0' : 'none'
                        }}
                        onClick={() => {
                          toggleCard('efficiency');
                          dispatch({
                            type: ACTIONS.UPDATE_SELECTED_OPTIMIZATIONS,
                            payload: {
                              [layoutState.selectedBuildingId]: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'efficiency' ? 'none' : 'efficiency'
                            }
                          });
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h4 style={{
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#1e40af',
                            margin: 0
                          }}>
                            Efficiency Optimization
                          </h4>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            transform: `rotate(${expandedCards.efficiency ? '90deg' : '0deg'})`,
                            transition: 'transform 0.2s ease'
                          }}>
                            ▶
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {layoutState.buildingOptimizations[layoutState.selectedBuildingId]?.efficiency?.optimizedBoxes ? (
                            (() => {
                              const efficiencyData = layoutState.buildingOptimizations[layoutState.selectedBuildingId].efficiency;
                              const optimizedBox = efficiencyData.optimizedBoxes[0];
                              const optimizedBoxWidthFt = (optimizedBox?.width || 0) * 3.28084;
                              const optimizedBoxHeightFt = (optimizedBox?.height || 0) * 3.28084;
                              const optimizedBoxWidth = Math.min(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxLength = Math.max(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxSqFt = optimizedBoxWidth * optimizedBoxLength;
                              const { change, symbol } = calculateChange(optimizedBoxSqFt, standardBoxSqFt);
                              
                              return (
                                <>
                                  <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#1e40af' }}>
                                    {optimizedBoxWidth.toFixed(1)} × {optimizedBoxLength.toFixed(1)}ft
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.7rem', 
                                    color: symbol === '↑' ? '#059669' : symbol === '↓' ? '#DC2626' : '#6b7280',
                                    fontWeight: '500'
                                  }}>
                                    {symbol === '–' ? 'No change' : `${symbol} ${change}%`}
                                  </div>
                                </>
                              );
                            })()
                          ) : (
                            <>
                              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>
                                Calculating...
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                Please wait
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Collapsible Content */}
                      {expandedCards.efficiency && layoutState.buildingOptimizations[layoutState.selectedBuildingId]?.efficiency?.optimizedBoxes && (
                        <div style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(() => {
                              const efficiencyData = layoutState.buildingOptimizations[layoutState.selectedBuildingId].efficiency;
                              const optimizedBox = efficiencyData.optimizedBoxes[0];
                              const optimizedBoxWidthFt = (optimizedBox?.width || 0) * 3.28084;
                              const optimizedBoxHeightFt = (optimizedBox?.height || 0) * 3.28084;
                              const optimizedBoxWidth = Math.min(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxLength = Math.max(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxSqFt = optimizedBoxWidth * optimizedBoxLength;
                              const optimizedBoxCount = efficiencyData.optimizedBoxes?.length || 0;
                              const optimizedComponentSqFt = optimizedBoxSqFt * optimizedBoxCount * floorCount;
                              const optimizedBuildingSqFt = optimizedBoxSqFt * optimizedBoxCount * (floorCount + (hasPodium ? 1 : 0));
                              const optimizedUnitCount = optimizedBoxCount * 2;
                              const realEfficiencyPercent = efficiencyData.metrics?.efficiency || 80;
                              const standardEfficiencyPercent = 80;
                              const standardComponentSqFt = standardBoxSqFt * standardBoxCount * floorCount;
                              const standardBuildingSqFt = standardBoxSqFt * standardBoxCount * (floorCount + (hasPodium ? 1 : 0));
                              
                              return (
                                <>
                                  <MetricRow 
                                    label="Size"
                                    value={`${optimizedBoxWidth.toFixed(1)} × ${optimizedBoxLength.toFixed(1)}ft`}
                                    comparison={renderChangeIndicator(optimizedBoxSqFt, standardBoxSqFt)}
                                  />
                                  <MetricRow 
                                    label="Box SQFT"
                                    value={optimizedBoxSqFt.toFixed(1)}
                                    comparison={renderChangeIndicator(optimizedBoxSqFt, standardBoxSqFt)}
                                  />
                                  <MetricRow 
                                    label="Component SQFT"
                                    value={optimizedComponentSqFt.toLocaleString()}
                                    comparison={renderChangeIndicator(optimizedComponentSqFt, standardComponentSqFt)}
                                  />
                                  <MetricRow 
                                    label="Building SQFT"
                                    value={optimizedBuildingSqFt.toLocaleString()}
                                    comparison={renderChangeIndicator(optimizedBuildingSqFt, standardBuildingSqFt)}
                                  />
                                  <div style={{ display: 'grid', gridTemplateColumns: "repeat(7, 1fr)", gap: '8px', margin: '4px 0' }}>
                                    <CompactMetricRow 
                                      label="Boxes"
                                      value={floorCount > 1 ? `${optimizedBoxCount * floorCount}` : optimizedBoxCount}
                                      comparison={renderChangeIndicator(optimizedBoxCount, standardBoxCount)}
                                    />
                                    <CompactMetricRow 
                                      label="Units"
                                      value={floorCount > 1 ? `${optimizedUnitCount * floorCount}` : optimizedUnitCount}
                                      comparison={renderChangeIndicator(optimizedUnitCount, standardUnitCount)}
                                    />
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: "repeat(7, 1fr)", gap: '8px' }}>
                                    <CompactMetricRow 
                                      label="Efficiency"
                                      value={`${Math.min(100, realEfficiencyPercent).toFixed(1)}%`}
                                      comparison={renderChangeIndicator(realEfficiencyPercent, standardEfficiencyPercent)}
                                    />
                                    <CompactMetricRow 
                                      label="Livability"
                                      value="75%"
                                      comparison={renderChangeIndicator(75, 80)}
                                    />
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Livability Card */}
                    <div style={{
                      backgroundColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'livability' ? '#f0fff4' : '#f8fafc',
                      borderRadius: '0.5rem',
                      border: '2px solid',
                      borderColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'livability' ? '#bef5cb' : '#e2e8f0',
                      overflow: 'hidden'
                    }}>
                      {/* Card Header */}
                      <div 
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: expandedCards.livability ? '1px solid #e2e8f0' : 'none'
                        }}
                        onClick={() => {
                          toggleCard('livability');
                          dispatch({
                            type: ACTIONS.UPDATE_SELECTED_OPTIMIZATIONS,
                            payload: {
                              [layoutState.selectedBuildingId]: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'livability' ? 'none' : 'livability'
                            }
                          });
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h4 style={{
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#166534',
                            margin: 0
                          }}>
                            Livability Optimization
                          </h4>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            transform: `rotate(${expandedCards.livability ? '90deg' : '0deg'})`,
                            transition: 'transform 0.2s ease'
                          }}>
                            ▶
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {layoutState.buildingOptimizations[layoutState.selectedBuildingId]?.livability?.optimizedBoxes ? (
                            (() => {
                              const livabilityData = layoutState.buildingOptimizations[layoutState.selectedBuildingId].livability;
                              const optimizedBox = livabilityData.optimizedBoxes[0];
                              if (!optimizedBox || !livabilityData.metrics) {
                                return (
                                  <>
                                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>
                                      Calculating...
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                      Please wait
                                    </div>
                                  </>
                                );
                              }
                              const optimizedBoxWidthFt = (optimizedBox?.width || 0) * 3.28084;
                              const optimizedBoxHeightFt = (optimizedBox?.height || 0) * 3.28084;
                              const optimizedBoxWidth = Math.min(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxLength = Math.max(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxSqFt = optimizedBoxWidth * optimizedBoxLength;
                              const { change, symbol } = calculateChange(optimizedBoxSqFt, standardBoxSqFt);
                              
                              return (
                                <>
                                  <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#166534' }}>
                                    {optimizedBoxWidth.toFixed(1)} × {optimizedBoxLength.toFixed(1)}ft
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.7rem', 
                                    color: symbol === '↑' ? '#059669' : symbol === '↓' ? '#DC2626' : '#6b7280',
                                    fontWeight: '500'
                                  }}>
                                    {symbol === '–' ? 'No change' : `${symbol} ${change}%`}
                                  </div>
                                </>
                              );
                            })()
                          ) : (
                            <>
                              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280' }}>
                                Calculating...
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                Please wait
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Collapsible Content */}
                      {expandedCards.livability && layoutState.buildingOptimizations[layoutState.selectedBuildingId]?.livability?.optimizedBoxes && (
                        <div style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(() => {
                              const livabilityData = layoutState.buildingOptimizations[layoutState.selectedBuildingId].livability;
                              const optimizedBox = livabilityData.optimizedBoxes[0];
                              
                              if (!optimizedBox || !livabilityData.metrics) {
                                return (
                                  <div style={{ 
                                    padding: '8px', 
                                    backgroundColor: '#fef3c7', 
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    color: '#92400e'
                                  }}>
                                    Livability optimization in progress...
                                  </div>
                                );
                              }
                              
                              const optimizedBoxWidthFt = (optimizedBox?.width || 0) * 3.28084;
                              const optimizedBoxHeightFt = (optimizedBox?.height || 0) * 3.28084;
                              const optimizedBoxWidth = Math.min(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxLength = Math.max(optimizedBoxWidthFt, optimizedBoxHeightFt);
                              const optimizedBoxSqFt = optimizedBoxWidth * optimizedBoxLength;
                              const optimizedBoxCount = livabilityData.optimizedBoxes?.length || 0;
                              const optimizedComponentSqFt = optimizedBoxSqFt * optimizedBoxCount * floorCount;
                              const optimizedBuildingSqFt = optimizedBoxSqFt * optimizedBoxCount * (floorCount + (hasPodium ? 1 : 0));
                              const optimizedUnitCount = optimizedBoxCount * 2;
                              const realEfficiencyPercent = livabilityData.metrics?.efficiency || 80;
                              const livabilityPercent = livabilityData.metrics?.livability || 80;
                              const standardEfficiencyPercent = 80;
                              const standardLivabilityPercent = 80;
                              const standardComponentSqFt = standardBoxSqFt * standardBoxCount * floorCount;
                              const standardBuildingSqFt = standardBoxSqFt * standardBoxCount * (floorCount + (hasPodium ? 1 : 0));
                              
                              return (
                                <>
                                  <MetricRow 
                                    label="Size"
                                    value={`${optimizedBoxWidth.toFixed(1)} × ${optimizedBoxLength.toFixed(1)}ft`}
                                    comparison={renderChangeIndicator(optimizedBoxSqFt, standardBoxSqFt)}
                                  />
                                  <MetricRow 
                                    label="Box SQFT"
                                    value={optimizedBoxSqFt.toFixed(1)}
                                    comparison={renderChangeIndicator(optimizedBoxSqFt, standardBoxSqFt)}
                                  />
                                  <MetricRow 
                                    label="Component SQFT"
                                    value={optimizedComponentSqFt.toLocaleString()}
                                    comparison={renderChangeIndicator(optimizedComponentSqFt, standardComponentSqFt)}
                                  />
                                  <MetricRow 
                                    label="Building SQFT"
                                    value={optimizedBuildingSqFt.toLocaleString()}
                                    comparison={renderChangeIndicator(optimizedBuildingSqFt, standardBuildingSqFt)}
                                  />
                                  <div style={{ display: 'grid', gridTemplateColumns: "repeat(7, 1fr)", gap: '8px', margin: '4px 0' }}>
                                    <CompactMetricRow 
                                      label="Boxes"
                                      value={floorCount > 1 ? `${optimizedBoxCount * floorCount}` : optimizedBoxCount}
                                      comparison={renderChangeIndicator(optimizedBoxCount, standardBoxCount)}
                                    />
                                    <CompactMetricRow 
                                      label="Units"
                                      value={floorCount > 1 ? `${optimizedUnitCount * floorCount}` : optimizedUnitCount}
                                      comparison={renderChangeIndicator(optimizedUnitCount, standardUnitCount)}
                                    />
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: "repeat(7, 1fr)", gap: '8px' }}>
                                    <CompactMetricRow 
                                      label="Efficiency"
                                      value={`${Math.min(100, realEfficiencyPercent).toFixed(1)}%`}
                                      comparison={renderChangeIndicator(realEfficiencyPercent, standardEfficiencyPercent)}
                                    />
                                    <CompactMetricRow 
                                      label="Livability"
                                      value={`${livabilityPercent.toFixed(1)}%`}
                                      comparison={renderChangeIndicator(livabilityPercent, standardLivabilityPercent)}
                                    />
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Comprehensive ModIQ Results Dialog */}
          {showModIQDialog && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000 // Increased from 50 to be higher than Settings button (9999)
            }}>
          <div style={{ 
            backgroundColor: 'white', 
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '800px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
                  justifyContent: 'space-between',
              alignItems: 'center', 
                  marginBottom: '16px'
            }}>
              <h3 style={{ 
                fontSize: '1.25rem', 
                    fontWeight: '600',
                color: '#1a202c', 
                margin: 0
                  }}>
                {isComprehensiveOptimizing ? 'ModIQ Pro Optimization in Progress...' : 'ModIQ Pro Results'}
              </h3>
                  <button
                    style={{
                      padding: '8px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowModIQDialog(false)}
                    disabled={isComprehensiveOptimizing}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

            {/* Progress Section */}
            {isComprehensiveOptimizing && optimizationProgress && (
              <div style={{
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                      Testing angle {optimizationProgress.currentAngle}° with {optimizationProgress.currentStrategy}
                    </span>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {optimizationProgress.progress}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${optimizationProgress.progress}%`,
                      height: '100%',
                      backgroundColor: '#3b82f6',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr 1fr', 
                  gap: '16px',
                  fontSize: '0.875rem'
                }}>
                  <div>
                    <strong>Progress:</strong> {optimizationProgress.evaluationCount}/{optimizationProgress.totalEvaluations}
                  </div>
                  <div>
                    <strong>Valid Configs:</strong> {optimizationProgress.validConfigurations || 0}
                  </div>
                  <div>
                    <strong>Invalid Configs:</strong> {optimizationProgress.invalidConfigurations || 0}
                  </div>
                  <div>
                    <strong>Time Remaining:</strong> {optimizationProgress.estimatedTimeRemaining}s
                  </div>
                  <div>
                    <strong>Current Best:</strong> {optimizationProgress.currentBest?.boxCount || 'None'} boxes
                  </div>
                  <div>
                    <em>Min 4 boxes/building</em>
                  </div>
                </div>
                
                {optimizationProgress.currentBest && (
                  <div style={{ 
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '4px'
                  }}>
                    <strong>Leading Configuration:</strong> {optimizationProgress.currentBest.boxCount} boxes at {optimizationProgress.currentBest.angle}° with {optimizationProgress.currentBest.strategy}
                  </div>
                )}
              </div>
            )}

            {/* Results Section */}
            {comprehensiveResults && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Winner Section */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f0fff4',
                  borderRadius: '8px',
                  border: '2px solid #22c55e'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#166534',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      🏆 Optimal Configuration
                    </h4>
                    <button
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                      onClick={() => {
                        // Apply the winning configuration
                        const winner = comprehensiveResults.summary.winner;
                        applyOptimalConfiguration(winner);
                      }}
                    >
                      Apply Configuration
                    </button>
                  </div>
                  
                  {comprehensiveResults.summary.winner && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr 1fr 1fr', 
                      gap: '16px',
                      fontSize: '0.875rem'
                    }}>
                      <div>
                        <strong>Boxes:</strong> {comprehensiveResults.summary.winner.metrics.boxCount}
                      </div>
                      <div>
                        <strong>Buildings:</strong> {comprehensiveResults.summary.winner.metrics.buildingCount}
                      </div>
                      <div>
                        <strong>Angle:</strong> {comprehensiveResults.summary.winner.angle.toFixed(1)}°
                      </div>
                      <div>
                        <strong>Strategy:</strong> {comprehensiveResults.summary.winner.strategyName}
                      </div>
                      <div>
                        <strong>Units:</strong> {comprehensiveResults.summary.winner.metrics.unitsTotal}
                      </div>
                      <div>
                        <strong>Total Time:</strong> {(comprehensiveResults.summary.totalTimeMs / 1000).toFixed(1)}s
                      </div>
                      <div style={{ gridColumn: 'span 2' }}>
                        <strong>Valid Configurations:</strong> {comprehensiveResults.summary.validConfigurations} / {comprehensiveResults.summary.totalConfigurations} ({((comprehensiveResults.summary.validConfigurations / comprehensiveResults.summary.totalConfigurations) * 100).toFixed(1)}%)
                      </div>
                      {comprehensiveResults.summary.invalidConfigurations > 0 && (
                        <div style={{ gridColumn: 'span 2', fontSize: '0.75rem', color: '#6b7280' }}>
                          <em>{comprehensiveResults.summary.invalidConfigurations} configurations rejected (buildings with &lt; 4 boxes)</em>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Top 5 Results */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 12px 0'
                  }}>Top 5 Configurations</h4>
                  
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f1f5f9' }}>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Rank</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Boxes</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Buildings</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Angle</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Strategy</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comprehensiveResults.summary.top10.slice(0, 5).map((result, index) => (
                          <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>#{index + 1}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>{result.metrics.boxCount}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>{result.metrics.buildingCount}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>{result.angle.toFixed(1)}°</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>{result.strategyName}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>{result.metrics.unitsTotal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Strategy Performance Summary */}
                {comprehensiveResults.strategyPerformance && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 12px 0'
                    }}>Strategy Performance Summary</h4>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '12px' 
                    }}>
                      {comprehensiveResults.strategyPerformance.map((strategy) => (
                        <div key={strategy.strategy} style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                            {strategy.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            <div><strong>Best:</strong> {strategy.maxBoxCount} boxes at {strategy.bestAngle.toFixed(1)}°</div>
                            <div><strong>Average:</strong> {strategy.averageBoxCount} boxes</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
              </div>
            </div>
          )}

          {/* Project Overview Dialog */}
          {showProjectOverview && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000 // Increased from 50 to be consistent with ModIQ modal
            }}>
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center', 
                  marginBottom: '20px'
                }}>
                  <h3 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: '600',
                    color: '#1a202c', 
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3h18v18h-18zM9 9h6v6h-6z"/>
                    </svg>
                    Project Overview
                  </h3>
                  <button
                    style={{
                      padding: '8px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowProjectOverview(false)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {(() => {
                  const metrics = calculateProjectMetrics();
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Summary Stats */}
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h4 style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 12px 0'
                        }}>Project Summary</h4>
                        
                        {/* All Metrics in One Row */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(8, 1fr)`, // 8 columns for project metrics including Footprint
                          gap: '10px',
                          fontSize: '0.75rem'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#059669' }}>
                              {metrics.totalBuildings}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Buildings
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#6366f1' }}>
                              {metrics.floorCount}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Floors
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#dc2626' }}>
                              {metrics.hasPodium ? 'Yes' : 'No'}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Podium
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#3b82f6' }}>
                              {metrics.totalUnits}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Slots
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#8b5cf6' }}>
                              {metrics.totalBoxesAllFloors}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Total Boxes
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#f59e0b' }}>
                              {metrics.componentAreaPerFloor.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Footprint (sq ft)
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#16a34a' }}>
                              {metrics.projectTotal.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Project Total (sq ft)
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#7c3aed' }}>
                              {metrics.componentAreaAllFloors.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                              Component Area (sq ft)
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Detailed Metrics */}
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#fefefe',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 16px 0'
                        }}>Area Breakdown</h4>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                              Component Area (Single Floor):
                            </span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                              {metrics.componentAreaPerFloor.toLocaleString()} sq ft
                            </span>
                          </div>
                          
                          {metrics.floorCount > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                                Component Area ({metrics.floorCount} Floors):
                              </span>
                              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                                {metrics.componentAreaAllFloors.toLocaleString()} sq ft
                              </span>
                            </div>
                          )}
                          
                          {metrics.hasPodium && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                                Podium Area:
                              </span>
                              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                                {metrics.podiumArea.toLocaleString()} sq ft
                              </span>
                            </div>
                          )}
                          
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            borderTop: '1px solid #e5e7eb',
                            paddingTop: '12px',
                            marginTop: '8px'
                          }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                              Total Building Area:
                            </span>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#059669' }}>
                              {metrics.totalBuildingArea.toLocaleString()} sq ft
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Site Efficiency */}
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#fef3f2',
                        borderRadius: '8px',
                        border: '1px solid #fecaca'
                      }}>
                        <h4 style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 12px 0'
                        }}>Site Utilization</h4>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                              Total Buildable Area:
                            </span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                              {metrics.buildableAreaSqFt.toLocaleString()} sq ft
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                              Building Footprint:
                            </span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                              {metrics.componentAreaPerFloor.toLocaleString()} sq ft
                            </span>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            borderTop: '1px solid #fecaca',
                            paddingTop: '8px',
                            marginTop: '8px'
                          }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                              Land Efficiency:
                            </span>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#ef4444' }}>
                              {metrics.landEfficiency.toFixed(1)}%
                            </span>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                            How much of the buildable area is occupied by buildings
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Unit Analysis Section */}
      <div id="unit-analysis" style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        boxSizing: 'border-box',
        borderTop: '1px solid #e2e8f0'
      }}>
        {/* Section Header */}
        <div style={{
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9,22 9,12 15,12 15,22"/>
            </svg>
            Unit Analysis
          </h2>
        </div>

        {/* Unit Analysis Content - Integrated Unit Mix */}
        {layoutState.buildPaths && layoutState.buildPaths.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Building Selector for Unit Mix */}
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              padding: '1rem'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 1rem 0'
              }}>
                Configure Unit Mix by Building
              </h3>
              
              {/* Building Tabs */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '1.5rem',
                flexWrap: 'wrap'
              }}>
                {[...(Array.isArray(layoutState.buildPaths) ? layoutState.buildPaths : [])].sort((a, b) => a.id - b.id).map((buildPath, index) => {
                  const currentBoxes = (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === buildPath.id);
                  const totalBoxes = currentBoxes.length;
                  const egressPositions = calculateEgressSlots(totalBoxes);
                  const totalSlots = totalBoxes * 2 * floorCount;
                  const egressSlotsTotal = egressPositions.length * floorCount;
                  const availableSlots = totalSlots - egressSlotsTotal;
                  
                  return (
                    <button
                      key={buildPath.id}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: layoutState.selectedBuildingId === buildPath.id ? '#3B82F6' : 'white',
                        color: layoutState.selectedBuildingId === buildPath.id ? 'white' : '#374151',
                        border: '1px solid',
                        borderColor: layoutState.selectedBuildingId === buildPath.id ? '#2563EB' : '#e2e8f0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => dispatch({
                        type: ACTIONS.SET_SELECTED_BUILDING,
                        payload: buildPath.id
                      })}
                    >
                      Building {index + 1} ({availableSlots} slots)
                    </button>
                  );
                })}
              </div>

              {/* Unit Mix Configuration for Selected Building */}
              {layoutState.selectedBuildingId && buildingSettings[layoutState.selectedBuildingId] && (
                <div>
                  {(() => {
                    const currentSettings = buildingSettings[layoutState.selectedBuildingId];
                    const currentBoxes = (Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => box.buildPathId === layoutState.selectedBuildingId);
                    
                    // Calculate AVAILABLE SLOTS (total slots minus egress)
                    const totalBoxes = currentBoxes.length;
                    const egressPositions = calculateEgressSlots(totalBoxes);
                    const totalSlots = totalBoxes * 2 * floorCount;
                    const egressSlotsTotal = egressPositions.length * floorCount; // Egress takes 1 slot each
                    const availableSlots = totalSlots - egressSlotsTotal;
                    
                    // Slot requirements for each unit type
                    const SLOT_REQUIREMENTS = {
                      studio: 1,
                      oneBR: 2,
                      twoBR: 3,
                      studioPlus: 1, // Part of 3-slot pair with 1BR+
                      oneBRPlus: 2   // Part of 3-slot pair with Studio+
                    };
                    
                    // Calculate actual dwelling unit counts based on slot percentages
                    // ALWAYS fills 100% of available slots
                    const calculateUnitCounts = (distribution) => {
                      const counts = {};
                      let totalSlotsUsed = 0;
                      
                      // First pass: calculate slot allocation for each type
                      const slotAllocations = {};
                      Object.keys(distribution).forEach(key => {
                        const percentage = distribution[key] || 0;
                        const slotsForType = Math.floor(availableSlots * (percentage / 100));
                        slotAllocations[key] = slotsForType;
                      });
                      
                      // Second pass: convert slots to actual dwelling units
                      Object.keys(distribution).forEach(key => {
                        const slotsForType = slotAllocations[key];
                        const slotsPerUnit = SLOT_REQUIREMENTS[key] || 1;
                        const unitCount = Math.floor(slotsForType / slotsPerUnit);
                        counts[key] = unitCount;
                        totalSlotsUsed += unitCount * slotsPerUnit;
                      });
                      
                      // Third pass: Fill remaining slots to always use 100%
                      // Prioritize by percentage allocation (user's preference)
                      let remainingSlots = availableSlots - totalSlotsUsed;
                      
                      // Sort unit types by their percentage (highest first) to respect user intent
                      const sortedTypes = Object.keys(distribution)
                        .filter(key => (distribution[key] || 0) > 0)
                        .sort((a, b) => (distribution[b] || 0) - (distribution[a] || 0));
                      
                      while (remainingSlots > 0) {
                        let placed = false;
                        
                        // Try to place units in order of user preference
                        for (const key of sortedTypes) {
                          const slotsPerUnit = SLOT_REQUIREMENTS[key] || 1;
                          
                          // Special handling for paired units (studioPlus/oneBRPlus)
                          if (key === 'studioPlus' || key === 'oneBRPlus') {
                            // Only add a pair if we have 3 slots and both are in the mix
                            if (remainingSlots >= 3 && distribution['studioPlus'] > 0 && distribution['oneBRPlus'] > 0) {
                              counts['studioPlus'] = (counts['studioPlus'] || 0) + 1;
                              counts['oneBRPlus'] = (counts['oneBRPlus'] || 0) + 1;
                              remainingSlots -= 3;
                              totalSlotsUsed += 3;
                              placed = true;
                              break;
                            }
                          } else if (remainingSlots >= slotsPerUnit) {
                            counts[key] = (counts[key] || 0) + 1;
                            remainingSlots -= slotsPerUnit;
                            totalSlotsUsed += slotsPerUnit;
                            placed = true;
                            break;
                          }
                        }
                        
                        // If we couldn't place any preferred unit, fill with smallest that fits
                        if (!placed) {
                          if (remainingSlots >= 3) {
                            // Try 2BR first if in mix, otherwise paired units
                            if ((distribution['twoBR'] || 0) > 0) {
                              counts['twoBR'] = (counts['twoBR'] || 0) + 1;
                              remainingSlots -= 3;
                              totalSlotsUsed += 3;
                            } else if ((distribution['studioPlus'] || 0) > 0 && (distribution['oneBRPlus'] || 0) > 0) {
                              counts['studioPlus'] = (counts['studioPlus'] || 0) + 1;
                              counts['oneBRPlus'] = (counts['oneBRPlus'] || 0) + 1;
                              remainingSlots -= 3;
                              totalSlotsUsed += 3;
                            } else {
                              // Fall back to 1BR + studio
                              counts['oneBR'] = (counts['oneBR'] || 0) + 1;
                              remainingSlots -= 2;
                              totalSlotsUsed += 2;
                            }
                          } else if (remainingSlots >= 2) {
                            counts['oneBR'] = (counts['oneBR'] || 0) + 1;
                            remainingSlots -= 2;
                            totalSlotsUsed += 2;
                          } else if (remainingSlots === 1) {
                            counts['studio'] = (counts['studio'] || 0) + 1;
                            remainingSlots -= 1;
                            totalSlotsUsed += 1;
                          } else {
                            break; // Safety exit
                          }
                        }
                      }
                      
                      return { counts, totalSlotsUsed };
                    };
                    
                    const { counts: unitCounts, totalSlotsUsed } = calculateUnitCounts(currentSettings.unitTypeDistribution);
                    const totalDwellingUnits = Object.values(unitCounts).reduce((sum, count) => sum + count, 0);

                    return (
                      <>
                        {/* Responsive 3-Column Layout */}
                        <div style={{ 
                          display: 'grid',
                          gridTemplateColumns: '3fr 3fr 4fr',
                          gap: '1rem',
                          marginBottom: '1.5rem'
                        }}>
                          {/* First Column - Unit Type Distribution */}
                          <div style={{
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid #bfdbfe',
                            padding: '1rem'
                          }}>
                            <h4 style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: '#1e40af',
                              margin: '0 0 1rem 0',
                              textAlign: 'left'
                            }}>
                              Unit Type Distribution
                            </h4>
                            
                            {/* Unit Type Sliders - Group paired units together */}
                            {(() => {
                              // Group unit types: regular units and paired units
                              const regularUnits = UNIT_TYPES.filter(type => 
                                type.key !== 'studioPlus' && type.key !== 'oneBRPlus'
                              );
                              const pairedUnits = UNIT_TYPES.filter(type => 
                                type.key === 'studioPlus' || type.key === 'oneBRPlus'
                              );
                              
                              // Use the calculated unit counts
                              const distributedCounts = unitCounts;
                              
                              return (
                                <>
                                  {/* Regular Units */}
                                  {regularUnits.map((type) => {
                                    const percentage = currentSettings.unitTypeDistribution[type.key] || 0;
                                    const unitCount = distributedCounts[type.key] || 0;
                                    
                                    return (
                                      <div key={type.key} style={{ 
                                        marginBottom: '0.75rem'
                                      }}>
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: '0.4rem'
                                        }}>
                                          <span style={{ 
                                            fontSize: '0.8rem',
                                            fontWeight: '500',
                                            color: '#374151'
                                          }}>
                                            {type.label}
                                          </span>
                                          <div style={{ textAlign: 'right' }}>
                                            <span style={{ 
                                              fontSize: '0.8rem',
                                              fontWeight: '600',
                                              color: '#1e40af'
                                            }}>
                                              {percentage}%
                                            </span>
                                            <span style={{ 
                                              fontSize: '0.7rem',
                                              color: '#6b7280',
                                              display: 'block'
                                            }}>
                                              ({unitCount} units)
                                            </span>
                                          </div>
                                        </div>
                                        <input
                                          type="range"
                                          min={0}
                                          max={100}
                                          step={1}
                                          value={percentage}
                                          onChange={(e) => handleUnitTypeSlider(layoutState.selectedBuildingId, type.key, Number(e.target.value))}
                                          style={{ 
                                            width: '100%',
                                            height: '5px',
                                            borderRadius: '3px',
                                            background: '#e2e8f0',
                                            outline: 'none',
                                            cursor: 'pointer'
                                          }}
                                        />
                                      </div>
                                    );
                                  })}
                                  
                                  {/* Paired Units - Studio+ & 1BR+ together */}
                                  {pairedUnits.length > 0 && (
                                    <div style={{
                                      backgroundColor: 'transparent',
                                      borderRadius: '6px',
                                      border: '1px solid #e2e8f0',
                                      padding: '0.75rem',
                                      marginBottom: '0.75rem'
                                    }}>
                                      <div style={{
                                        fontSize: '0.7rem',
                                        color: '#6b7280',
                                        fontWeight: '500',
                                        marginBottom: '0.4rem',
                                        textAlign: 'center'
                                      }}>
                                        PAIRED UNITS (move together)
                                      </div>
                                      
                                      {(() => {
                                        // Use Studio+ percentage for both (they're always the same)
                                        const percentage = currentSettings.unitTypeDistribution['studioPlus'] || 0;
                                        const unitCount = distributedCounts['studioPlus'] || 0;
                                        
                                        return (
                                          <div>
                                            <div style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              marginBottom: '0.4rem'
                                            }}>
                                              <div style={{ 
                                                fontSize: '0.8rem',
                                                fontWeight: '500',
                                                color: '#374151'
                                              }}>
                                                <div>Studio+ & 1 Bedroom+</div>
                                                <div style={{ 
                                                  fontSize: '0.65rem', 
                                                  color: '#6b7280',
                                                  fontStyle: 'italic'
                                                }}>
                                                  (3-slot sequence)
                                                </div>
                                              </div>
                                              <div style={{ textAlign: 'right' }}>
                                                <span style={{ 
                                                  fontSize: '0.8rem',
                                                  fontWeight: '600',
                                                  color: '#1e40af'
                                                }}>
                                                  {percentage}%
                                                </span>
                                                <span style={{ 
                                                  fontSize: '0.7rem',
                                                  color: '#6b7280',
                                                  display: 'block'
                                                }}>
                                                  ({unitCount} sets)
                                                </span>
                                              </div>
                                            </div>
                                            <input
                                              type="range"
                                              min={0}
                                              max={100}
                                              step={1}
                                              value={percentage}
                                              onChange={(e) => {
                                                const newValue = Number(e.target.value);
                                                // Update both Studio+ and 1BR+ to the same value
                                                setBuildingSettings(prev => {
                                                  const settings = { ...prev[layoutState.selectedBuildingId] };
                                                  const updated = { 
                                                    ...settings.unitTypeDistribution, 
                                                    studioPlus: newValue,
                                                    oneBRPlus: newValue
                                                  };
                                                  settings.unitTypeDistribution = normalizeSliders(updated);
                                                  return { ...prev, [layoutState.selectedBuildingId]: settings };
                                                });
                                              }}
                                              style={{ 
                                                width: '100%',
                                                height: '5px',
                                                borderRadius: '3px',
                                                background: '#e2e8f0',
                                                outline: 'none',
                                                cursor: 'pointer'
                                              }}
                                            />
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            
                            <div style={{ 
                              textAlign: 'center',
                              marginTop: '0.75rem',
                              paddingTop: '0.75rem',
                              borderTop: '1px solid #bfdbfe'
                            }}>
                              <div style={{ 
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                color: '#1e40af'
                              }}>
                                Total: {totalDwellingUnits} units
                              </div>
                              <div style={{ 
                                fontSize: '0.65rem',
                                color: '#6b7280',
                                marginTop: '2px'
                              }}>
                                ({totalSlotsUsed}/{availableSlots} slots used)
                              </div>
                            </div>
                          </div>

                          {/* Second Column - Business Model Distribution */}
                          <div style={{
                            backgroundColor: '#f0fdf4',
                            borderRadius: '8px',
                            border: '1px solid #bbf7d0',
                            padding: '1rem'
                          }}>
                            <h4 style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: '#166534',
                              margin: '0 0 1rem 0',
                              textAlign: 'left'
                            }}>
                              Business Model Distribution
                            </h4>
                            
                            {BUSINESS_MODELS.map((model) => {
                              const percentage = currentSettings.businessModelDistribution[model] || 0;
                              const unitCount = Math.round(totalDwellingUnits * (percentage / 100));
                              
                              return (
                                <div key={model} style={{ 
                                  marginBottom: '0.75rem'
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '0.4rem'
                                  }}>
                                    <span style={{ 
                                      fontSize: '0.875rem',
                                      fontWeight: '500',
                                      color: '#374151'
                                    }}>
                                      {model}
                                    </span>
                                    <div style={{ textAlign: 'right' }}>
                                      <span style={{ 
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        color: '#166534'
                                      }}>
                                        {percentage}%
                                      </span>
                                      <span style={{ 
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        display: 'block'
                                      }}>
                                        ({unitCount} units)
                                      </span>
                                    </div>
                                  </div>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={percentage}
                                    onChange={(e) => handleBusinessModelSlider(layoutState.selectedBuildingId, model, Number(e.target.value))}
                                    style={{ 
                                      width: '100%',
                                      height: '5px',
                                      borderRadius: '3px',
                                      background: '#e2e8f0',
                                      outline: 'none',
                                      cursor: 'pointer'
                                    }}
                                  />
                                </div>
                              );
                            })}
                            
                            <div style={{ 
                              textAlign: 'center',
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              marginTop: '1rem',
                              paddingTop: '1rem',
                              borderTop: '1px solid #bbf7d0',
                              fontStyle: 'italic'
                            }}>
                              Applied proportionally to all unit types
                            </div>
                          </div>

                          {/* Third Column - Market Selection + Intelligent Unit Mix Advisor */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                          }}>
                            {/* Select Market Panel */}
                            <div style={{
                              backgroundColor: '#eff6ff',
                              borderRadius: '8px',
                              border: '1px solid #bfdbfe',
                              padding: '12px',
                              marginBottom: '12px'
                            }}>
                              <h4 style={{
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#1e40af',
                                margin: '0 0 8px 0',
                                textAlign: 'left'
                              }}>
                                Select Market
                              </h4>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <select
                                  value={selectedMarket}
                                  onChange={(e) => {
                                    setSelectedMarket(e.target.value);
                                    // Clear custom overrides when market changes (optional)
                                    // setCustomRentOverrides(null);
                                  }}
                                  style={{
                                    flex: '1',
                                    padding: '8px',
                                    fontSize: '0.8rem',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '4px',
                                    backgroundColor: 'white'
                                  }}
                                >
                                  {getAvailableMarkets().map(market => (
                                    <option key={market.key} value={market.key}>
                                      {market.label} (×{market.multiplier})
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => setShowRentEditModal(true)}
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#1e40af',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  Edit Rates
                                </button>
                              </div>
                              {customRentOverrides && (
                                <div style={{
                                  marginTop: '8px',
                                  fontSize: '0.7rem',
                                  color: '#1e40af',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span>Using custom rent rates</span>
                                  <button
                                    onClick={() => setCustomRentOverrides(null)}
                                    style={{
                                      padding: '2px 6px',
                                      backgroundColor: 'transparent',
                                      color: '#dc2626',
                                      border: '1px solid #dc2626',
                                      borderRadius: '3px',
                                      fontSize: '0.65rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Reset to Default
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Rent Edit Modal */}
                            {showRentEditModal && (() => {
                              const currentMarket = MICHIGAN_MARKETS[selectedMarket];
                              const marketRates = getMarketAdjustedRates(selectedMarket, 'marketRate');
                              const defaultRates = marketRates?.rates || { studio: 950, oneBR: 1100, twoBR: 1400, studioPlus: 1000, oneBRPlus: 1200 };
                              const currentRates = customRentOverrides || defaultRates;
                              
                              return createPortal(
                                <div style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: 'rgba(0,0,0,0.5)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10000
                                }}>
                                  <div style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    width: '400px',
                                    maxWidth: '90vw',
                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: '16px'
                                    }}>
                                      <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937' }}>
                                        Edit Monthly Rent Rates
                                      </h3>
                                      <button
                                        onClick={() => setShowRentEditModal(false)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          fontSize: '1.5rem',
                                          cursor: 'pointer',
                                          color: '#6b7280'
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                    
                                    <div style={{
                                      fontSize: '0.8rem',
                                      color: '#6b7280',
                                      marginBottom: '16px'
                                    }}>
                                      Market: <strong>{currentMarket?.city || 'Unknown'}</strong>
                                      <br />
                                      <span style={{ fontSize: '0.7rem' }}>
                                        Edit rates below. These will override the default market rates.
                                      </span>
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                      {[
                                        { key: 'studio', label: 'Studio' },
                                        { key: 'oneBR', label: '1 Bedroom' },
                                        { key: 'twoBR', label: '2 Bedroom' },
                                        { key: 'studioPlus', label: 'Studio+' },
                                        { key: 'oneBRPlus', label: '1BR+' }
                                      ].map(unit => (
                                        <div key={unit.key} style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                        }}>
                                          <label style={{ fontSize: '0.85rem', color: '#374151' }}>
                                            {unit.label}
                                          </label>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                              Default: ${Math.round(defaultRates[unit.key]).toLocaleString()}
                                            </span>
                                            <div style={{ position: 'relative' }}>
                                              <span style={{
                                                position: 'absolute',
                                                left: '8px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#6b7280'
                                              }}>$</span>
                                              <input
                                                type="number"
                                                value={currentRates[unit.key] || ''}
                                                onChange={(e) => {
                                                  const newValue = parseInt(e.target.value) || 0;
                                                  setCustomRentOverrides(prev => ({
                                                    ...(prev || defaultRates),
                                                    [unit.key]: newValue
                                                  }));
                                                }}
                                                style={{
                                                  width: '100px',
                                                  padding: '6px 8px 6px 20px',
                                                  border: '1px solid #d1d5db',
                                                  borderRadius: '4px',
                                                  fontSize: '0.85rem'
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    
                                    <div style={{
                                      display: 'flex',
                                      gap: '8px',
                                      marginTop: '20px',
                                      justifyContent: 'flex-end'
                                    }}>
                                      <button
                                        onClick={() => {
                                          setCustomRentOverrides(null);
                                        }}
                                        style={{
                                          padding: '8px 16px',
                                          backgroundColor: '#f3f4f6',
                                          color: '#374151',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '6px',
                                          fontSize: '0.85rem',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Reset to Default
                                      </button>
                                      <button
                                        onClick={() => setShowRentEditModal(false)}
                                        style={{
                                          padding: '8px 16px',
                                          backgroundColor: '#3b82f6',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '6px',
                                          fontSize: '0.85rem',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Save & Close
                                      </button>
                                    </div>
                                  </div>
                                </div>,
                                document.body
                              );
                            })()}

                            {/* Current Selection Revenue Panel */}
                            {(() => {
                              // Calculate estimated monthly revenue based on current unit mix
                              const businessModelMix = currentSettings.businessModelDistribution;
                              let estimatedRevenue = 0;
                              
                              // Store rent rates for display
                              const unitRentDetails = [];
                              
                              // Get market rates and calculate revenue
                              const unitTypesList = ['studio', 'oneBR', 'twoBR', 'studioPlus', 'oneBRPlus'];
                              const unitLabels = {
                                studio: 'Studio',
                                oneBR: '1 BR',
                                twoBR: '2 BR',
                                studioPlus: 'Studio+',
                                oneBRPlus: '1BR+'
                              };
                              const slotRequirements = {
                                studio: 1,
                                oneBR: 2,
                                twoBR: 3,
                                studioPlus: 1,
                                oneBRPlus: 2
                              };
                              
                              unitTypesList.forEach(unitType => {
                                const count = unitCounts[unitType] || 0;
                                
                                // Calculate weighted average rent based on business model distribution
                                let weightedRent = 0;
                                const businessModelMap = {
                                  'Market Rate Apartment': 'marketRate',
                                  'Short Term Rental': 'shortTermRental',
                                  'Workforce Housing': 'workforceHousing',
                                  'Sell': 'sell'
                                };
                                
                                // Use custom rent overrides if available, otherwise use market rates
                                if (customRentOverrides && customRentOverrides[unitType]) {
                                  // Custom rates apply directly (no business model weighting for simplicity)
                                  weightedRent = customRentOverrides[unitType];
                                } else {
                                  // Use market rates with business model weighting
                                  Object.keys(businessModelMix).forEach(model => {
                                    const percentage = businessModelMix[model] || 0;
                                    const marketKey = businessModelMap[model] || 'marketRate';
                                    try {
                                      const marketData = getMarketAdjustedRates(selectedMarket, marketKey);
                                      if (marketData && marketData.rates && marketData.rates[unitType]) {
                                        weightedRent += (marketData.rates[unitType] * percentage / 100);
                                      }
                                    } catch (e) {
                                      // Fall back to default rates if market data unavailable
                                      const defaultRates = { studio: 950, oneBR: 1100, twoBR: 1400, studioPlus: 1000, oneBRPlus: 1200 };
                                      weightedRent += ((defaultRates[unitType] || 1000) * percentage / 100);
                                    }
                                  });
                                }
                                
                                const subtotal = count * weightedRent;
                                estimatedRevenue += subtotal;
                                
                                // Always store the rent rate, even if count is 0
                                unitRentDetails.push({
                                  key: unitType,
                                  label: unitLabels[unitType],
                                  count,
                                  rent: Math.round(weightedRent),
                                  subtotal: Math.round(subtotal),
                                  slots: slotRequirements[unitType]
                                });
                              });
                              
                              return (
                                <div style={{
                                  backgroundColor: '#eff6ff',
                                  borderRadius: '8px',
                                  border: '1px solid #bfdbfe',
                                  padding: '12px',
                                  marginBottom: '12px'
                                }}>
                                  {/* Collapsible Header */}
                                  <div 
                                    onClick={() => setShowCurrentSelection(!showCurrentSelection)}
                                    style={{
                                      cursor: 'pointer',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: showCurrentSelection ? '8px' : '0'
                                    }}
                                  >
                                    <div>
                                      <h4 style={{
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        color: '#1e40af',
                                        margin: '0 0 4px 0',
                                        textAlign: 'left'
                                      }}>
                                        Current Selection
                                      </h4>
                                      <div style={{
                                        fontSize: '0.7rem',
                                        color: '#3b82f6',
                                        textAlign: 'left'
                                      }}>
                                        Based on your slider settings
                                      </div>
                                    </div>
                                    <span style={{ 
                                      color: '#3b82f6', 
                                      fontSize: '0.8rem',
                                      transform: showCurrentSelection ? 'rotate(0deg)' : 'rotate(-90deg)',
                                      transition: 'transform 0.2s ease'
                                    }}>▼</span>
                                  </div>
                                  
                                  {showCurrentSelection && (
                                    <>
                                      {/* Unit breakdown table with rents */}
                                      <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        marginBottom: '10px',
                                        fontSize: '0.65rem'
                                      }}>
                                        {/* Table header */}
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: '1fr 50px 70px 50px 70px',
                                          gap: '4px',
                                          paddingBottom: '4px',
                                          borderBottom: '1px solid #e5e7eb',
                                          marginBottom: '4px',
                                          color: '#6b7280',
                                          fontWeight: '600'
                                        }}>
                                          <div>Type</div>
                                          <div style={{ textAlign: 'right' }}>Units</div>
                                          <div style={{ textAlign: 'right' }}>Rent/mo</div>
                                          <div style={{ textAlign: 'right' }}>Slots</div>
                                          <div style={{ textAlign: 'right' }}>Subtotal</div>
                                        </div>
                                        {/* Table rows */}
                                        {unitRentDetails.map(detail => (
                                          <div 
                                            key={detail.key} 
                                            style={{
                                              display: 'grid',
                                              gridTemplateColumns: '1fr 50px 70px 50px 70px',
                                              gap: '4px',
                                              padding: '2px 0',
                                              color: detail.count > 0 ? '#1f2937' : '#9ca3af'
                                            }}
                                          >
                                            <div style={{ fontWeight: detail.count > 0 ? '600' : '400' }}>{detail.label}</div>
                                            <div style={{ textAlign: 'right' }}>{detail.count}</div>
                                            <div style={{ textAlign: 'right' }}>${detail.rent.toLocaleString()}</div>
                                            <div style={{ textAlign: 'right' }}>{detail.count * detail.slots}</div>
                                            <div style={{ textAlign: 'right', fontWeight: detail.count > 0 ? '600' : '400' }}>
                                              ${detail.subtotal.toLocaleString()}
                                            </div>
                                          </div>
                                        ))}
                                        {/* Egress row - shows slots reserved for egress */}
                                        <div 
                                          style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 50px 70px 50px 70px',
                                            gap: '4px',
                                            padding: '4px 0 2px 0',
                                            marginTop: '4px',
                                            borderTop: '1px dashed #d1d5db',
                                            color: '#dc2626'
                                          }}
                                        >
                                          <div style={{ fontWeight: '500' }}>Egress</div>
                                          <div style={{ textAlign: 'right' }}>—</div>
                                          <div style={{ textAlign: 'right' }}>—</div>
                                          <div style={{ textAlign: 'right' }}>{egressSlotsTotal}</div>
                                          <div style={{ textAlign: 'right' }}>—</div>
                                        </div>
                                        {/* Total row */}
                                        <div 
                                          style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 50px 70px 50px 70px',
                                            gap: '4px',
                                            padding: '4px 0 0 0',
                                            marginTop: '4px',
                                            borderTop: '1px solid #374151',
                                            color: '#1f2937',
                                            fontWeight: '700'
                                          }}
                                        >
                                          <div>TOTAL</div>
                                          <div style={{ textAlign: 'right' }}>{totalDwellingUnits}</div>
                                          <div style={{ textAlign: 'right' }}>—</div>
                                          <div style={{ textAlign: 'right' }}>{totalSlotsUsed + egressSlotsTotal}</div>
                                          <div style={{ textAlign: 'right' }}>${Math.round(estimatedRevenue).toLocaleString()}</div>
                                        </div>
                                      </div>
                                      
                                      {/* Revenue and stats - matches Maximum Revenue Strategy layout */}
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '0.7rem',
                                        color: '#374151',
                                        marginBottom: '4px'
                                      }}>
                                        <span><strong>Total Revenue:</strong> ${Math.round(estimatedRevenue).toLocaleString()}/month</span>
                                        <span><strong>Units:</strong> {totalDwellingUnits}</span>
                                      </div>
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '0.7rem',
                                        color: '#374151'
                                      }}>
                                        <span><strong>Building:</strong> {totalSlotsUsed + egressSlotsTotal} slots <span style={{ color: '#dc2626' }}>({egressSlotsTotal} egress)</span></span>
                                        <span><strong>Efficiency:</strong> {availableSlots > 0 ? Math.round((totalSlotsUsed / availableSlots) * 100) : 0}%</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                            
                            <IntelligentUnitMixAdvisor 
                              buildingBoxes={layoutState.selectedBuildingId ? 
                                (Array.isArray(layoutState.optimizedBoxes) ? layoutState.optimizedBoxes : 
                                 Array.isArray(layoutState.boxes) ? layoutState.boxes : []).filter(box => 
                                  box.buildPathId === layoutState.selectedBuildingId
                                ) : []
                              }
                              floorCount={floorCount}
                              unitQuantities={unitQuantities}
                              selectedMarket={selectedMarket}
                              customRentOverrides={customRentOverrides}
                              onApplyRecommendation={(recommendedUnitMix) => {
                                // Convert the AI recommendation to the format expected by the existing system
                                const convertedQuantities = {};
                                Object.entries(recommendedUnitMix).forEach(([type, count]) => {
                                  // Map AI unit types to existing unit type keys
                                  if (type === 'studio') convertedQuantities.studio = count;
                                  if (type === 'oneBR') convertedQuantities.oneBR = count;
                                  if (type === 'twoBR') convertedQuantities.twoBR = count;
                                  if (type === 'studioPlus') convertedQuantities.studioPlus = count;
                                  if (type === 'oneBRPlus') convertedQuantities.oneBRPlus = count;
                                });
                                
                                console.log('🎯 Applying AI recommendations:', convertedQuantities);
                                
                                // Update the unitQuantities state first (this will update the unit map)
                                console.log('🗺️ Current unitQuantities before update:', unitQuantities);
                                setUnitQuantities(convertedQuantities);
                                console.log('🗺️ Setting new unitQuantities:', convertedQuantities);
                                
                                // Also update the building settings sliders to match the recommendation
                                setBuildingSettings(prev => {
                                  const settings = { ...prev[layoutState.selectedBuildingId] };
                                  const totalUnitsRecommended = Object.values(convertedQuantities).reduce((sum, count) => sum + count, 0);
                                  
                                  // Convert counts to percentages for unit type distribution sliders
                                  const newUnitDistribution = {};
                                  UNIT_TYPES.forEach(type => {
                                    const count = convertedQuantities[type.key] || 0;
                                    const percentage = totalUnitsRecommended > 0 ? Math.round((count / totalUnitsRecommended) * 100) : 0;
                                    newUnitDistribution[type.key] = percentage;
                                  });
                                  
                                  // Update business model distribution to match AI assumptions
                                  const newBusinessDistribution = {
                                    'Market Rate Apartment': 60,
                                    'Short Term Rental': 30,
                                    'Workforce Housing': 10,
                                    'Sell': 0
                                  };
                                  
                                  // Normalize unit type distribution to ensure total is 100%
                                  settings.unitTypeDistribution = normalizeSliders(newUnitDistribution);
                                  settings.businessModelDistribution = normalizeSliders(newBusinessDistribution);
                                  
                                  return { ...prev, [layoutState.selectedBuildingId]: settings };
                                });
                                
                                console.log('✅ AI recommendations applied to both unit map and sliders');
                              }}
                            />
                          </div>
                        </div>

                        {/* Visual Unit Layout by Floor Map */}
                        <div style={{ marginTop: '2rem' }}>
                          <VisualUnitMap
                            buildingId={layoutState.selectedBuildingId}
                            buildingBoxes={currentBoxes}
                            unitQuantities={unitQuantities}
                            floorCount={floorCount}
                            onExport={(data, format) => {
                              console.log(`Exporting ${format}:`, data);
                            }}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{
            padding: '2rem',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#6b7280',
              margin: '0 0 1rem 0'
            }}>
              Unit Mix & Business Model Configuration
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#6b7280',
              margin: 0
            }}>
              Please create a building layout first to configure unit mix and business models.
            </p>
          </div>
        )}

        {/* Unit Data Table - Collapsible Section */}
        {showLivableSpacesTable && livableSpaces.length > 0 && (
          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            marginTop: '2rem'
          }}>
            {/* Collapsible Header */}
            <div 
              style={{
                padding: '1rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: showUnitDataTable ? '1px solid #e2e8f0' : 'none'
              }}
              onClick={() => setShowUnitDataTable(!showUnitDataTable)}
            >
              <div>
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 'bold', 
                  color: '#1a202c',
                  margin: 0
                }}>
                  Unit Data
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  margin: '0.25rem 0 0 0',
                  fontStyle: 'italic'
                }}>
                  (Advanced details)
                </p>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button 
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#22c55e', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the collapse/expand
                    exportToCSV();
                  }}
                >
                  Export to CSV
                </button>
                
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{
                    color: '#6b7280',
                    transform: showUnitDataTable ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path d="M6 9l6 6 6-6v-11a2 2 0 0 1 2 2z"/>
                  <polyline points="15,3 15,13 9,13 9,3"/>
                </svg>
              </div>
            </div>
            
            {/* Collapsible Content */}
            {showUnitDataTable && (
              <div style={{ padding: '1rem' }}>
                {layoutState.selectedOptimizations[layoutState.selectedBuildingId] !== 'none' && (
                  <div style={{
                    padding: '0.5rem',
                    backgroundColor: layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'efficiency' ? '#ebf5ff' : '#f0fff4',
                    borderRadius: '0.25rem',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem'
                  }}>
                    <p style={{ margin: 0 }}>
                      <strong>Note:</strong> Unit data shown for {layoutState.selectedOptimizations[layoutState.selectedBuildingId] === 'efficiency' ? 'efficiency-optimized' : 'livability-optimized'} layout.
                    </p>
                  </div>
                )}
                
                {floorCount > 1 && (
                  <div style={{
                    backgroundColor: '#f3f0ff',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #e9d5ff'
                  }}>
                    <p style={{ fontSize: '0.875rem', color: '#6b21a8', margin: '0 0 0.5rem 0' }}>
                      <strong>Multi-Floor Information:</strong> This table shows ground floor spaces only. 
                      For a {floorCount}-story building, each tower would have {floorCount} boxes with 2 livable spaces each.
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#6b21a8', margin: 0 }}>
                      Total livable spaces across all floors: {results.totalLivableSpaces}
                    </p>
                  </div>
                )}
                
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse' 
                  }}>
                    <thead style={{ backgroundColor: '#f9fafb' }}>
                      <tr>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>ID</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Building</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Tower</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Floor</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Side</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Width (ft)</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Length (ft)</th>
                        <th style={{ 
                          padding: '0.75rem', 
                          textAlign: 'left', 
                          borderBottom: '1px solid #e5e7eb' 
                        }}>Sq. Ft.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {livableSpaces.map((space, index) => (
                        <tr 
                          key={space.id} 
                          style={{ 
                            backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                          }}
                        >
                          <td style={{ padding: '0.75rem' }}>{space.id}</td>
                          <td style={{ padding: '0.75rem' }}>{space.buildingId}</td>
                          <td style={{ padding: '0.75rem' }}>{space.towerId}</td>
                          <td style={{ padding: '0.75rem' }}>{space.floorId}</td>
                          <td style={{ padding: '0.75rem' }}>{space.side}</td>
                          <td style={{ padding: '0.75rem' }}>{space.width}</td>
                          <td style={{ padding: '0.75rem' }}>{space.length}</td>
                          <td style={{ padding: '0.75rem' }}>{space.squareFeet}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginTop: '1rem',
                  backgroundColor: '#f9fafb',
                  padding: '0.75rem',
                  borderRadius: '0.375rem'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                    Ground Floor Spaces: {livableSpaces.length}
                  </div>
                  {floorCount > 1 && (
                    <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                      All Floors: {results.totalLivableSpaces} spaces
                      {hasPodium ? ` (${floorCount + 1} stories incl. podium)` : ` (${floorCount} stories)`}
                    </div>
                  )}
                  {hasPodium && (
                    <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                      Podium Area: {Math.round(results.podiumArea)} sq ft
                    </div>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '0.5rem',
                  marginTop: '1rem',
                  backgroundColor: '#f9fafb',
                  padding: '0.75rem',
                  borderRadius: '0.375rem'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                    Component Area: {Math.round(results.componentArea)} sq ft
                  </div>
                  {hasPodium && (
                    <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                      Podium Area: {Math.round(results.podiumArea)} sq ft
                    </div>
                  )}
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem' }}>
                    Total Building Area: {Math.round(results.totalBuildingArea)} sq ft
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                    Livable Spaces: {results.totalLivableSpaces} spaces
                    {hasPodium ? ` (${floorCount} stories + podium)` : ` (${floorCount} stories)`}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PDF Report Generator */}
        <div style={{ marginTop: '2rem' }}>
          <ProjectReportGenerator 
            layoutState={layoutState}
            buildingSettings={buildingSettings}
            floorCount={floorCount}
            hasPodium={hasPodium}
            calculateProjectMetrics={calculateProjectMetrics}
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleModIQVisualizer;